{
  "openapi": "3.1.0",
  "info": {
    "title": "Whop API",
    "description": "Hand-written V1 endpoints. Merged into the GraphqlRestProxy-generated schema at build time.",
    "version": "v1"
  },
  "servers": [
    {
      "url": "https://{defaultHost}",
      "variables": {
        "defaultHost": {
          "default": "api.whop.com/api/v1"
        }
      }
    }
  ],
  "paths": {
    "/accounts": {
      "get": {
        "summary": "List Accounts",
        "tags": [
          "Accounts"
        ],
        "security": [
          {
            "bearerAuth": [
              "company:balance:read"
            ]
          }
        ],
        "description": "Lists accounts visible to the credential. User tokens return the user's business accounts; business account API keys return the requesting business account and its connected accounts.",
        "operationId": "list",
        "parameters": [
          {
            "name": "first",
            "in": "query",
            "schema": {
              "type": "integer"
            },
            "required": false,
            "description": "The number of accounts to return (default 10, max 50)."
          },
          {
            "name": "after",
            "in": "query",
            "schema": {
              "type": "string"
            },
            "required": false,
            "description": "A cursor; returns accounts after this position."
          },
          {
            "name": "last",
            "in": "query",
            "schema": {
              "type": "integer"
            },
            "required": false,
            "description": "The number of accounts to return from the end of the range."
          },
          {
            "name": "before",
            "in": "query",
            "schema": {
              "type": "string"
            },
            "required": false,
            "description": "A cursor; returns accounts before this position."
          },
          {
            "name": "order",
            "in": "query",
            "schema": {
              "type": "string",
              "enum": [
                "created_at"
              ],
              "default": "created_at"
            },
            "required": false,
            "description": "The field to sort accounts by."
          },
          {
            "name": "direction",
            "in": "query",
            "schema": {
              "type": "string",
              "enum": [
                "asc",
                "desc"
              ],
              "default": "desc"
            },
            "required": false,
            "description": "Sort direction."
          }
        ],
        "responses": {
          "200": {
            "description": "accounts listed",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "data": {
                      "type": "array",
                      "items": {
                        "$ref": "#/components/schemas/Account"
                      }
                    },
                    "page_info": {
                      "type": "object",
                      "properties": {
                        "end_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "start_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        }
                      },
                      "required": [
                        "end_cursor",
                        "start_cursor",
                        "has_next_page",
                        "has_previous_page"
                      ]
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ]
                }
              }
            }
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          },
          "403": {
            "description": "credential lacks the required balance-read scope",
            "$ref": "#/components/responses/Forbidden"
          }
        }
      },
      "post": {
        "summary": "Create Account",
        "tags": [
          "Accounts"
        ],
        "security": [
          {
            "bearerAuth": [
              "company:create_child"
            ]
          }
        ],
        "description": "Creates an account. User tokens create business accounts; business account API keys create connected accounts. Tax fields (`tax_remitted_by`, `product_tax_code_id`, `business_address`, `tax_identifiers`) are configured with Update Account, not at creation.",
        "operationId": "create",
        "parameters": [],
        "responses": {
          "201": {
            "description": "account created",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/Account"
                }
              }
            }
          },
          "400": {
            "description": "the request body is invalid",
            "$ref": "#/components/responses/InvalidParameters"
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          },
          "403": {
            "description": "credential lacks the required connected-account creation scope",
            "$ref": "#/components/responses/Forbidden"
          }
        },
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "properties": {
                  "email": {
                    "type": "string",
                    "description": "The email address of the account owner. Required for business account API key requests."
                  },
                  "metadata": {
                    "type": "object",
                    "additionalProperties": true,
                    "description": "Arbitrary key/value metadata to store on the account."
                  }
                }
              }
            }
          },
          "required": true
        }
      }
    },
    "/accounts/{account_id}": {
      "get": {
        "summary": "Retrieve Account",
        "tags": [
          "Accounts"
        ],
        "security": [
          {
            "bearerAuth": [
              "company:balance:read"
            ]
          }
        ],
        "description": "Retrieves a single account visible to the credential, including its crypto wallet.",
        "operationId": "get",
        "parameters": [
          {
            "name": "account_id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "Account ID, prefixed `biz_`."
          }
        ],
        "responses": {
          "200": {
            "description": "account retrieved",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/Account"
                }
              }
            }
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          },
          "403": {
            "description": "credential lacks the required balance-read scope",
            "$ref": "#/components/responses/Forbidden"
          },
          "404": {
            "description": "no account with that ID is visible to the credential",
            "$ref": "#/components/responses/NotFound"
          }
        }
      },
      "patch": {
        "summary": "Update Account",
        "tags": [
          "Accounts"
        ],
        "security": [
          {
            "bearerAuth": [
              "company:update"
            ]
          }
        ],
        "description": "Updates an account. User tokens can update business accounts; business account API keys can update connected accounts.",
        "operationId": "update",
        "parameters": [
          {
            "name": "account_id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "Account ID, prefixed `biz_`."
          }
        ],
        "responses": {
          "200": {
            "description": "account updated",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/Account"
                }
              }
            }
          },
          "400": {
            "description": "store page config is not an object",
            "$ref": "#/components/responses/InvalidParameters"
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          },
          "403": {
            "description": "credential lacks the required update scope",
            "$ref": "#/components/responses/Forbidden"
          },
          "404": {
            "description": "no account with that ID is visible to the credential",
            "$ref": "#/components/responses/NotFound"
          }
        },
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "properties": {
                  "affiliate_application_required": {
                    "type": "boolean",
                    "description": "Whether prospective affiliates must submit an application before promoting this account."
                  },
                  "affiliate_instructions": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "Guidelines shown to affiliates promoting this account."
                  },
                  "banner_image": {
                    "type": [
                      "object",
                      "null"
                    ],
                    "additionalProperties": true,
                    "description": "Attachment input for the account banner image."
                  },
                  "business_type": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "High-level business category for the account."
                  },
                  "country": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "Country where the account is located."
                  },
                  "description": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "Account promotional description."
                  },
                  "featured_affiliate_product_id": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "The ID of the product to feature for affiliates. Pass `null` to clear."
                  },
                  "home_preferences": {
                    "type": "array",
                    "items": {
                      "type": "string"
                    },
                    "description": "Public account home page preferences."
                  },
                  "industry_group": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "Account industry group."
                  },
                  "industry_type": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "Specific industry vertical for the account."
                  },
                  "invoice_prefix": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "Prefix used for account invoices."
                  },
                  "logo": {
                    "type": [
                      "object",
                      "null"
                    ],
                    "additionalProperties": true,
                    "description": "Attachment input for the account logo."
                  },
                  "onboarding_type": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "The type of onboarding the account has completed."
                  },
                  "opengraph_image": {
                    "type": [
                      "object",
                      "null"
                    ],
                    "additionalProperties": true,
                    "description": "Attachment input for the account Open Graph image."
                  },
                  "opengraph_image_variant": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "The account Open Graph image variant."
                  },
                  "other_business_description": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "The description of the business type when business_type is other."
                  },
                  "other_industry_description": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "The description of the industry type when industry_type is other."
                  },
                  "require_2fa": {
                    "type": "boolean",
                    "description": "Whether the account requires authorized users to have two-factor authentication enabled."
                  },
                  "route": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "The unique URL slug for the account."
                  },
                  "send_customer_emails": {
                    "type": "boolean",
                    "description": "Whether Whop sends transactional emails to customers on behalf of this account."
                  },
                  "show_joined_whops": {
                    "type": "boolean",
                    "description": "Whether the account appears in joined whops on other accounts."
                  },
                  "show_reviews_dtc": {
                    "type": "boolean",
                    "description": "Whether reviews are displayed on direct-to-consumer product pages."
                  },
                  "show_user_directory": {
                    "type": "boolean",
                    "description": "Whether the account shows users in the user directory."
                  },
                  "social_links": {
                    "type": "array",
                    "items": {
                      "type": "object",
                      "additionalProperties": true
                    },
                    "description": "The full list of social links to display for the account."
                  },
                  "store_page_config": {
                    "type": [
                      "object",
                      "null"
                    ],
                    "additionalProperties": true,
                    "description": "Account store page display configuration."
                  },
                  "target_audience": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "The target audience for this account."
                  },
                  "title": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "The display name of the account."
                  },
                  "use_logo_as_opengraph_image_fallback": {
                    "type": "boolean",
                    "description": "Whether the account uses its logo as the fallback Open Graph image."
                  },
                  "metadata": {
                    "type": "object",
                    "additionalProperties": true,
                    "description": "Arbitrary key/value metadata to store on the account."
                  },
                  "tax_remitted_by": {
                    "type": "string",
                    "enum": [
                      "whop",
                      "self",
                      "none"
                    ],
                    "description": "Who calculates and remits tax for the account: `whop` (Whop calculates and remits), `self` (Whop calculates; the account collects and remits), or `none` (neither; the account is responsible). `self` requires a `business_address` in a supported country."
                  },
                  "product_tax_code_id": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "ID of the tax classification code applied by default to the account's products."
                  },
                  "business_address": {
                    "type": "object",
                    "description": "Account business address used to calculate tax. A complete address in a supported country is required when `tax_remitted_by` is `self`.",
                    "properties": {
                      "line1": {
                        "type": "string",
                        "description": "First line of the street address."
                      },
                      "line2": {
                        "type": [
                          "string",
                          "null"
                        ],
                        "description": "Second line of the street address."
                      },
                      "city": {
                        "type": [
                          "string",
                          "null"
                        ],
                        "description": "City name."
                      },
                      "state": {
                        "type": [
                          "string",
                          "null"
                        ],
                        "description": "State, province, or region code, for example `CA`."
                      },
                      "postal_code": {
                        "type": [
                          "string",
                          "null"
                        ],
                        "description": "Postal or ZIP code."
                      },
                      "country": {
                        "type": "string",
                        "description": "Two-letter ISO 3166-1 country code, for example `US`, `DE`, or `GB`."
                      }
                    }
                  },
                  "tax_identifiers": {
                    "type": "array",
                    "description": "Account tax/VAT registrations to add or update. When `tax_remitted_by` is `self`, tax is calculated and collected only in the countries where the account holds a registration.",
                    "items": {
                      "type": "object",
                      "required": [
                        "tax_id_type",
                        "tax_id_value"
                      ],
                      "properties": {
                        "tax_id_type": {
                          "type": "string",
                          "enum": [
                            "ad_nrt",
                            "ao_tin",
                            "ar_cuit",
                            "al_tin",
                            "am_tin",
                            "aw_tin",
                            "au_abn",
                            "au_arn",
                            "eu_vat",
                            "az_tin",
                            "bs_tin",
                            "bh_vat",
                            "bd_bin",
                            "bb_tin",
                            "by_tin",
                            "bj_ifu",
                            "bo_tin",
                            "ba_tin",
                            "br_cnpj",
                            "br_cpf",
                            "bg_uic",
                            "bf_ifu",
                            "kh_tin",
                            "cm_niu",
                            "ca_bn",
                            "ca_gst_hst",
                            "ca_pst_bc",
                            "ca_pst_mb",
                            "ca_pst_sk",
                            "ca_qst",
                            "cv_nif",
                            "cl_tin",
                            "cn_tin",
                            "co_nit",
                            "cd_nif",
                            "cr_tin",
                            "hr_oib",
                            "do_rcn",
                            "ec_ruc",
                            "eg_tin",
                            "sv_nit",
                            "et_tin",
                            "eu_oss_vat",
                            "ge_vat",
                            "gh_tin",
                            "de_stn",
                            "gb_vat",
                            "gn_nif",
                            "hk_br",
                            "hu_tin",
                            "is_vat",
                            "in_gst",
                            "id_npwp",
                            "il_vat",
                            "jp_cn",
                            "jp_rn",
                            "jp_trn",
                            "kz_bin",
                            "ke_pin",
                            "kg_tin",
                            "la_tin",
                            "li_uid",
                            "li_vat",
                            "my_frp",
                            "my_itn",
                            "my_sst",
                            "mr_nif",
                            "mx_rfc",
                            "md_vat",
                            "me_pib",
                            "ma_vat",
                            "np_pan",
                            "nz_gst",
                            "ng_tin",
                            "mk_vat",
                            "no_vat",
                            "no_voec",
                            "om_vat",
                            "pe_ruc",
                            "ph_tin",
                            "ro_tin",
                            "ru_inn",
                            "ru_kpp",
                            "sa_vat",
                            "sn_ninea",
                            "rs_pib",
                            "sg_gst",
                            "sg_uen",
                            "si_tin",
                            "za_vat",
                            "kr_brn",
                            "es_cif",
                            "ch_uid",
                            "ch_vat",
                            "tw_vat",
                            "tj_tin",
                            "tz_vat",
                            "th_vat",
                            "tr_tin",
                            "ug_tin",
                            "ua_vat",
                            "ae_trn",
                            "us_ein",
                            "uy_ruc",
                            "uz_tin",
                            "uz_vat",
                            "ve_rif",
                            "vn_tin",
                            "zm_tin",
                            "zw_tin",
                            "sr_fin"
                          ],
                          "description": "Tax ID type, for example `eu_vat`, `gb_vat`, or `us_ein`."
                        },
                        "tax_id_value": {
                          "type": "string",
                          "description": "Tax ID value, for example `DE123456789`."
                        }
                      }
                    }
                  }
                }
              }
            }
          },
          "required": true
        }
      }
    },
    "/accounts/me": {
      "get": {
        "summary": "Retrieve Requesting Account",
        "tags": [
          "Accounts"
        ],
        "security": [
          {
            "bearerAuth": [
              "company:balance:read"
            ]
          }
        ],
        "description": "Retrieves the business account associated with the current business account API key.",
        "operationId": "me",
        "responses": {
          "200": {
            "description": "requesting account retrieved",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/Account"
                }
              }
            }
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          },
          "403": {
            "description": "credential lacks the required balance-read scope",
            "$ref": "#/components/responses/Forbidden"
          }
        },
        "parameters": []
      }
    },
    "/ad_campaigns": {
      "get": {
        "summary": "List Ad Campaigns",
        "tags": [
          "Ad Campaigns"
        ],
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:basic:read"
            ]
          }
        ],
        "description": "Lists the ad campaigns for an account, with stats over the requested window.",
        "operationId": "list",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "account_id",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "The account the campaigns belong to. Defaults to the account-scoped key's own account."
          },
          {
            "name": "status",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string",
              "enum": [
                "draft",
                "active",
                "paused",
                "payment_failed"
              ]
            },
            "description": "Only return campaigns with this status."
          },
          {
            "name": "query",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Filter campaigns by a title or ID substring."
          },
          {
            "name": "order",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string",
              "enum": [
                "created_at",
                "updated_at"
              ]
            },
            "description": "The field to sort by. Defaults to created_at."
          },
          {
            "name": "direction",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string",
              "enum": [
                "asc",
                "desc"
              ]
            },
            "description": "The sort direction. Defaults to desc."
          },
          {
            "name": "created_before",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Only return campaigns created before this timestamp."
          },
          {
            "name": "created_after",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Only return campaigns created after this timestamp."
          },
          {
            "name": "stats_from",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Start of the stats window. Defaults to all-time."
          },
          {
            "name": "stats_to",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "End of the stats window. Defaults to now."
          },
          {
            "name": "first",
            "in": "query",
            "required": false,
            "schema": {
              "type": "integer",
              "default": 20,
              "maximum": 100
            },
            "description": "The number of campaigns to return."
          },
          {
            "name": "after",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Cursor to fetch the page after (from page_info.end_cursor)."
          },
          {
            "name": "last",
            "in": "query",
            "required": false,
            "schema": {
              "type": "integer",
              "maximum": 100
            },
            "description": "The number of campaigns to return from the end of the range."
          },
          {
            "name": "before",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Cursor to fetch the page before (from page_info.start_cursor)."
          }
        ],
        "responses": {
          "200": {
            "description": "ad campaigns listed",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "data": {
                      "type": "array",
                      "items": {
                        "$ref": "#/components/schemas/AdCampaign"
                      }
                    },
                    "page_info": {
                      "type": "object",
                      "properties": {
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "end_cursor": {
                          "type": "string",
                          "nullable": true
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        },
                        "start_cursor": {
                          "type": "string",
                          "nullable": true
                        }
                      },
                      "required": [
                        "has_next_page",
                        "end_cursor",
                        "has_previous_page",
                        "start_cursor"
                      ]
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ]
                }
              }
            }
          },
          "400": {
            "description": "account_id missing and unresolvable",
            "$ref": "#/components/responses/InvalidParameters"
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          }
        }
      },
      "post": {
        "summary": "Create an Ad Campaign",
        "tags": [
          "Ad Campaigns"
        ],
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:create"
            ]
          }
        ],
        "description": "Creates an ad campaign for an account.",
        "operationId": "create",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          }
        ],
        "responses": {
          "200": {
            "description": "ad campaign created",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/AdCampaign"
                }
              }
            }
          },
          "400": {
            "description": "unsupported platform",
            "$ref": "#/components/responses/InvalidParameters"
          }
        },
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "properties": {
                  "account_id": {
                    "type": "string",
                    "description": "The account to create the campaign under. Defaults to the account-scoped key's own account."
                  },
                  "title": {
                    "type": "string",
                    "description": "The title of the campaign."
                  },
                  "platform": {
                    "type": "string",
                    "enum": [
                      "meta"
                    ],
                    "description": "The ad network the campaign runs on."
                  },
                  "objective": {
                    "type": "string",
                    "enum": [
                      "awareness",
                      "traffic",
                      "engagement",
                      "leads",
                      "sales"
                    ],
                    "description": "The goal the campaign optimizes toward."
                  },
                  "budget_amount": {
                    "type": "number",
                    "description": "The campaign budget, in USD. Required for CBO (budget_optimization: ad_campaign); omit for ABO."
                  },
                  "budget_optimization": {
                    "type": "string",
                    "enum": [
                      "ad_campaign",
                      "ad_group"
                    ],
                    "description": "Which level owns the budget — the campaign (CBO) or each ad group (ABO). Defaults to ad_group."
                  },
                  "budget_type": {
                    "type": "string",
                    "enum": [
                      "daily",
                      "lifetime"
                    ],
                    "description": "Whether the budget is spent per day or over the campaign's lifetime. Defaults to daily."
                  },
                  "special_ad_categories": {
                    "type": "array",
                    "items": {
                      "type": "string",
                      "enum": [
                        "housing",
                        "employment",
                        "financial_products",
                        "politics"
                      ]
                    },
                    "description": "Regulated categories the campaign falls under. Ads in these categories are subject to extra targeting restrictions."
                  },
                  "bid_type": {
                    "type": "string",
                    "enum": [
                      "minimum_cost",
                      "average_target",
                      "maximum_target"
                    ],
                    "description": "CBO bid strategy: minimum_cost (lowest cost), average_target (cost cap), or maximum_target (bid cap). CBO only."
                  },
                  "desired_cost_per_result": {
                    "type": "number",
                    "description": "Target/cap cost per result in USD for average_target / maximum_target bidding. CBO only."
                  },
                  "starts_at": {
                    "type": "string",
                    "description": "Campaign schedule start (ISO 8601). CBO only."
                  },
                  "ends_at": {
                    "type": "string",
                    "description": "Campaign schedule end (ISO 8601). CBO only."
                  }
                },
                "required": [
                  "title",
                  "platform",
                  "objective"
                ]
              }
            }
          },
          "required": true
        }
      }
    },
    "/ad_campaigns/{id}": {
      "get": {
        "summary": "Retrieve an Ad Campaign",
        "tags": [
          "Ad Campaigns"
        ],
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:basic:read"
            ]
          }
        ],
        "description": "Retrieves a single ad campaign with stats over the requested window.",
        "operationId": "get",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "The ad campaign ID."
          },
          {
            "name": "stats_from",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Start of the stats window."
          },
          {
            "name": "stats_to",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "End of the stats window."
          }
        ],
        "responses": {
          "200": {
            "description": "ad campaign retrieved",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/AdCampaign"
                }
              }
            }
          },
          "404": {
            "description": "ad campaign not found",
            "$ref": "#/components/responses/NotFound"
          }
        }
      },
      "patch": {
        "summary": "Update an Ad Campaign",
        "tags": [
          "Ad Campaigns"
        ],
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:update"
            ]
          }
        ],
        "description": "Updates an ad campaign's editable fields (title, budget, schedule), and launches a draft campaign by setting status to active. Objective, budget optimization, budget type, special ad categories, bid type and desired cost per result are fixed at creation and cannot be changed.",
        "operationId": "update",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "The ad campaign ID."
          }
        ],
        "responses": {
          "200": {
            "description": "ad campaign updated",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/AdCampaign"
                }
              }
            }
          }
        },
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "properties": {
                  "title": {
                    "type": "string",
                    "description": "The name of the campaign."
                  },
                  "budget_amount": {
                    "type": "number",
                    "description": "The campaign budget, in the account's currency. Interpreted as daily or lifetime per the campaign's existing budget type."
                  },
                  "starts_at": {
                    "type": "string",
                    "description": "Campaign schedule start (ISO 8601). CBO only."
                  },
                  "ends_at": {
                    "type": "string",
                    "description": "Campaign schedule end (ISO 8601). CBO only."
                  },
                  "status": {
                    "type": "string",
                    "enum": [
                      "active"
                    ],
                    "description": "Set to active to launch a draft campaign (moderates and pushes it live). Live-campaign pause and resume use the pause and unpause actions."
                  }
                }
              }
            }
          },
          "required": true
        }
      },
      "delete": {
        "summary": "Delete an Ad Campaign",
        "tags": [
          "Ad Campaigns"
        ],
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:update"
            ]
          }
        ],
        "description": "Deletes an ad campaign and archives it on the ad platform (cascades to ad groups and ads). Returns true on success.",
        "operationId": "delete",
        "responses": {
          "200": {
            "description": "ad campaign deleted",
            "content": {
              "application/json": {
                "schema": {
                  "type": "boolean"
                }
              }
            }
          },
          "404": {
            "description": "ad campaign not found",
            "$ref": "#/components/responses/NotFound"
          }
        },
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "The ad campaign ID."
          }
        ]
      }
    },
    "/ad_campaigns/{id}/pause": {
      "post": {
        "summary": "Pause an Ad Campaign",
        "tags": [
          "Ad Campaigns"
        ],
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:update"
            ]
          }
        ],
        "description": "Pauses an active ad campaign.",
        "operationId": "pause",
        "responses": {
          "200": {
            "description": "ad campaign paused",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/AdCampaign"
                }
              }
            }
          }
        },
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "The ad campaign ID."
          }
        ]
      }
    },
    "/ad_campaigns/{id}/unpause": {
      "post": {
        "summary": "Unpause an Ad Campaign",
        "tags": [
          "Ad Campaigns"
        ],
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:update"
            ]
          }
        ],
        "description": "Resumes a paused ad campaign.",
        "operationId": "unpause",
        "responses": {
          "200": {
            "description": "ad campaign unpaused",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/AdCampaign"
                }
              }
            }
          }
        },
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "The ad campaign ID."
          }
        ]
      }
    },
    "/ad_groups": {
      "get": {
        "summary": "List Ad Groups",
        "tags": [
          "Ad Groups"
        ],
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:basic:read"
            ]
          }
        ],
        "description": "Lists ad groups for the account, newest first.",
        "operationId": "list",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "account_id",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Account whose ad groups to list. Defaults to the authenticated account."
          },
          {
            "name": "ad_campaign_id",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Filter to ad groups in this campaign."
          },
          {
            "name": "status",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Filter to a status (active, paused, in_review, rejected)."
          },
          {
            "name": "order",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string",
              "enum": [
                "created_at",
                "updated_at"
              ]
            },
            "description": "The field to sort by. Defaults to created_at."
          },
          {
            "name": "direction",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string",
              "enum": [
                "asc",
                "desc"
              ]
            },
            "description": "The sort direction. Defaults to desc."
          }
        ],
        "responses": {
          "200": {
            "description": "ad groups listed",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "data": {
                      "type": "array",
                      "items": {
                        "$ref": "#/components/schemas/AdGroup"
                      }
                    },
                    "page_info": {
                      "type": "object"
                    }
                  }
                }
              }
            }
          }
        }
      },
      "post": {
        "summary": "Create an Ad Group",
        "tags": [
          "Ad Groups"
        ],
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:create"
            ]
          }
        ],
        "description": "Creates an ad group (ad set) in a campaign.",
        "operationId": "create",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          }
        ],
        "responses": {
          "200": {
            "description": "ad group created",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/AdGroup"
                }
              }
            }
          },
          "404": {
            "description": "ad campaign not found",
            "$ref": "#/components/responses/NotFound"
          }
        },
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "properties": {
                  "ad_campaign_id": {
                    "type": "string",
                    "description": "The ad campaign to create the ad group in."
                  },
                  "title": {
                    "type": "string",
                    "description": "The display name of the ad group."
                  },
                  "status": {
                    "type": "string",
                    "enum": [
                      "active",
                      "paused"
                    ],
                    "description": "Initial status (default: active)."
                  },
                  "optimization_goal": {
                    "type": "string",
                    "description": "What the ad group optimizes for (e.g. conversions, link_clicks, reach)."
                  },
                  "conversion_location": {
                    "type": "string",
                    "enum": [
                      "website",
                      "profile",
                      "messaging",
                      "on_ad",
                      "instant_forms",
                      "instant_forms_and_messenger",
                      "website_and_instant_forms"
                    ],
                    "description": "Where results happen: website (conversions), profile (IG/FB engagement), messaging (DM), on_ad (engagement on the ad, surface follows the optimization goal), or the lead destinations (instant_forms, instant_forms_and_messenger, website_and_instant_forms). The lead form itself is set on the ad."
                  },
                  "message_apps": {
                    "type": "array",
                    "items": {
                      "type": "string",
                      "enum": [
                        "messenger",
                        "instagram",
                        "whatsapp"
                      ]
                    },
                    "description": "Required when conversion_location is messaging: which apps to message on. Combinations map to the matching Meta destination."
                  },
                  "conversion_event": {
                    "$ref": "#/components/schemas/ConversionEvent"
                  },
                  "budget_amount": {
                    "type": "number",
                    "description": "Ad-set budget in dollars (ABO only; omit under CBO)."
                  },
                  "budget_type": {
                    "type": "string",
                    "enum": [
                      "daily",
                      "lifetime"
                    ],
                    "description": "Whether the budget is daily or lifetime."
                  },
                  "minimum_daily_spend": {
                    "type": "number",
                    "description": "Daily spend floor within the budget."
                  },
                  "bid_type": {
                    "type": "string",
                    "enum": [
                      "minimum_cost",
                      "average_target",
                      "maximum_target"
                    ],
                    "description": "Bid strategy."
                  },
                  "desired_cost_per_result": {
                    "type": "number",
                    "description": "Target/cap cost for average_target / maximum_target."
                  },
                  "starts_at": {
                    "type": "string",
                    "description": "Schedule start, ISO 8601."
                  },
                  "ends_at": {
                    "type": "string",
                    "description": "Schedule end, ISO 8601."
                  },
                  "regions": {
                    "type": "object",
                    "description": "Geo targeting: { include / exclude: { countries (ISO 3166-1), regions (states/provinces as ISO 3166-2, e.g. US-CA), cities (keyed), zips } }."
                  },
                  "demographics": {
                    "type": "object",
                    "description": "Demographic targeting: { automatic, minimum_age, maximum_age, gender }."
                  },
                  "audiences": {
                    "type": "object",
                    "description": "Saved-audience targeting: { include, exclude } arrays of audience IDs. Incompatible with demographics.automatic (Advantage+)."
                  },
                  "languages": {
                    "type": "array",
                    "items": {
                      "type": "string"
                    },
                    "description": "Languages to target as ISO 639 codes (e.g. en, es). Empty/omitted = all languages."
                  },
                  "dynamic_creative": {
                    "type": "boolean",
                    "description": "Run Meta dynamic (Advantage+) creative for this ad set. Set at creation; immutable afterward."
                  },
                  "devices": {
                    "type": "object",
                    "description": "Device targeting: { platforms, operating_systems: [{ os, minimum_version }] }."
                  },
                  "frequency_cap": {
                    "type": "object",
                    "description": "{ maximum_impressions, per_days } — only valid for reach optimization."
                  },
                  "placements": {
                    "description": "'automatic' (Advantage+) or a list of { platform, positions }."
                  }
                },
                "required": [
                  "ad_campaign_id"
                ]
              }
            }
          },
          "required": true
        }
      }
    },
    "/ad_groups/{id}": {
      "get": {
        "summary": "Retrieve an Ad Group",
        "tags": [
          "Ad Groups"
        ],
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:basic:read"
            ]
          }
        ],
        "description": "Retrieves a single ad group.",
        "operationId": "get",
        "responses": {
          "200": {
            "description": "ad group retrieved",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/AdGroup"
                }
              }
            }
          },
          "404": {
            "description": "ad group not found",
            "$ref": "#/components/responses/NotFound"
          }
        },
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "The ad group ID."
          }
        ]
      },
      "patch": {
        "summary": "Update an Ad Group",
        "tags": [
          "Ad Groups"
        ],
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:update"
            ]
          }
        ],
        "description": "Updates an ad group's editable fields. Only the keys you send are changed.",
        "operationId": "update",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "The ad group ID."
          }
        ],
        "responses": {
          "200": {
            "description": "ad group updated",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/AdGroup"
                }
              }
            }
          }
        },
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "properties": {
                  "title": {
                    "type": "string",
                    "description": "The display name of the ad group."
                  },
                  "status": {
                    "type": "string",
                    "enum": [
                      "active",
                      "paused"
                    ],
                    "description": "Initial status (default: active)."
                  },
                  "optimization_goal": {
                    "type": "string",
                    "description": "What the ad group optimizes for (e.g. conversions, link_clicks, reach)."
                  },
                  "conversion_location": {
                    "type": "string",
                    "enum": [
                      "website",
                      "profile",
                      "messaging",
                      "on_ad",
                      "instant_forms",
                      "instant_forms_and_messenger",
                      "website_and_instant_forms"
                    ],
                    "description": "Where results happen: website (conversions), profile (IG/FB engagement), messaging (DM), on_ad (engagement on the ad, surface follows the optimization goal), or the lead destinations (instant_forms, instant_forms_and_messenger, website_and_instant_forms). The lead form itself is set on the ad."
                  },
                  "message_apps": {
                    "type": "array",
                    "items": {
                      "type": "string",
                      "enum": [
                        "messenger",
                        "instagram",
                        "whatsapp"
                      ]
                    },
                    "description": "Required when conversion_location is messaging: which apps to message on. Combinations map to the matching Meta destination."
                  },
                  "conversion_event": {
                    "$ref": "#/components/schemas/ConversionEvent"
                  },
                  "budget_amount": {
                    "type": "number",
                    "description": "Ad-set budget in dollars (ABO only; omit under CBO)."
                  },
                  "budget_type": {
                    "type": "string",
                    "enum": [
                      "daily",
                      "lifetime"
                    ],
                    "description": "Whether the budget is daily or lifetime."
                  },
                  "minimum_daily_spend": {
                    "type": "number",
                    "description": "Daily spend floor within the budget."
                  },
                  "bid_type": {
                    "type": "string",
                    "enum": [
                      "minimum_cost",
                      "average_target",
                      "maximum_target"
                    ],
                    "description": "Bid strategy."
                  },
                  "desired_cost_per_result": {
                    "type": "number",
                    "description": "Target/cap cost for average_target / maximum_target."
                  },
                  "starts_at": {
                    "type": "string",
                    "description": "Schedule start, ISO 8601."
                  },
                  "ends_at": {
                    "type": "string",
                    "description": "Schedule end, ISO 8601."
                  },
                  "regions": {
                    "type": "object",
                    "description": "Geo targeting: { include / exclude: { countries (ISO 3166-1), regions (states/provinces as ISO 3166-2, e.g. US-CA), cities (keyed), zips } }."
                  },
                  "demographics": {
                    "type": "object",
                    "description": "Demographic targeting: { automatic, minimum_age, maximum_age, gender }."
                  },
                  "audiences": {
                    "type": "object",
                    "description": "Saved-audience targeting: { include, exclude } arrays of audience IDs. Incompatible with demographics.automatic (Advantage+)."
                  },
                  "languages": {
                    "type": "array",
                    "items": {
                      "type": "string"
                    },
                    "description": "Languages to target as ISO 639 codes (e.g. en, es). Empty/omitted = all languages."
                  },
                  "devices": {
                    "type": "object",
                    "description": "Device targeting: { platforms, operating_systems: [{ os, minimum_version }] }."
                  },
                  "frequency_cap": {
                    "type": "object",
                    "description": "{ maximum_impressions, per_days } — only valid for reach optimization."
                  },
                  "placements": {
                    "description": "'automatic' (Advantage+) or a list of { platform, positions }."
                  }
                }
              }
            }
          },
          "required": true
        }
      },
      "delete": {
        "summary": "Delete an Ad Group",
        "tags": [
          "Ad Groups"
        ],
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:update"
            ]
          }
        ],
        "description": "Deletes an ad group. Returns true on success.",
        "operationId": "delete",
        "responses": {
          "200": {
            "description": "ad group deleted",
            "content": {
              "application/json": {
                "schema": {
                  "type": "boolean"
                }
              }
            }
          },
          "404": {
            "description": "ad group not found",
            "$ref": "#/components/responses/NotFound"
          }
        },
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "The ad group ID."
          }
        ]
      }
    },
    "/ad_groups/{id}/pause": {
      "post": {
        "summary": "Pause an Ad Group",
        "tags": [
          "Ad Groups"
        ],
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:update"
            ]
          }
        ],
        "description": "Pauses delivery of an ad group.",
        "operationId": "pause",
        "responses": {
          "200": {
            "description": "ad group paused",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/AdGroup"
                }
              }
            }
          }
        },
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "The ad group ID."
          }
        ]
      }
    },
    "/ad_groups/{id}/unpause": {
      "post": {
        "summary": "Unpause an Ad Group",
        "tags": [
          "Ad Groups"
        ],
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:update"
            ]
          }
        ],
        "description": "Resumes delivery of a paused ad group.",
        "operationId": "unpause",
        "responses": {
          "200": {
            "description": "ad group unpaused",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/AdGroup"
                }
              }
            }
          }
        },
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "The ad group ID."
          }
        ]
      }
    },
    "/ads": {
      "get": {
        "summary": "List Ads",
        "tags": [
          "Ads"
        ],
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:basic:read"
            ]
          }
        ],
        "description": "Lists the ads for an account, with stats over the requested window.",
        "operationId": "list",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "account_id",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "The account the ads belong to. Defaults to the account-scoped key's own account."
          },
          {
            "name": "ad_campaign_id",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Only return ads in this ad campaign."
          },
          {
            "name": "ad_group_id",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Only return ads in this ad group."
          },
          {
            "name": "status",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string",
              "enum": [
                "active",
                "paused",
                "in_review",
                "rejected"
              ]
            },
            "description": "Only return ads with this status."
          },
          {
            "name": "query",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Filter ads by a title or ID substring."
          },
          {
            "name": "order",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string",
              "enum": [
                "created_at",
                "updated_at"
              ]
            },
            "description": "The field to sort by. Defaults to created_at."
          },
          {
            "name": "direction",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string",
              "enum": [
                "asc",
                "desc"
              ]
            },
            "description": "The sort direction. Defaults to desc."
          },
          {
            "name": "created_before",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Only return ads created before this timestamp."
          },
          {
            "name": "created_after",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Only return ads created after this timestamp."
          },
          {
            "name": "stats_from",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Start of the stats window. Defaults to all-time."
          },
          {
            "name": "stats_to",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "End of the stats window. Defaults to now."
          },
          {
            "name": "first",
            "in": "query",
            "required": false,
            "schema": {
              "type": "integer",
              "default": 20,
              "maximum": 100
            },
            "description": "The number of ads to return."
          },
          {
            "name": "after",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Cursor to fetch the page after (from page_info.end_cursor)."
          },
          {
            "name": "last",
            "in": "query",
            "required": false,
            "schema": {
              "type": "integer",
              "maximum": 100
            },
            "description": "The number of ads to return from the end of the range."
          },
          {
            "name": "before",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Cursor to fetch the page before (from page_info.start_cursor)."
          }
        ],
        "responses": {
          "200": {
            "description": "ads listed",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "data": {
                      "type": "array",
                      "items": {
                        "$ref": "#/components/schemas/Ad"
                      }
                    },
                    "page_info": {
                      "type": "object",
                      "properties": {
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "end_cursor": {
                          "type": "string",
                          "nullable": true
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        },
                        "start_cursor": {
                          "type": "string",
                          "nullable": true
                        }
                      },
                      "required": [
                        "has_next_page",
                        "end_cursor",
                        "has_previous_page",
                        "start_cursor"
                      ]
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ]
                }
              }
            }
          },
          "400": {
            "description": "account_id missing and unresolvable",
            "$ref": "#/components/responses/InvalidParameters"
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          }
        }
      },
      "post": {
        "summary": "Create an Ad",
        "tags": [
          "Ads"
        ],
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:create"
            ]
          }
        ],
        "description": "Creates an ad in an ad group.",
        "operationId": "create",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          }
        ],
        "responses": {
          "200": {
            "description": "ad created",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/Ad"
                }
              }
            }
          },
          "400": {
            "description": "Whop pixel not installed on the destination",
            "$ref": "#/components/responses/InvalidParameters"
          },
          "404": {
            "description": "ad group not found",
            "$ref": "#/components/responses/NotFound"
          }
        },
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "properties": {
                  "ad_group_id": {
                    "type": "string",
                    "description": "The existing ad group to create the ad in. Provide this OR ad_group, not both."
                  },
                  "ad_group": {
                    "type": "object",
                    "description": "An inline ad group to create (same shape as POST /ad_groups, including ad_campaign_id). Creates the ad group and the ad together. Provide this OR ad_group_id."
                  },
                  "title": {
                    "type": "string",
                    "description": "The display name of the ad."
                  },
                  "primary_texts": {
                    "type": "array",
                    "items": {
                      "type": "string"
                    },
                    "description": "The primary text variants shown in the ad body."
                  },
                  "headlines": {
                    "type": "array",
                    "items": {
                      "type": "string"
                    },
                    "description": "The headline variants shown on the ad."
                  },
                  "descriptions": {
                    "type": "array",
                    "items": {
                      "type": "string"
                    },
                    "description": "The description variants shown on the ad."
                  },
                  "call_to_action": {
                    "type": "string",
                    "enum": [
                      "apply_now",
                      "book_now",
                      "call_now",
                      "contact_us",
                      "download",
                      "get_directions",
                      "get_offer",
                      "get_quote",
                      "learn_more",
                      "listen_now",
                      "message_page",
                      "no_button",
                      "open_link",
                      "order_now",
                      "request_time",
                      "see_details",
                      "see_menu",
                      "send_updates",
                      "shop_now",
                      "sign_up",
                      "subscribe",
                      "watch_more"
                    ],
                    "description": "The call-to-action button shown on the ad."
                  },
                  "url": {
                    "type": "string",
                    "description": "The URL the ad links to."
                  },
                  "url_parameters": {
                    "type": "object",
                    "description": "Query parameters appended to the destination URL, as a string-to-string map."
                  },
                  "creatives": {
                    "type": "array",
                    "items": {
                      "type": "object",
                      "properties": {
                        "id": {
                          "type": "string"
                        },
                        "format": {
                          "type": "string",
                          "enum": [
                            "square",
                            "vertical",
                            "horizontal"
                          ]
                        }
                      }
                    },
                    "description": "The ad's creatives. Each entry is an uploaded file id with an optional format; omit format for the original/uncropped asset."
                  },
                  "social_accounts": {
                    "type": "array",
                    "items": {
                      "type": "object",
                      "properties": {
                        "id": {
                          "type": "string"
                        }
                      }
                    },
                    "description": "The social accounts (Facebook page, Instagram profile) the ad runs under."
                  },
                  "post_id": {
                    "type": "string",
                    "description": "Promote an existing post instead of uploading creatives — a Facebook post or Instagram media id. Mutually exclusive with creatives."
                  },
                  "messaging_config": {
                    "type": "object",
                    "properties": {
                      "message": {
                        "type": "string"
                      },
                      "keyword": {
                        "type": "string"
                      }
                    },
                    "description": "Click-to-message welcome copy: the greeting (message) and the ice-breaker prompt (keyword)."
                  },
                  "multi_advertiser_ads": {
                    "type": "boolean",
                    "description": "Whether the ad can appear alongside other advertisers' ads in the same unit. Defaults to true."
                  },
                  "lead_form": {
                    "type": "object",
                    "description": "Instant lead form for the ad. Only allowed when the ad group's conversion_location is an instant-form destination (instant_forms, instant_forms_and_messenger, website_and_instant_forms).",
                    "properties": {
                      "name": {
                        "type": "string",
                        "description": "Internal name for the form. Auto-generated if omitted."
                      },
                      "form_type": {
                        "type": "string",
                        "enum": [
                          "more_volume",
                          "higher_intent"
                        ],
                        "description": "more_volume (default) is quickest to submit; higher_intent adds a confirmation step."
                      },
                      "intro": {
                        "type": "object",
                        "properties": {
                          "headline": {
                            "type": "string"
                          },
                          "description": {
                            "type": "string"
                          }
                        },
                        "description": "Optional intro screen shown before the questions."
                      },
                      "questions": {
                        "type": "array",
                        "items": {
                          "type": "object",
                          "properties": {
                            "type": {
                              "type": "string",
                              "enum": [
                                "email",
                                "phone",
                                "full_name",
                                "first_name",
                                "last_name",
                                "city",
                                "state",
                                "zip",
                                "country",
                                "street_address",
                                "job_title",
                                "company_name",
                                "work_email",
                                "work_phone_number",
                                "dob",
                                "gender",
                                "marital_status",
                                "relationship_status",
                                "military_status",
                                "date_time",
                                "custom"
                              ]
                            },
                            "label": {
                              "type": "string"
                            },
                            "format": {
                              "type": "string",
                              "enum": [
                                "short_answer",
                                "multiple_choice",
                                "appointment"
                              ]
                            },
                            "options": {
                              "type": "array",
                              "items": {
                                "type": "object",
                                "properties": {
                                  "value": {
                                    "type": "string"
                                  },
                                  "key": {
                                    "type": "string"
                                  },
                                  "logic": {
                                    "type": "object",
                                    "properties": {
                                      "action": {
                                        "type": "string",
                                        "enum": [
                                          "go_to_question",
                                          "submit_form",
                                          "close_form"
                                        ]
                                      },
                                      "target_question_index": {
                                        "type": "integer"
                                      },
                                      "target_end_page_index": {
                                        "type": "integer"
                                      }
                                    }
                                  }
                                }
                              }
                            }
                          }
                        },
                        "description": "The questions on the form. Standard prefill types need only a type; a custom question needs a label and a format (plus options for multiple_choice). Options carry an optional key and answer-routing logic."
                      },
                      "privacy_policy": {
                        "type": "object",
                        "properties": {
                          "url": {
                            "type": "string"
                          },
                          "link_text": {
                            "type": "string"
                          }
                        },
                        "description": "Your privacy policy. url is required by Meta."
                      },
                      "completion": {
                        "type": "object",
                        "properties": {
                          "headline": {
                            "type": "string"
                          },
                          "description": {
                            "type": "string"
                          },
                          "button_text": {
                            "type": "string"
                          },
                          "url": {
                            "type": "string"
                          }
                        },
                        "description": "Optional completion screen shown after submission; url sets the follow-up website button."
                      },
                      "disclaimer": {
                        "type": "object",
                        "properties": {
                          "title": {
                            "type": "string"
                          },
                          "body": {
                            "type": "string"
                          },
                          "checkboxes": {
                            "type": "array",
                            "items": {
                              "type": "object",
                              "properties": {
                                "text": {
                                  "type": "string"
                                },
                                "key": {
                                  "type": "string"
                                },
                                "required": {
                                  "type": "boolean"
                                },
                                "checked_by_default": {
                                  "type": "boolean"
                                }
                              }
                            }
                          }
                        },
                        "description": "Optional custom consent disclaimer with checkboxes."
                      },
                      "phone_verification": {
                        "type": "boolean",
                        "description": "Require SMS verification of the phone number (higher_intent forms)."
                      }
                    }
                  }
                }
              }
            }
          },
          "required": true
        }
      }
    },
    "/ads/{id}": {
      "get": {
        "summary": "Retrieve an Ad",
        "tags": [
          "Ads"
        ],
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:basic:read"
            ]
          }
        ],
        "description": "Retrieves a single ad with stats over the requested window.",
        "operationId": "get",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "The ad ID."
          },
          {
            "name": "stats_from",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Start of the stats window."
          },
          {
            "name": "stats_to",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "End of the stats window."
          }
        ],
        "responses": {
          "200": {
            "description": "ad retrieved",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/Ad"
                }
              }
            }
          },
          "404": {
            "description": "ad not found",
            "$ref": "#/components/responses/NotFound"
          }
        }
      },
      "patch": {
        "summary": "Update an Ad",
        "tags": [
          "Ads"
        ],
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:update"
            ]
          }
        ],
        "description": "Updates an ad's editable fields.",
        "operationId": "update",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "The ad ID."
          }
        ],
        "responses": {
          "200": {
            "description": "ad updated",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/Ad"
                }
              }
            }
          }
        },
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "properties": {
                  "title": {
                    "type": "string",
                    "description": "The display name of the ad."
                  },
                  "primary_texts": {
                    "type": "array",
                    "items": {
                      "type": "string"
                    },
                    "description": "The primary text variants shown in the ad body."
                  },
                  "headlines": {
                    "type": "array",
                    "items": {
                      "type": "string"
                    },
                    "description": "The headline variants shown on the ad."
                  },
                  "descriptions": {
                    "type": "array",
                    "items": {
                      "type": "string"
                    },
                    "description": "The description variants shown on the ad."
                  },
                  "call_to_action": {
                    "type": "string",
                    "enum": [
                      "apply_now",
                      "book_now",
                      "call_now",
                      "contact_us",
                      "download",
                      "get_directions",
                      "get_offer",
                      "get_quote",
                      "learn_more",
                      "listen_now",
                      "message_page",
                      "no_button",
                      "open_link",
                      "order_now",
                      "request_time",
                      "see_details",
                      "see_menu",
                      "send_updates",
                      "shop_now",
                      "sign_up",
                      "subscribe",
                      "watch_more"
                    ],
                    "description": "The call-to-action button shown on the ad."
                  },
                  "url": {
                    "type": "string",
                    "description": "The URL the ad links to."
                  },
                  "url_parameters": {
                    "type": "object",
                    "description": "Query parameters appended to the destination URL, as a string-to-string map."
                  },
                  "creatives": {
                    "type": "array",
                    "items": {
                      "type": "object",
                      "properties": {
                        "id": {
                          "type": "string"
                        },
                        "format": {
                          "type": "string",
                          "enum": [
                            "square",
                            "vertical",
                            "horizontal"
                          ]
                        }
                      }
                    },
                    "description": "The ad's creatives. Each entry is an uploaded file id with an optional format; omit format for the original/uncropped asset. Replaces a live ad's creative on the platform."
                  },
                  "social_accounts": {
                    "type": "array",
                    "items": {
                      "type": "object",
                      "properties": {
                        "id": {
                          "type": "string"
                        }
                      }
                    },
                    "description": "The social accounts the ad runs under."
                  },
                  "post_id": {
                    "type": "string",
                    "description": "Promote an existing post instead of uploading creatives — a Facebook post or Instagram media id. Mutually exclusive with creatives."
                  },
                  "messaging_config": {
                    "type": "object",
                    "properties": {
                      "message": {
                        "type": "string"
                      },
                      "keyword": {
                        "type": "string"
                      }
                    },
                    "description": "Click-to-message welcome copy: the greeting (message) and the ice-breaker prompt (keyword)."
                  },
                  "multi_advertiser_ads": {
                    "type": "boolean",
                    "description": "Whether the ad can appear alongside other advertisers' ads in the same unit. Defaults to true."
                  },
                  "lead_form": {
                    "type": "object",
                    "description": "Instant lead form for the ad. Only allowed when the ad group's conversion_location is an instant-form destination (instant_forms, instant_forms_and_messenger, website_and_instant_forms).",
                    "properties": {
                      "name": {
                        "type": "string",
                        "description": "Internal name for the form. Auto-generated if omitted."
                      },
                      "form_type": {
                        "type": "string",
                        "enum": [
                          "more_volume",
                          "higher_intent"
                        ],
                        "description": "more_volume (default) is quickest to submit; higher_intent adds a confirmation step."
                      },
                      "intro": {
                        "type": "object",
                        "properties": {
                          "headline": {
                            "type": "string"
                          },
                          "description": {
                            "type": "string"
                          }
                        },
                        "description": "Optional intro screen shown before the questions."
                      },
                      "questions": {
                        "type": "array",
                        "items": {
                          "type": "object",
                          "properties": {
                            "type": {
                              "type": "string",
                              "enum": [
                                "email",
                                "phone",
                                "full_name",
                                "first_name",
                                "last_name",
                                "city",
                                "state",
                                "zip",
                                "country",
                                "street_address",
                                "job_title",
                                "company_name",
                                "work_email",
                                "work_phone_number",
                                "dob",
                                "gender",
                                "marital_status",
                                "relationship_status",
                                "military_status",
                                "date_time",
                                "custom"
                              ]
                            },
                            "label": {
                              "type": "string"
                            },
                            "format": {
                              "type": "string",
                              "enum": [
                                "short_answer",
                                "multiple_choice",
                                "appointment"
                              ]
                            },
                            "options": {
                              "type": "array",
                              "items": {
                                "type": "object",
                                "properties": {
                                  "value": {
                                    "type": "string"
                                  },
                                  "key": {
                                    "type": "string"
                                  },
                                  "logic": {
                                    "type": "object",
                                    "properties": {
                                      "action": {
                                        "type": "string",
                                        "enum": [
                                          "go_to_question",
                                          "submit_form",
                                          "close_form"
                                        ]
                                      },
                                      "target_question_index": {
                                        "type": "integer"
                                      },
                                      "target_end_page_index": {
                                        "type": "integer"
                                      }
                                    }
                                  }
                                }
                              }
                            }
                          }
                        },
                        "description": "The questions on the form. Standard prefill types need only a type; a custom question needs a label and a format (plus options for multiple_choice). Options carry an optional key and answer-routing logic."
                      },
                      "privacy_policy": {
                        "type": "object",
                        "properties": {
                          "url": {
                            "type": "string"
                          },
                          "link_text": {
                            "type": "string"
                          }
                        },
                        "description": "Your privacy policy. url is required by Meta."
                      },
                      "completion": {
                        "type": "object",
                        "properties": {
                          "headline": {
                            "type": "string"
                          },
                          "description": {
                            "type": "string"
                          },
                          "button_text": {
                            "type": "string"
                          },
                          "url": {
                            "type": "string"
                          }
                        },
                        "description": "Optional completion screen shown after submission; url sets the follow-up website button."
                      },
                      "disclaimer": {
                        "type": "object",
                        "properties": {
                          "title": {
                            "type": "string"
                          },
                          "body": {
                            "type": "string"
                          },
                          "checkboxes": {
                            "type": "array",
                            "items": {
                              "type": "object",
                              "properties": {
                                "text": {
                                  "type": "string"
                                },
                                "key": {
                                  "type": "string"
                                },
                                "required": {
                                  "type": "boolean"
                                },
                                "checked_by_default": {
                                  "type": "boolean"
                                }
                              }
                            }
                          }
                        },
                        "description": "Optional custom consent disclaimer with checkboxes."
                      },
                      "phone_verification": {
                        "type": "boolean",
                        "description": "Require SMS verification of the phone number (higher_intent forms)."
                      }
                    }
                  }
                }
              }
            }
          },
          "required": true
        }
      },
      "delete": {
        "summary": "Delete an Ad",
        "tags": [
          "Ads"
        ],
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:update"
            ]
          }
        ],
        "description": "Deletes an ad. Returns true on success.",
        "operationId": "delete",
        "responses": {
          "200": {
            "description": "ad deleted",
            "content": {
              "application/json": {
                "schema": {
                  "type": "boolean"
                }
              }
            }
          },
          "404": {
            "description": "ad not found",
            "$ref": "#/components/responses/NotFound"
          }
        },
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "The ad ID."
          }
        ]
      }
    },
    "/ads/{id}/pause": {
      "post": {
        "summary": "Pause an Ad",
        "tags": [
          "Ads"
        ],
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:update"
            ]
          }
        ],
        "description": "Pauses an active ad.",
        "operationId": "pause",
        "responses": {
          "200": {
            "description": "ad paused",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/Ad"
                }
              }
            }
          }
        },
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "The ad ID."
          }
        ]
      }
    },
    "/ads/{id}/unpause": {
      "post": {
        "summary": "Unpause an Ad",
        "tags": [
          "Ads"
        ],
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:update"
            ]
          }
        ],
        "description": "Resumes a paused ad.",
        "operationId": "unpause",
        "responses": {
          "200": {
            "description": "ad unpaused",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/Ad"
                }
              }
            }
          }
        },
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "The ad ID."
          }
        ]
      }
    },
    "/audiences": {
      "get": {
        "summary": "List Audiences",
        "tags": [
          "Audiences"
        ],
        "security": [
          {
            "bearerAuth": [
              "audience:basic:read"
            ]
          }
        ],
        "description": "Lists uploaded customer-list audiences for an account. Pass `audience_id` to return a specific audience.",
        "operationId": "list",
        "parameters": [
          {
            "name": "account_id",
            "in": "query",
            "schema": {
              "type": "string"
            },
            "required": true,
            "description": "Account ID, prefixed `biz_`."
          },
          {
            "name": "audience_id",
            "in": "query",
            "schema": {
              "type": "string"
            },
            "required": false,
            "description": "Audience ID, prefixed `adaud_`, used to filter the response to one audience."
          },
          {
            "name": "first",
            "in": "query",
            "schema": {
              "type": "integer"
            },
            "required": false,
            "description": "Number of audiences to return. Defaults to 20; maximum 100."
          },
          {
            "name": "after",
            "in": "query",
            "schema": {
              "type": "string"
            },
            "required": false,
            "description": "Cursor for the next page of audiences."
          }
        ],
        "responses": {
          "200": {
            "description": "Audiences listed.",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "data": {
                      "type": "array",
                      "items": {
                        "$ref": "#/components/schemas/Audience"
                      }
                    },
                    "page_info": {
                      "type": "object",
                      "properties": {
                        "end_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "start_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        }
                      },
                      "required": [
                        "end_cursor",
                        "start_cursor",
                        "has_next_page",
                        "has_previous_page"
                      ]
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ]
                }
              }
            }
          },
          "401": {
            "description": "Missing or invalid authentication.",
            "$ref": "#/components/responses/Unauthorized"
          }
        }
      },
      "post": {
        "summary": "Create Audience",
        "tags": [
          "Audiences"
        ],
        "security": [
          {
            "bearerAuth": [
              "audience:update"
            ]
          }
        ],
        "description": "Creates an audience from an uploaded customer identity CSV file and starts processing it.",
        "operationId": "create",
        "parameters": [],
        "responses": {
          "201": {
            "description": "Audience created.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/Audience"
                }
              }
            }
          },
          "401": {
            "description": "Missing or invalid authentication.",
            "$ref": "#/components/responses/Unauthorized"
          }
        },
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "properties": {
                  "account_id": {
                    "type": "string",
                    "description": "Account ID, prefixed `biz_`."
                  },
                  "name": {
                    "type": "string",
                    "description": "Audience display name."
                  },
                  "column_mapping": {
                    "type": "object",
                    "description": "Maps supported identity fields to CSV column headers. Map at least one of `email` or `phone`.",
                    "properties": {
                      "email": {
                        "type": "string",
                        "description": "CSV header for email addresses."
                      },
                      "phone": {
                        "type": "string",
                        "description": "CSV header for phone numbers."
                      },
                      "first_name": {
                        "type": "string",
                        "description": "CSV header for first names."
                      },
                      "last_name": {
                        "type": "string",
                        "description": "CSV header for last names."
                      },
                      "country": {
                        "type": "string",
                        "description": "CSV header for ISO 3166-1 alpha-2 country codes, such as `US`."
                      }
                    }
                  },
                  "file_id": {
                    "type": "string",
                    "description": "Direct upload ID from the standard media upload endpoint."
                  }
                },
                "required": [
                  "account_id",
                  "name",
                  "column_mapping",
                  "file_id"
                ]
              }
            }
          },
          "required": true
        }
      }
    },
    "/audiences/{audience_id}": {
      "delete": {
        "summary": "Delete Audience",
        "tags": [
          "Audiences"
        ],
        "security": [
          {
            "bearerAuth": [
              "audience:update"
            ]
          }
        ],
        "description": "Deletes an audience so it is no longer available for targeting.",
        "operationId": "delete",
        "parameters": [
          {
            "name": "audience_id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "Audience ID, prefixed `adaud_`."
          }
        ],
        "responses": {
          "200": {
            "description": "Audience deleted.",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "success": {
                      "type": "boolean"
                    }
                  },
                  "required": [
                    "success"
                  ]
                }
              }
            }
          }
        }
      }
    },
    "/referrals/businesses": {
      "get": {
        "summary": "List business referrals",
        "tags": [
          "Referrals"
        ],
        "security": [
          {
            "bearerAuth": [
              "referral:basic:read"
            ]
          }
        ],
        "description": "Lists the businesses the authenticated user referred onto Whop, most recent first.",
        "operationId": "list",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "status",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string",
              "enum": [
                "active",
                "removed"
              ]
            },
            "description": "Filter by referral status."
          },
          {
            "name": "has_earnings",
            "in": "query",
            "required": false,
            "schema": {
              "type": "boolean"
            },
            "description": "When true, only businesses with at least one non-canceled, non-reversed earning paid to the caller."
          },
          {
            "name": "first",
            "in": "query",
            "required": false,
            "schema": {
              "type": "integer",
              "default": 20,
              "maximum": 100
            },
            "description": "Number of business referrals to return from the start of the window."
          },
          {
            "name": "after",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Cursor to fetch the page after (from page_info.end_cursor)."
          },
          {
            "name": "last",
            "in": "query",
            "required": false,
            "schema": {
              "type": "integer",
              "maximum": 100
            },
            "description": "Number of business referrals to return from the end of the window."
          },
          {
            "name": "before",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Cursor to fetch the page before (from page_info.start_cursor)."
          },
          {
            "name": "order",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string",
              "enum": [
                "created_at",
                "referral_started_at",
                "referral_expires_at",
                "payout_percentage"
              ],
              "default": "created_at"
            },
            "description": "The field to sort business referrals by."
          },
          {
            "name": "direction",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string",
              "enum": [
                "asc",
                "desc"
              ],
              "default": "desc"
            },
            "description": "Sort direction."
          }
        ],
        "responses": {
          "200": {
            "description": "business referrals sorted oldest first",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "data": {
                      "type": "array",
                      "items": {
                        "type": "object",
                        "properties": {
                          "object": {
                            "type": "string",
                            "enum": [
                              "business_referral"
                            ]
                          },
                          "id": {
                            "type": "string",
                            "description": "Business referral ID."
                          },
                          "account": {
                            "type": [
                              "object",
                              "null"
                            ],
                            "description": "Referred account.",
                            "properties": {
                              "id": {
                                "type": "string",
                                "description": "Referred account ID."
                              },
                              "title": {
                                "type": "string",
                                "description": "Referred account display name."
                              },
                              "logo_url": {
                                "type": [
                                  "string",
                                  "null"
                                ],
                                "description": "Referred account logo URL."
                              },
                              "route": {
                                "type": "string",
                                "description": "Referred account route."
                              }
                            },
                            "required": [
                              "id",
                              "title",
                              "logo_url",
                              "route"
                            ]
                          },
                          "user": {
                            "type": [
                              "object",
                              "null"
                            ],
                            "description": "Owner of the referred account.",
                            "properties": {
                              "id": {
                                "type": "string",
                                "description": "User ID, prefixed `user_`."
                              },
                              "username": {
                                "type": "string",
                                "description": "The user's unique username."
                              },
                              "name": {
                                "type": [
                                  "string",
                                  "null"
                                ],
                                "description": "The user's display name."
                              },
                              "profile_picture": {
                                "type": "object",
                                "properties": {
                                  "url": {
                                    "type": "string",
                                    "description": "The user's profile picture URL."
                                  }
                                },
                                "required": [
                                  "url"
                                ],
                                "description": "The user's profile picture."
                              }
                            },
                            "required": [
                              "id",
                              "username",
                              "name",
                              "profile_picture"
                            ]
                          },
                          "status": {
                            "type": "string",
                            "enum": [
                              "active",
                              "removed"
                            ],
                            "description": "Current referral status.",
                            "example": "active"
                          },
                          "payout_percentage": {
                            "type": "number",
                            "format": "float",
                            "description": "Referrer's share of Whop gross profit, as a fraction (0.3 = 30%)."
                          },
                          "referral_started_at": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "format": "date-time",
                            "description": "When the referral became active."
                          },
                          "referral_expires_at": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "format": "date-time",
                            "description": "When the referral expires."
                          },
                          "created_at": {
                            "type": "string",
                            "format": "date-time",
                            "description": "When the business referral was created."
                          },
                          "volume_usd": {
                            "type": "object",
                            "properties": {
                              "attributed": {
                                "type": "string",
                                "description": "Credited GMV (awaiting_settlement + settled); excludes canceled and reversed, in USD."
                              },
                              "awaiting_settlement": {
                                "type": "string",
                                "description": "GMV awaiting settlement (commission not yet computed), in USD."
                              },
                              "settled": {
                                "type": "string",
                                "description": "GMV of pending + completed payments, in USD."
                              }
                            },
                            "required": [
                              "attributed",
                              "awaiting_settlement",
                              "settled"
                            ]
                          },
                          "earnings_usd": {
                            "type": "object",
                            "properties": {
                              "total": {
                                "type": "string",
                                "description": "Pending + completed commission, in USD."
                              },
                              "pending": {
                                "type": "string",
                                "description": "Commission scheduled but not yet paid, in USD."
                              },
                              "completed": {
                                "type": "string",
                                "description": "Commission already paid out, in USD."
                              }
                            },
                            "required": [
                              "total",
                              "pending",
                              "completed"
                            ]
                          }
                        },
                        "required": [
                          "object",
                          "id",
                          "account",
                          "user",
                          "status",
                          "payout_percentage",
                          "referral_started_at",
                          "referral_expires_at",
                          "created_at",
                          "volume_usd",
                          "earnings_usd"
                        ]
                      }
                    },
                    "page_info": {
                      "type": "object",
                      "properties": {
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "end_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        },
                        "start_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "has_next_page",
                        "end_cursor",
                        "has_previous_page",
                        "start_cursor"
                      ]
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ]
                }
              }
            }
          },
          "400": {
            "description": "invalid sort field",
            "$ref": "#/components/responses/InvalidParameters"
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          },
          "403": {
            "description": "credential lacks the referral-read scope",
            "$ref": "#/components/responses/Forbidden"
          }
        }
      }
    },
    "/referrals/businesses/{id}": {
      "get": {
        "summary": "Retrieve a business referral",
        "tags": [
          "Referrals"
        ],
        "security": [
          {
            "bearerAuth": [
              "referral:basic:read"
            ]
          }
        ],
        "description": "Retrieves a single referred business and its referral terms.",
        "operationId": "get",
        "responses": {
          "200": {
            "description": "business referral retrieved",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "object": {
                      "type": "string",
                      "enum": [
                        "business_referral"
                      ]
                    },
                    "id": {
                      "type": "string",
                      "description": "Business referral ID."
                    },
                    "account": {
                      "type": [
                        "object",
                        "null"
                      ],
                      "description": "Referred account.",
                      "properties": {
                        "id": {
                          "type": "string",
                          "description": "Referred account ID."
                        },
                        "title": {
                          "type": "string",
                          "description": "Referred account display name."
                        },
                        "logo_url": {
                          "type": [
                            "string",
                            "null"
                          ],
                          "description": "Referred account logo URL."
                        },
                        "route": {
                          "type": "string",
                          "description": "Referred account route."
                        },
                        "capabilities": {
                          "$ref": "#/components/schemas/AccountCapabilities",
                          "nullable": true
                        },
                        "required_actions": {
                          "type": [
                            "array",
                            "null"
                          ],
                          "items": {
                            "$ref": "#/components/schemas/AccountRequiredAction"
                          },
                          "description": "Actions the referred account owner must take to unblock capabilities."
                        },
                        "recommended_actions": {
                          "type": [
                            "array",
                            "null"
                          ],
                          "items": {
                            "$ref": "#/components/schemas/AccountRecommendedAction"
                          },
                          "description": "Optional actions that unlock capabilities or grow the referred account."
                        }
                      },
                      "required": [
                        "id",
                        "title",
                        "logo_url",
                        "route",
                        "capabilities",
                        "required_actions",
                        "recommended_actions"
                      ]
                    },
                    "user": {
                      "type": [
                        "object",
                        "null"
                      ],
                      "description": "Owner of the referred account.",
                      "properties": {
                        "id": {
                          "type": "string",
                          "description": "User ID, prefixed `user_`."
                        },
                        "username": {
                          "type": "string",
                          "description": "The user's unique username."
                        },
                        "name": {
                          "type": [
                            "string",
                            "null"
                          ],
                          "description": "The user's display name."
                        },
                        "profile_picture": {
                          "type": "object",
                          "properties": {
                            "url": {
                              "type": "string",
                              "description": "The user's profile picture URL."
                            }
                          },
                          "required": [
                            "url"
                          ],
                          "description": "The user's profile picture."
                        }
                      },
                      "required": [
                        "id",
                        "username",
                        "name",
                        "profile_picture"
                      ]
                    },
                    "status": {
                      "type": "string",
                      "enum": [
                        "active",
                        "removed"
                      ],
                      "description": "Current referral status."
                    },
                    "payout_percentage": {
                      "type": "number",
                      "format": "float",
                      "description": "Referrer's share of Whop gross profit, as a fraction (0.3 = 30%)."
                    },
                    "referral_started_at": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "format": "date-time",
                      "description": "When the referral became active."
                    },
                    "referral_expires_at": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "format": "date-time",
                      "description": "When the referral expires."
                    },
                    "created_at": {
                      "type": "string",
                      "format": "date-time",
                      "description": "When the business referral was created."
                    },
                    "volume_usd": {
                      "type": "object",
                      "properties": {
                        "attributed": {
                          "type": "string",
                          "description": "Credited GMV (awaiting_settlement + settled); excludes canceled and reversed, in USD."
                        },
                        "awaiting_settlement": {
                          "type": "string",
                          "description": "GMV awaiting settlement (commission not yet computed), in USD."
                        },
                        "settled": {
                          "type": "string",
                          "description": "GMV of pending + completed payments, in USD."
                        }
                      },
                      "required": [
                        "attributed",
                        "awaiting_settlement",
                        "settled"
                      ]
                    },
                    "earnings_usd": {
                      "type": "object",
                      "properties": {
                        "total": {
                          "type": "string",
                          "description": "Pending + completed commission, in USD."
                        },
                        "pending": {
                          "type": "string",
                          "description": "Commission scheduled but not yet paid, in USD."
                        },
                        "completed": {
                          "type": "string",
                          "description": "Commission already paid out, in USD."
                        }
                      },
                      "required": [
                        "total",
                        "pending",
                        "completed"
                      ]
                    }
                  },
                  "required": [
                    "object",
                    "id",
                    "account",
                    "user",
                    "status",
                    "payout_percentage",
                    "referral_started_at",
                    "referral_expires_at",
                    "created_at",
                    "volume_usd",
                    "earnings_usd"
                  ]
                }
              }
            }
          },
          "404": {
            "description": "no such business referral is visible to the credential",
            "$ref": "#/components/responses/NotFound"
          }
        },
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "The business referral ID (a coma_ identifier)."
          }
        ]
      }
    },
    "/referrals/businesses/{id}/earnings": {
      "get": {
        "summary": "List a business referral's earnings",
        "tags": [
          "Referrals"
        ],
        "security": [
          {
            "bearerAuth": [
              "referral:basic:read"
            ]
          }
        ],
        "description": "Lists the earnings Whop pays out for one referred business's activity, most recent first.",
        "operationId": "earnings",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "The business referral ID (a coma_ identifier)."
          },
          {
            "name": "status",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string",
              "enum": [
                "awaiting_settlement",
                "pending",
                "completed",
                "canceled",
                "reversed"
              ]
            },
            "description": "Filter by earning status."
          },
          {
            "name": "first",
            "in": "query",
            "required": false,
            "schema": {
              "type": "integer",
              "default": 20,
              "maximum": 100
            }
          },
          {
            "name": "after",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "name": "last",
            "in": "query",
            "required": false,
            "schema": {
              "type": "integer",
              "maximum": 100
            }
          },
          {
            "name": "before",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "name": "order",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string",
              "enum": [
                "created_at",
                "commission_amount",
                "transaction_amount",
                "payout_at"
              ],
              "default": "created_at"
            },
            "description": "The field to sort earnings by."
          },
          {
            "name": "direction",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string",
              "enum": [
                "asc",
                "desc"
              ],
              "default": "desc"
            },
            "description": "Sort direction."
          }
        ],
        "responses": {
          "200": {
            "description": "earnings sorted by transaction amount",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "data": {
                      "type": "array",
                      "items": {
                        "type": "object",
                        "properties": {
                          "object": {
                            "type": "string",
                            "enum": [
                              "business_referral_earning"
                            ]
                          },
                          "id": {
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "account": {
                            "type": [
                              "object",
                              "null"
                            ],
                            "description": "Referred account.",
                            "properties": {
                              "id": {
                                "type": "string",
                                "description": "Referred account ID."
                              },
                              "title": {
                                "type": "string",
                                "description": "Referred account display name."
                              },
                              "logo_url": {
                                "type": [
                                  "string",
                                  "null"
                                ],
                                "description": "Referred account logo URL."
                              },
                              "route": {
                                "type": "string",
                                "description": "Referred account route."
                              }
                            },
                            "required": [
                              "id",
                              "title",
                              "logo_url",
                              "route"
                            ]
                          },
                          "product": {
                            "type": [
                              "object",
                              "null"
                            ],
                            "properties": {
                              "id": {
                                "type": "string"
                              },
                              "title": {
                                "type": "string"
                              },
                              "route": {
                                "type": "string"
                              }
                            },
                            "required": [
                              "id",
                              "title",
                              "route"
                            ]
                          },
                          "status": {
                            "type": "string",
                            "enum": [
                              "awaiting_settlement",
                              "pending",
                              "completed",
                              "canceled",
                              "reversed"
                            ],
                            "example": "pending",
                            "description": "Current status of the earning."
                          },
                          "transaction_amount_usd": {
                            "type": "string",
                            "description": "The sale amount the commission is calculated from, in USD."
                          },
                          "payout_percentage": {
                            "type": [
                              "number",
                              "null"
                            ],
                            "format": "float",
                            "description": "The referrer's share of Whop's gross profit, as a fraction (0.3 = 30%). Null until the earning settles."
                          },
                          "commission_amount_usd": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "What the referrer earns, in USD. Null until the earning settles."
                          },
                          "cancelation_reason": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "Why the earning was canceled or reversed, if applicable."
                          },
                          "created_at": {
                            "type": "string",
                            "format": "date-time"
                          },
                          "payout_at": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "format": "date-time"
                          },
                          "resource": {
                            "anyOf": [
                              {
                                "type": [
                                  "object",
                                  "null"
                                ],
                                "properties": {
                                  "object": {
                                    "type": "string",
                                    "enum": [
                                      "receipt"
                                    ]
                                  },
                                  "id": {
                                    "type": "string"
                                  },
                                  "currency": {
                                    "type": "string"
                                  },
                                  "payment_method_type": {
                                    "type": [
                                      "string",
                                      "null"
                                    ]
                                  },
                                  "processor": {
                                    "type": [
                                      "string",
                                      "null"
                                    ]
                                  },
                                  "brand": {
                                    "type": [
                                      "string",
                                      "null"
                                    ]
                                  },
                                  "last4": {
                                    "type": [
                                      "string",
                                      "null"
                                    ]
                                  },
                                  "created_at": {
                                    "type": "string",
                                    "format": "date-time"
                                  },
                                  "alternative_payment_method": {
                                    "type": [
                                      "object",
                                      "null"
                                    ],
                                    "properties": {
                                      "name": {
                                        "type": "string"
                                      },
                                      "image_url": {
                                        "type": [
                                          "string",
                                          "null"
                                        ]
                                      }
                                    },
                                    "required": [
                                      "name",
                                      "image_url"
                                    ]
                                  }
                                },
                                "required": [
                                  "object",
                                  "id",
                                  "currency",
                                  "payment_method_type",
                                  "processor",
                                  "brand",
                                  "last4",
                                  "created_at",
                                  "alternative_payment_method"
                                ]
                              },
                              {
                                "type": "null"
                              }
                            ],
                            "description": "The resource that generated the affiliate earning."
                          }
                        },
                        "required": [
                          "object",
                          "id",
                          "account",
                          "product",
                          "status",
                          "transaction_amount_usd",
                          "payout_percentage",
                          "commission_amount_usd",
                          "cancelation_reason",
                          "created_at",
                          "payout_at",
                          "resource"
                        ]
                      }
                    },
                    "page_info": {
                      "type": "object",
                      "properties": {
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "end_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        },
                        "start_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "has_next_page",
                        "end_cursor",
                        "has_previous_page",
                        "start_cursor"
                      ]
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ]
                }
              }
            }
          },
          "400": {
            "description": "invalid sort direction",
            "$ref": "#/components/responses/InvalidParameters"
          }
        }
      }
    },
    "/referrals/businesses/earnings": {
      "get": {
        "summary": "List all business referral earnings",
        "tags": [
          "Referrals"
        ],
        "security": [
          {
            "bearerAuth": [
              "referral:basic:read"
            ]
          }
        ],
        "description": "Lists every business referral earning the authenticated user has, most recent first.",
        "operationId": "all-earnings",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "status",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string",
              "enum": [
                "awaiting_settlement",
                "pending",
                "completed",
                "canceled",
                "reversed"
              ]
            },
            "description": "Filter by earning status."
          },
          {
            "name": "first",
            "in": "query",
            "required": false,
            "schema": {
              "type": "integer",
              "default": 20,
              "maximum": 100
            }
          },
          {
            "name": "after",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "name": "last",
            "in": "query",
            "required": false,
            "schema": {
              "type": "integer",
              "maximum": 100
            }
          },
          {
            "name": "before",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "name": "order",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string",
              "enum": [
                "created_at",
                "commission_amount",
                "transaction_amount",
                "payout_at"
              ],
              "default": "created_at"
            },
            "description": "The field to sort earnings by."
          },
          {
            "name": "direction",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string",
              "enum": [
                "asc",
                "desc"
              ],
              "default": "desc"
            },
            "description": "Sort direction."
          }
        ],
        "responses": {
          "200": {
            "description": "earnings filtered by status",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "data": {
                      "type": "array",
                      "items": {
                        "type": "object",
                        "properties": {
                          "object": {
                            "type": "string",
                            "enum": [
                              "business_referral_earning"
                            ]
                          },
                          "id": {
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "account": {
                            "type": [
                              "object",
                              "null"
                            ],
                            "description": "Referred account.",
                            "properties": {
                              "id": {
                                "type": "string",
                                "description": "Referred account ID."
                              },
                              "title": {
                                "type": "string",
                                "description": "Referred account display name."
                              },
                              "logo_url": {
                                "type": [
                                  "string",
                                  "null"
                                ],
                                "description": "Referred account logo URL."
                              },
                              "route": {
                                "type": "string",
                                "description": "Referred account route."
                              }
                            },
                            "required": [
                              "id",
                              "title",
                              "logo_url",
                              "route"
                            ]
                          },
                          "product": {
                            "type": [
                              "object",
                              "null"
                            ],
                            "properties": {
                              "id": {
                                "type": "string"
                              },
                              "title": {
                                "type": "string"
                              },
                              "route": {
                                "type": "string"
                              }
                            },
                            "required": [
                              "id",
                              "title",
                              "route"
                            ]
                          },
                          "status": {
                            "type": "string",
                            "enum": [
                              "awaiting_settlement",
                              "pending",
                              "completed",
                              "canceled",
                              "reversed"
                            ],
                            "example": "pending",
                            "description": "Current status of the earning."
                          },
                          "transaction_amount_usd": {
                            "type": "string",
                            "description": "The sale amount the commission is calculated from, in USD."
                          },
                          "payout_percentage": {
                            "type": [
                              "number",
                              "null"
                            ],
                            "format": "float",
                            "description": "The referrer's share of Whop's gross profit, as a fraction (0.3 = 30%). Null until the earning settles."
                          },
                          "commission_amount_usd": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "What the referrer earns, in USD. Null until the earning settles."
                          },
                          "cancelation_reason": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "Why the earning was canceled or reversed, if applicable."
                          },
                          "created_at": {
                            "type": "string",
                            "format": "date-time"
                          },
                          "payout_at": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "format": "date-time"
                          },
                          "resource": {
                            "anyOf": [
                              {
                                "type": [
                                  "object",
                                  "null"
                                ],
                                "properties": {
                                  "object": {
                                    "type": "string",
                                    "enum": [
                                      "receipt"
                                    ]
                                  },
                                  "id": {
                                    "type": "string"
                                  },
                                  "currency": {
                                    "type": "string"
                                  },
                                  "payment_method_type": {
                                    "type": [
                                      "string",
                                      "null"
                                    ]
                                  },
                                  "processor": {
                                    "type": [
                                      "string",
                                      "null"
                                    ]
                                  },
                                  "brand": {
                                    "type": [
                                      "string",
                                      "null"
                                    ]
                                  },
                                  "last4": {
                                    "type": [
                                      "string",
                                      "null"
                                    ]
                                  },
                                  "created_at": {
                                    "type": "string",
                                    "format": "date-time"
                                  },
                                  "alternative_payment_method": {
                                    "type": [
                                      "object",
                                      "null"
                                    ],
                                    "properties": {
                                      "name": {
                                        "type": "string"
                                      },
                                      "image_url": {
                                        "type": [
                                          "string",
                                          "null"
                                        ]
                                      }
                                    },
                                    "required": [
                                      "name",
                                      "image_url"
                                    ]
                                  }
                                },
                                "required": [
                                  "object",
                                  "id",
                                  "currency",
                                  "payment_method_type",
                                  "processor",
                                  "brand",
                                  "last4",
                                  "created_at",
                                  "alternative_payment_method"
                                ]
                              },
                              {
                                "type": "null"
                              }
                            ],
                            "description": "The resource that generated the affiliate earning."
                          }
                        },
                        "required": [
                          "object",
                          "id",
                          "account",
                          "product",
                          "status",
                          "transaction_amount_usd",
                          "payout_percentage",
                          "commission_amount_usd",
                          "cancelation_reason",
                          "created_at",
                          "payout_at",
                          "resource"
                        ]
                      }
                    },
                    "page_info": {
                      "type": "object",
                      "properties": {
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "end_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        },
                        "start_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "has_next_page",
                        "end_cursor",
                        "has_previous_page",
                        "start_cursor"
                      ]
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ]
                }
              }
            }
          },
          "400": {
            "description": "invalid status filter",
            "$ref": "#/components/responses/InvalidParameters"
          }
        }
      }
    },
    "/cards": {
      "get": {
        "summary": "List Cards",
        "tags": [
          "Cards"
        ],
        "security": [
          {
            "bearerAuth": [
              "payout:account:read"
            ]
          }
        ],
        "description": "Lists issued Whop virtual cards for an account or user, including pending invitation cards that have not been issued by the card provider yet. Pass exactly one of account_id (a biz_ identifier) or user_id (a user_ identifier). Non-owner team members only see cards assigned to them. Users without the payout:account:read scope can still list cards assigned to them (for example moderators or external cardholders). Use GET /cards/:card_id to retrieve a single card with its secrets.",
        "operationId": "list",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "account_id",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "The owning account ID (a biz_ identifier). Provide this or user_id."
          },
          {
            "name": "user_id",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "The owning user ID (a user_ identifier). Provide this or account_id."
          }
        ],
        "responses": {
          "200": {
            "description": "cards listed for an assigned cardholder without the ledger scope",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "data": {
                      "type": "array",
                      "items": {
                        "type": "object",
                        "properties": {
                          "object": {
                            "type": "string",
                            "enum": [
                              "card"
                            ]
                          },
                          "id": {
                            "type": "string",
                            "description": "Card ID, prefixed `icrd_`."
                          },
                          "name": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "Card display name."
                          },
                          "type": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "enum": [
                              null,
                              "virtual",
                              "physical"
                            ],
                            "description": "The card type."
                          },
                          "status": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "enum": [
                              null,
                              "active",
                              "frozen",
                              "canceled",
                              "invited"
                            ],
                            "description": "The card status."
                          },
                          "last4": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "Last four digits of the card number. `null` for pending invitation cards."
                          },
                          "expiration_month": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "Card expiration month."
                          },
                          "expiration_year": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "Card expiration year."
                          },
                          "user_id": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "Cardholder user ID, prefixed `user_`, when assigned."
                          },
                          "spent_last_month": {
                            "type": [
                              "integer",
                              "null"
                            ],
                            "description": "Total spend in the last 30 days, in cents."
                          },
                          "limit": {
                            "type": [
                              "object",
                              "null"
                            ],
                            "description": "The spending limit configuration.",
                            "properties": {
                              "amount": {
                                "type": "number",
                                "description": "The limit amount in dollars."
                              },
                              "frequency": {
                                "type": "string",
                                "description": "Limit window, for example `per24HourPeriod` or `perAuthorization`."
                              }
                            },
                            "required": [
                              "amount",
                              "frequency"
                            ]
                          },
                          "billing": {
                            "type": [
                              "object",
                              "null"
                            ],
                            "description": "The billing address.",
                            "properties": {
                              "line1": {
                                "type": [
                                  "string",
                                  "null"
                                ],
                                "description": "Street address line 1."
                              },
                              "line2": {
                                "type": [
                                  "string",
                                  "null"
                                ],
                                "description": "Street address line 2."
                              },
                              "city": {
                                "type": [
                                  "string",
                                  "null"
                                ],
                                "description": "Billing city."
                              },
                              "region": {
                                "type": [
                                  "string",
                                  "null"
                                ],
                                "description": "Billing region or state."
                              },
                              "postal_code": {
                                "type": [
                                  "string",
                                  "null"
                                ],
                                "description": "Billing postal code."
                              },
                              "country_code": {
                                "type": [
                                  "string",
                                  "null"
                                ],
                                "description": "Billing country code."
                              }
                            },
                            "required": [
                              "line1",
                              "line2",
                              "city",
                              "region",
                              "postal_code",
                              "country_code"
                            ]
                          },
                          "created_at": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "format": "date-time",
                            "description": "When the card was created."
                          },
                          "canceled_at": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "format": "date-time",
                            "description": "When the card was canceled."
                          },
                          "secrets": {
                            "type": [
                              "object",
                              "null"
                            ],
                            "description": "Sensitive card details. Present only on `GET /cards/:card_id` for active cards; `null` when the card is inactive or details cannot be retrieved.",
                            "properties": {
                              "card_number": {
                                "type": "string",
                                "description": "Full card number."
                              },
                              "cvc": {
                                "type": "string",
                                "description": "Card verification code."
                              },
                              "name_on_card": {
                                "type": [
                                  "string",
                                  "null"
                                ],
                                "description": "Cardholder name printed on the card."
                              }
                            },
                            "required": [
                              "card_number",
                              "cvc",
                              "name_on_card"
                            ]
                          }
                        },
                        "required": [
                          "object",
                          "id",
                          "name",
                          "type",
                          "status",
                          "last4",
                          "expiration_month",
                          "expiration_year",
                          "user_id",
                          "spent_last_month",
                          "limit",
                          "billing",
                          "created_at",
                          "canceled_at"
                        ]
                      }
                    }
                  },
                  "required": [
                    "data"
                  ]
                }
              }
            }
          },
          "400": {
            "description": "neither account_id nor user_id provided",
            "$ref": "#/components/responses/InvalidParameters"
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          },
          "403": {
            "description": "credential lacks the payout-account-read scope",
            "$ref": "#/components/responses/Forbidden"
          },
          "404": {
            "description": "no ledger for that owner is visible to the credential",
            "$ref": "#/components/responses/NotFound"
          }
        }
      },
      "post": {
        "summary": "Create Card",
        "tags": [
          "Cards"
        ],
        "security": [
          {
            "bearerAuth": [
              "payout:account:update"
            ]
          }
        ],
        "description": "Issues a virtual card. For an individual (consumer) card issuing account, the card is issued to the account's own cardholder. For a company (business) card issuing account, pass assigned_user_id to issue the card to a company member; if that member is not yet an approved card-issuing user, the card is provisioned asynchronously or an onboarding invitation is sent (HTTP 202). Pass exactly one of account_id (a biz_ identifier) or user_id (a user_ identifier). Returns the newly created card resource.",
        "operationId": "create",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          }
        ],
        "responses": {
          "201": {
            "description": "company card created for an assigned member",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "object": {
                      "type": "string",
                      "enum": [
                        "card"
                      ]
                    },
                    "id": {
                      "type": "string",
                      "description": "Card ID, prefixed `icrd_`."
                    },
                    "name": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "description": "Card display name."
                    },
                    "type": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "enum": [
                        null,
                        "virtual",
                        "physical"
                      ],
                      "description": "The card type."
                    },
                    "status": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "enum": [
                        null,
                        "active",
                        "frozen",
                        "canceled",
                        "invited"
                      ],
                      "description": "The card status."
                    },
                    "last4": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "description": "Last four digits of the card number. `null` for pending invitation cards."
                    },
                    "expiration_month": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "description": "Card expiration month."
                    },
                    "expiration_year": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "description": "Card expiration year."
                    },
                    "user_id": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "description": "Cardholder user ID, prefixed `user_`, when assigned."
                    },
                    "spent_last_month": {
                      "type": [
                        "integer",
                        "null"
                      ],
                      "description": "Total spend in the last 30 days, in cents."
                    },
                    "limit": {
                      "type": [
                        "object",
                        "null"
                      ],
                      "description": "The spending limit configuration.",
                      "properties": {
                        "amount": {
                          "type": "number",
                          "description": "The limit amount in dollars."
                        },
                        "frequency": {
                          "type": "string",
                          "description": "Limit window, for example `per24HourPeriod` or `perAuthorization`."
                        }
                      },
                      "required": [
                        "amount",
                        "frequency"
                      ]
                    },
                    "billing": {
                      "type": [
                        "object",
                        "null"
                      ],
                      "description": "The billing address.",
                      "properties": {
                        "line1": {
                          "type": [
                            "string",
                            "null"
                          ],
                          "description": "Street address line 1."
                        },
                        "line2": {
                          "type": [
                            "string",
                            "null"
                          ],
                          "description": "Street address line 2."
                        },
                        "city": {
                          "type": [
                            "string",
                            "null"
                          ],
                          "description": "Billing city."
                        },
                        "region": {
                          "type": [
                            "string",
                            "null"
                          ],
                          "description": "Billing region or state."
                        },
                        "postal_code": {
                          "type": [
                            "string",
                            "null"
                          ],
                          "description": "Billing postal code."
                        },
                        "country_code": {
                          "type": [
                            "string",
                            "null"
                          ],
                          "description": "Billing country code."
                        }
                      },
                      "required": [
                        "line1",
                        "line2",
                        "city",
                        "region",
                        "postal_code",
                        "country_code"
                      ]
                    },
                    "created_at": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "format": "date-time",
                      "description": "When the card was created."
                    },
                    "canceled_at": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "format": "date-time",
                      "description": "When the card was canceled."
                    },
                    "secrets": {
                      "type": [
                        "object",
                        "null"
                      ],
                      "description": "Sensitive card details. Present only on `GET /cards/:card_id` for active cards; `null` when the card is inactive or details cannot be retrieved.",
                      "properties": {
                        "card_number": {
                          "type": "string",
                          "description": "Full card number."
                        },
                        "cvc": {
                          "type": "string",
                          "description": "Card verification code."
                        },
                        "name_on_card": {
                          "type": [
                            "string",
                            "null"
                          ],
                          "description": "Cardholder name printed on the card."
                        }
                      },
                      "required": [
                        "card_number",
                        "cvc",
                        "name_on_card"
                      ]
                    }
                  },
                  "required": [
                    "object",
                    "id",
                    "name",
                    "type",
                    "status",
                    "last4",
                    "expiration_month",
                    "expiration_year",
                    "user_id",
                    "spent_last_month",
                    "limit",
                    "billing",
                    "created_at",
                    "canceled_at"
                  ]
                }
              }
            }
          },
          "202": {
            "description": "card not issued synchronously: provisioning started or onboarding invitation sent",
            "content": {
              "application/json": {
                "schema": {
                  "oneOf": [
                    {
                      "type": "object",
                      "properties": {
                        "object": {
                          "type": "string",
                          "enum": [
                            "card_provisioning"
                          ]
                        },
                        "provisioning_job_id": {
                          "type": "string",
                          "description": "Background job ID to poll until the card is ready."
                        }
                      },
                      "required": [
                        "object",
                        "provisioning_job_id"
                      ]
                    },
                    {
                      "type": "object",
                      "properties": {
                        "object": {
                          "type": "string",
                          "enum": [
                            "card_invitation"
                          ]
                        },
                        "invitation_sent": {
                          "type": "boolean",
                          "description": "Always true; the member must finish onboarding before the card is issued."
                        }
                      },
                      "required": [
                        "object",
                        "invitation_sent"
                      ]
                    }
                  ]
                }
              }
            }
          },
          "400": {
            "description": "business card issuing account without an assigned_user_id",
            "$ref": "#/components/responses/InvalidParameters"
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          },
          "403": {
            "description": "credential lacks the payout-account-update scope",
            "$ref": "#/components/responses/Forbidden"
          },
          "404": {
            "description": "no ledger for that owner is visible to the credential",
            "$ref": "#/components/responses/NotFound"
          }
        },
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "properties": {
                  "account_id": {
                    "type": "string",
                    "description": "The owning account ID (a biz_ identifier). Provide this or user_id."
                  },
                  "user_id": {
                    "type": "string",
                    "description": "The owning user ID (a user_ identifier). Provide this or account_id."
                  },
                  "assigned_user_id": {
                    "type": "string",
                    "description": "The company member (a user_ identifier) to assign the card to. Required for company (business) card issuing accounts."
                  },
                  "name": {
                    "type": "string",
                    "description": "A display name for the card."
                  },
                  "spend_limit": {
                    "type": "number",
                    "description": "Spending limit amount, in dollars."
                  },
                  "spend_limit_frequency": {
                    "type": "string",
                    "enum": [
                      "daily",
                      "weekly",
                      "monthly",
                      "one_time"
                    ],
                    "description": "The spending limit window."
                  },
                  "transaction_limit": {
                    "type": "number",
                    "description": "Per-transaction limit amount, in dollars."
                  }
                }
              }
            }
          },
          "required": true
        }
      }
    },
    "/cards/{card_id}": {
      "get": {
        "summary": "Retrieve Card",
        "tags": [
          "Cards"
        ],
        "security": [
          {
            "bearerAuth": [
              "payout:account:read"
            ]
          }
        ],
        "description": "Retrieves a single card by its icrd_ identifier, including its secrets (full card number, CVC, and cardholder name) for active cards.",
        "operationId": "get",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "card_id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "Card ID to retrieve, prefixed `icrd_`."
          },
          {
            "name": "account_id",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "The owning account ID (a biz_ identifier). Provide this or user_id."
          },
          {
            "name": "user_id",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "The owning user ID (a user_ identifier). Provide this or account_id."
          }
        ],
        "responses": {
          "200": {
            "description": "card retrieved with secrets",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "object": {
                      "type": "string",
                      "enum": [
                        "card"
                      ]
                    },
                    "id": {
                      "type": "string",
                      "description": "Card ID, prefixed `icrd_`."
                    },
                    "name": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "description": "Card display name."
                    },
                    "type": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "enum": [
                        null,
                        "virtual",
                        "physical"
                      ],
                      "description": "The card type."
                    },
                    "status": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "enum": [
                        null,
                        "active",
                        "frozen",
                        "canceled",
                        "invited"
                      ],
                      "description": "The card status."
                    },
                    "last4": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "description": "Last four digits of the card number. `null` for pending invitation cards."
                    },
                    "expiration_month": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "description": "Card expiration month."
                    },
                    "expiration_year": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "description": "Card expiration year."
                    },
                    "user_id": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "description": "Cardholder user ID, prefixed `user_`, when assigned."
                    },
                    "spent_last_month": {
                      "type": [
                        "integer",
                        "null"
                      ],
                      "description": "Total spend in the last 30 days, in cents."
                    },
                    "limit": {
                      "type": [
                        "object",
                        "null"
                      ],
                      "description": "The spending limit configuration.",
                      "properties": {
                        "amount": {
                          "type": "number",
                          "description": "The limit amount in dollars."
                        },
                        "frequency": {
                          "type": "string",
                          "description": "Limit window, for example `per24HourPeriod` or `perAuthorization`."
                        }
                      },
                      "required": [
                        "amount",
                        "frequency"
                      ]
                    },
                    "billing": {
                      "type": [
                        "object",
                        "null"
                      ],
                      "description": "The billing address.",
                      "properties": {
                        "line1": {
                          "type": [
                            "string",
                            "null"
                          ],
                          "description": "Street address line 1."
                        },
                        "line2": {
                          "type": [
                            "string",
                            "null"
                          ],
                          "description": "Street address line 2."
                        },
                        "city": {
                          "type": [
                            "string",
                            "null"
                          ],
                          "description": "Billing city."
                        },
                        "region": {
                          "type": [
                            "string",
                            "null"
                          ],
                          "description": "Billing region or state."
                        },
                        "postal_code": {
                          "type": [
                            "string",
                            "null"
                          ],
                          "description": "Billing postal code."
                        },
                        "country_code": {
                          "type": [
                            "string",
                            "null"
                          ],
                          "description": "Billing country code."
                        }
                      },
                      "required": [
                        "line1",
                        "line2",
                        "city",
                        "region",
                        "postal_code",
                        "country_code"
                      ]
                    },
                    "created_at": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "format": "date-time",
                      "description": "When the card was created."
                    },
                    "canceled_at": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "format": "date-time",
                      "description": "When the card was canceled."
                    },
                    "secrets": {
                      "type": [
                        "object",
                        "null"
                      ],
                      "description": "Sensitive card details. Present only on `GET /cards/:card_id` for active cards; `null` when the card is inactive or details cannot be retrieved.",
                      "properties": {
                        "card_number": {
                          "type": "string",
                          "description": "Full card number."
                        },
                        "cvc": {
                          "type": "string",
                          "description": "Card verification code."
                        },
                        "name_on_card": {
                          "type": [
                            "string",
                            "null"
                          ],
                          "description": "Cardholder name printed on the card."
                        }
                      },
                      "required": [
                        "card_number",
                        "cvc",
                        "name_on_card"
                      ]
                    }
                  },
                  "required": [
                    "object",
                    "id",
                    "name",
                    "type",
                    "status",
                    "last4",
                    "expiration_month",
                    "expiration_year",
                    "user_id",
                    "spent_last_month",
                    "limit",
                    "billing",
                    "created_at",
                    "canceled_at"
                  ]
                }
              }
            }
          },
          "404": {
            "description": "card not found",
            "$ref": "#/components/responses/NotFound"
          }
        }
      }
    },
    "/checkout_configurations": {
      "get": {
        "summary": "List checkout configurations",
        "tags": [
          "Checkout Configurations"
        ],
        "operationId": "list",
        "security": [
          {
            "bearerAuth": []
          }
        ],
        "description": "Returns a paginated list of checkout configurations for a company.",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "company_id",
            "in": "query",
            "required": true,
            "description": "The ID of the company to list checkout configurations for.",
            "schema": {
              "type": "string"
            }
          },
          {
            "name": "plan_id",
            "in": "query",
            "required": false,
            "description": "Filter by plan ID.",
            "schema": {
              "type": "string"
            }
          },
          {
            "name": "created_before",
            "in": "query",
            "required": false,
            "description": "Filter to configurations created before this Unix timestamp.",
            "schema": {
              "type": "integer"
            }
          },
          {
            "name": "created_after",
            "in": "query",
            "required": false,
            "description": "Filter to configurations created after this Unix timestamp.",
            "schema": {
              "type": "integer"
            }
          },
          {
            "name": "order",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string",
              "enum": [
                "created_at"
              ],
              "default": "created_at"
            },
            "description": "The field to sort checkout configurations by."
          },
          {
            "name": "direction",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string",
              "enum": [
                "asc",
                "desc"
              ],
              "default": "desc"
            },
            "description": "Sort direction."
          },
          {
            "name": "first",
            "in": "query",
            "required": false,
            "description": "Number of results to return (forward pagination).",
            "schema": {
              "type": "integer"
            }
          },
          {
            "name": "after",
            "in": "query",
            "required": false,
            "description": "Cursor for forward pagination.",
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "description": "checkout configurations listed",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "data": {
                      "type": "array",
                      "items": {
                        "type": "object",
                        "properties": {
                          "id": {
                            "type": "string",
                            "description": "The unique identifier of the checkout configuration."
                          },
                          "created_at": {
                            "type": "integer",
                            "description": "Unix timestamp when the checkout configuration was created."
                          },
                          "updated_at": {
                            "type": "integer",
                            "description": "Unix timestamp when the checkout configuration was last updated."
                          },
                          "company_id": {
                            "type": "string",
                            "description": "The ID of the company that owns this checkout configuration."
                          },
                          "mode": {
                            "type": "string",
                            "enum": [
                              "payment",
                              "setup"
                            ],
                            "description": "The checkout mode."
                          },
                          "currency": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "The currency for this checkout configuration."
                          },
                          "plan": {
                            "type": [
                              "object",
                              "null"
                            ],
                            "description": "The plan associated with this checkout configuration."
                          },
                          "affiliate_code": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "The affiliate code applied at checkout."
                          },
                          "metadata": {
                            "type": [
                              "object",
                              "null"
                            ],
                            "description": "Arbitrary key-value metadata. Only returned when caller has checkout_configuration:basic:read scope."
                          },
                          "redirect_url": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "The URL to redirect after checkout."
                          },
                          "purchase_url": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "The URL for the checkout page."
                          },
                          "three_ds_level": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "The 3D Secure enforcement level."
                          },
                          "payment_method_configuration": {
                            "type": [
                              "object",
                              "null"
                            ],
                            "description": "Payment method configuration."
                          }
                        },
                        "required": [
                          "id",
                          "created_at",
                          "updated_at",
                          "company_id",
                          "mode"
                        ]
                      }
                    },
                    "page_info": {
                      "type": "object",
                      "properties": {
                        "end_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "start_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        }
                      },
                      "required": [
                        "end_cursor",
                        "start_cursor",
                        "has_next_page",
                        "has_previous_page"
                      ]
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ]
                }
              }
            }
          },
          "400": {
            "description": "company_id missing",
            "$ref": "#/components/responses/InvalidParameters"
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          }
        }
      },
      "post": {
        "summary": "Create a checkout configuration",
        "tags": [
          "Checkout Configurations"
        ],
        "operationId": "create",
        "security": [
          {
            "bearerAuth": []
          }
        ],
        "description": "Creates a checkout configuration for a plan. Mode defaults to 'payment'.",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          }
        ],
        "responses": {
          "200": {
            "description": "checkout configuration created",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "id": {
                      "type": "string",
                      "description": "The unique identifier of the checkout configuration."
                    },
                    "created_at": {
                      "type": "integer",
                      "description": "Unix timestamp when the checkout configuration was created."
                    },
                    "updated_at": {
                      "type": "integer",
                      "description": "Unix timestamp when the checkout configuration was last updated."
                    },
                    "company_id": {
                      "type": "string",
                      "description": "The ID of the company that owns this checkout configuration."
                    },
                    "mode": {
                      "type": "string",
                      "enum": [
                        "payment",
                        "setup"
                      ],
                      "description": "The checkout mode."
                    },
                    "currency": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "description": "The currency for this checkout configuration."
                    },
                    "plan": {
                      "type": [
                        "object",
                        "null"
                      ],
                      "description": "The plan associated with this checkout configuration."
                    },
                    "affiliate_code": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "description": "The affiliate code applied at checkout."
                    },
                    "metadata": {
                      "type": [
                        "object",
                        "null"
                      ],
                      "description": "Arbitrary key-value metadata. Only returned when caller has checkout_configuration:basic:read scope."
                    },
                    "redirect_url": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "description": "The URL to redirect after checkout."
                    },
                    "purchase_url": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "description": "The URL for the checkout page."
                    },
                    "three_ds_level": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "description": "The 3D Secure enforcement level."
                    },
                    "payment_method_configuration": {
                      "type": [
                        "object",
                        "null"
                      ],
                      "description": "Payment method configuration."
                    }
                  },
                  "required": [
                    "id",
                    "created_at",
                    "updated_at",
                    "company_id",
                    "mode"
                  ]
                }
              }
            }
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          }
        },
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "properties": {
                  "company_id": {
                    "type": "string",
                    "description": "The ID of the company."
                  },
                  "plan_id": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "The ID of an existing plan to attach."
                  },
                  "plan": {
                    "type": [
                      "object",
                      "null"
                    ],
                    "description": "Plan attributes to create a new plan inline for this checkout configuration. Mutually exclusive with plan_id.",
                    "properties": {
                      "company_id": {
                        "type": [
                          "string",
                          "null"
                        ],
                        "description": "The company the plan should be created for. Defaults to the company resolved from the request."
                      },
                      "product_id": {
                        "type": [
                          "string",
                          "null"
                        ],
                        "description": "The ID of an existing product (access pass) to attach the plan to."
                      },
                      "currency": {
                        "type": [
                          "string",
                          "null"
                        ],
                        "description": "The three-letter ISO currency code for the plan's pricing."
                      },
                      "initial_price": {
                        "type": [
                          "number",
                          "null"
                        ],
                        "description": "The amount charged on the first purchase, in the plan's currency."
                      },
                      "renewal_price": {
                        "type": [
                          "number",
                          "null"
                        ],
                        "description": "The amount charged each billing period for recurring plans, in the plan's currency."
                      },
                      "plan_type": {
                        "type": [
                          "string",
                          "null"
                        ],
                        "description": "The billing model for the plan, e.g. 'one_time' or 'renewal'."
                      },
                      "billing_period": {
                        "type": [
                          "integer",
                          "null"
                        ],
                        "description": "The number of days between recurring charges."
                      },
                      "expiration_days": {
                        "type": [
                          "integer",
                          "null"
                        ],
                        "description": "The number of days until the membership expires."
                      },
                      "trial_period_days": {
                        "type": [
                          "integer",
                          "null"
                        ],
                        "description": "The number of free trial days before the first charge."
                      },
                      "release_method": {
                        "type": [
                          "string",
                          "null"
                        ],
                        "description": "How the plan is sold, e.g. 'buy_now'."
                      },
                      "title": {
                        "type": [
                          "string",
                          "null"
                        ],
                        "description": "The display name of the plan shown to customers."
                      },
                      "description": {
                        "type": [
                          "string",
                          "null"
                        ],
                        "description": "A text description of the plan displayed to customers."
                      },
                      "visibility": {
                        "type": [
                          "string",
                          "null"
                        ],
                        "description": "Whether the plan is visible to customers or hidden."
                      },
                      "stock": {
                        "type": [
                          "integer",
                          "null"
                        ],
                        "description": "The maximum number of units available for purchase."
                      },
                      "unlimited_stock": {
                        "type": [
                          "boolean",
                          "null"
                        ],
                        "description": "Whether the plan has unlimited stock."
                      },
                      "override_tax_type": {
                        "type": [
                          "string",
                          "null"
                        ],
                        "description": "Override the default tax classification for this plan."
                      },
                      "metadata": {
                        "type": [
                          "object",
                          "null"
                        ],
                        "description": "Custom key-value metadata to store on the plan."
                      },
                      "force_create_new_plan": {
                        "type": [
                          "boolean",
                          "null"
                        ],
                        "description": "Force creating a new plan even if one with the same attributes already exists."
                      },
                      "payment_method_configuration": {
                        "type": [
                          "object",
                          "null"
                        ],
                        "properties": {
                          "enabled": {
                            "type": "array",
                            "items": {
                              "type": "string"
                            }
                          },
                          "disabled": {
                            "type": "array",
                            "items": {
                              "type": "string"
                            }
                          },
                          "include_platform_defaults": {
                            "type": "boolean"
                          }
                        }
                      }
                    }
                  },
                  "mode": {
                    "type": "string",
                    "enum": [
                      "payment",
                      "setup"
                    ],
                    "description": "Checkout mode. Defaults to 'payment'."
                  },
                  "currency": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "The currency code."
                  },
                  "affiliate_code": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "An affiliate code to apply."
                  },
                  "metadata": {
                    "type": [
                      "object",
                      "null"
                    ],
                    "description": "Arbitrary key-value metadata."
                  },
                  "redirect_url": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "URL to redirect after checkout."
                  },
                  "three_ds_level": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "3D Secure enforcement level."
                  },
                  "payment_method_configuration": {
                    "type": [
                      "object",
                      "null"
                    ],
                    "properties": {
                      "enabled": {
                        "type": "array",
                        "items": {
                          "type": "string"
                        }
                      },
                      "disabled": {
                        "type": "array",
                        "items": {
                          "type": "string"
                        }
                      },
                      "include_platform_defaults": {
                        "type": "boolean"
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    },
    "/checkout_configurations/{id}": {
      "get": {
        "summary": "Retrieve a checkout configuration",
        "tags": [
          "Checkout Configurations"
        ],
        "operationId": "get",
        "description": "Retrieves a checkout configuration by ID. No authentication required.",
        "responses": {
          "200": {
            "description": "checkout configuration found (unauthenticated)",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "id": {
                      "type": "string",
                      "description": "The unique identifier of the checkout configuration."
                    },
                    "created_at": {
                      "type": "integer",
                      "description": "Unix timestamp when the checkout configuration was created."
                    },
                    "updated_at": {
                      "type": "integer",
                      "description": "Unix timestamp when the checkout configuration was last updated."
                    },
                    "company_id": {
                      "type": "string",
                      "description": "The ID of the company that owns this checkout configuration."
                    },
                    "mode": {
                      "type": "string",
                      "enum": [
                        "payment",
                        "setup"
                      ],
                      "description": "The checkout mode."
                    },
                    "currency": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "description": "The currency for this checkout configuration."
                    },
                    "plan": {
                      "type": [
                        "object",
                        "null"
                      ],
                      "description": "The plan associated with this checkout configuration."
                    },
                    "affiliate_code": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "description": "The affiliate code applied at checkout."
                    },
                    "metadata": {
                      "type": [
                        "object",
                        "null"
                      ],
                      "description": "Arbitrary key-value metadata. Only returned when caller has checkout_configuration:basic:read scope."
                    },
                    "redirect_url": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "description": "The URL to redirect after checkout."
                    },
                    "purchase_url": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "description": "The URL for the checkout page."
                    },
                    "three_ds_level": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "description": "The 3D Secure enforcement level."
                    },
                    "payment_method_configuration": {
                      "type": [
                        "object",
                        "null"
                      ],
                      "description": "Payment method configuration."
                    }
                  },
                  "required": [
                    "id",
                    "created_at",
                    "updated_at",
                    "company_id",
                    "mode"
                  ]
                }
              }
            }
          },
          "404": {
            "description": "not found",
            "$ref": "#/components/responses/NotFound"
          }
        },
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "id",
            "in": "path",
            "required": true,
            "description": "The ID of the checkout configuration.",
            "schema": {
              "type": "string"
            }
          }
        ]
      },
      "delete": {
        "summary": "Delete a checkout configuration",
        "tags": [
          "Checkout Configurations"
        ],
        "operationId": "delete",
        "security": [
          {
            "bearerAuth": []
          }
        ],
        "description": "Deletes a checkout configuration.",
        "responses": {
          "200": {
            "description": "checkout configuration deleted"
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          },
          "404": {
            "description": "not found",
            "$ref": "#/components/responses/NotFound"
          }
        },
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "id",
            "in": "path",
            "required": true,
            "description": "The ID of the checkout configuration.",
            "schema": {
              "type": "string"
            }
          }
        ]
      }
    },
    "/deposits": {
      "get": {
        "summary": "Retrieve Deposits",
        "tags": [
          "Deposits"
        ],
        "security": [
          {
            "bearerAuth": [
              "payout:account:read"
            ]
          }
        ],
        "description": "Returns deposit transactions for a business account. Bank deposit transactions are nested under the bank field.",
        "operationId": "list",
        "parameters": [
          {
            "name": "account_id",
            "in": "query",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "Business account ID (biz_*)."
          }
        ],
        "responses": {
          "200": {
            "description": "deposits retrieved",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "object": {
                      "type": "string",
                      "enum": [
                        "deposits"
                      ]
                    },
                    "account_id": {
                      "type": "string",
                      "description": "Account ID that owns these deposit transactions."
                    },
                    "bank": {
                      "type": "array",
                      "description": "Bank deposit transactions for this account.",
                      "items": {
                        "type": "object",
                        "properties": {
                          "id": {
                            "type": "string",
                            "description": "Bank deposit transaction ID."
                          },
                          "source_amount": {
                            "type": "string",
                            "description": "Amount sent by the depositor."
                          },
                          "source_currency": {
                            "type": "string",
                            "description": "Currency sent by the depositor."
                          },
                          "destination_amount": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "Amount credited to the account balance."
                          },
                          "destination_currency": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "Currency credited to the account balance."
                          },
                          "status": {
                            "type": "string",
                            "description": "Current bank deposit status."
                          },
                          "created_at": {
                            "type": "string",
                            "format": "date-time",
                            "description": "When the bank deposit transaction was created."
                          }
                        },
                        "required": [
                          "id",
                          "source_amount",
                          "source_currency",
                          "destination_amount",
                          "destination_currency",
                          "status",
                          "created_at"
                        ]
                      }
                    }
                  },
                  "required": [
                    "object",
                    "account_id",
                    "bank"
                  ]
                }
              }
            }
          },
          "400": {
            "description": "account ID is invalid",
            "$ref": "#/components/responses/InvalidParameters"
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          },
          "403": {
            "description": "credential lacks the payout account read scope",
            "$ref": "#/components/responses/Forbidden"
          }
        }
      },
      "post": {
        "summary": "Create Deposit",
        "tags": [
          "Deposits"
        ],
        "description": "Resolves a deposit destination and returns the on-chain addresses that can fund it. No authentication is required; any business can be resolved by its account ID.",
        "operationId": "create",
        "parameters": [],
        "responses": {
          "201": {
            "description": "deposit created",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "object": {
                      "type": "string",
                      "enum": [
                        "deposit"
                      ]
                    },
                    "amount": {
                      "type": "string",
                      "description": "Requested deposit amount."
                    },
                    "account_id": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "description": "Account ID of the destination owner. Null for raw wallet address destinations."
                    },
                    "hosted_url": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "description": "URL of the hosted deposit page. Only present for business destinations."
                    },
                    "methods": {
                      "type": "object",
                      "description": "Available deposit methods for destination.",
                      "properties": {
                        "bank": {
                          "type": [
                            "object",
                            "null"
                          ],
                          "description": "Bank deposit details. Only present when bank deposits are active for the destination account.",
                          "properties": {
                            "currencies": {
                              "type": "array",
                              "description": "Bank transfer currencies available for this deposit.",
                              "items": {
                                "type": "object",
                                "properties": {
                                  "currency": {
                                    "type": "string",
                                    "description": "Currency supported by these bank instructions."
                                  },
                                  "account_number": {
                                    "type": [
                                      "string",
                                      "null"
                                    ],
                                    "description": "Bank account number for deposits in this currency."
                                  },
                                  "routing_number": {
                                    "type": [
                                      "string",
                                      "null"
                                    ],
                                    "description": "Bank routing number for deposits in this currency."
                                  },
                                  "deposit_bank_name": {
                                    "type": [
                                      "string",
                                      "null"
                                    ],
                                    "description": "Receiving bank name."
                                  },
                                  "deposit_beneficiary_name": {
                                    "type": [
                                      "string",
                                      "null"
                                    ],
                                    "description": "Beneficiary name to use for transfer."
                                  },
                                  "deposit_reference": {
                                    "type": [
                                      "string",
                                      "null"
                                    ],
                                    "description": "Reference to include with bank transfer."
                                  },
                                  "rails": {
                                    "type": "array",
                                    "items": {
                                      "type": "string"
                                    },
                                    "description": "Active deposit rails for this currency, such as `ach`, `wire`, or `sepa`."
                                  }
                                },
                                "required": [
                                  "currency",
                                  "account_number",
                                  "routing_number",
                                  "deposit_bank_name",
                                  "deposit_beneficiary_name",
                                  "deposit_reference",
                                  "rails"
                                ]
                              }
                            }
                          },
                          "required": [
                            "currencies"
                          ]
                        },
                        "crypto": {
                          "type": "array",
                          "description": "Crypto networks available for this deposit, each with its on-chain deposit address and the tokens accepted on that network.",
                          "items": {
                            "type": "object",
                            "properties": {
                              "name": {
                                "type": "string",
                                "description": "Network display name, such as `Ethereum` or `Solana`."
                              },
                              "deposit_address": {
                                "type": [
                                  "string",
                                  "null"
                                ],
                                "description": "Address to send funds to on this network. Null when the provider has not issued one yet."
                              },
                              "icon_url": {
                                "type": [
                                  "string",
                                  "null"
                                ],
                                "description": "Network icon URL."
                              },
                              "supported_currencies": {
                                "type": "array",
                                "description": "Tokens accepted for deposit on this network.",
                                "items": {
                                  "type": "object",
                                  "properties": {
                                    "name": {
                                      "type": "string",
                                      "description": "Token symbol, such as `USDC`."
                                    },
                                    "icon_url": {
                                      "type": [
                                        "string",
                                        "null"
                                      ],
                                      "description": "Token icon URL. Null when no icon is available."
                                    }
                                  },
                                  "required": [
                                    "name",
                                    "icon_url"
                                  ]
                                }
                              }
                            },
                            "required": [
                              "name",
                              "deposit_address",
                              "icon_url",
                              "supported_currencies"
                            ]
                          }
                        }
                      },
                      "required": [
                        "bank",
                        "crypto"
                      ]
                    },
                    "metadata": {
                      "type": "object",
                      "additionalProperties": true,
                      "description": "Metadata from the request."
                    }
                  },
                  "required": [
                    "object",
                    "account_id",
                    "hosted_url",
                    "methods",
                    "metadata"
                  ]
                }
              }
            }
          },
          "400": {
            "description": "request is invalid",
            "$ref": "#/components/responses/InvalidParameters"
          }
        },
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "properties": {
                  "amount": {
                    "type": "number",
                    "description": "Amount to prefill on hosted deposit page."
                  },
                  "destination": {
                    "oneOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "object",
                        "properties": {
                          "account_id": {
                            "type": "string",
                            "description": "Destination account ID."
                          },
                          "address": {
                            "type": "string",
                            "description": "Destination wallet address."
                          },
                          "network": {
                            "type": "string",
                            "description": "Destination wallet network."
                          }
                        }
                      }
                    ],
                    "description": "Destination account ID or wallet address. Object form is supported for compatibility."
                  },
                  "network": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "Destination network override."
                  },
                  "metadata": {
                    "type": "object",
                    "additionalProperties": true,
                    "description": "Metadata to include with the deposit response."
                  }
                },
                "required": [
                  "destination"
                ]
              }
            }
          },
          "required": true
        }
      }
    },
    "/financial-activity": {
      "get": {
        "summary": "List Financial Activity",
        "tags": [
          "Ledgers"
        ],
        "security": [
          {
            "bearerAuth": [
              "company:balance:read"
            ]
          }
        ],
        "description": "Returns a paginated activity feed for one account or user, derived from ledger lines with typed resource and source objects for presentation. Pass exactly one of `account_id` (a `biz_` identifier) or `user_id` (a `user_` identifier). Filter by line type, currency, posted timestamp, or settlement date to reconcile a specific window.",
        "operationId": "list",
        "parameters": [
          {
            "name": "account_id",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "The owning account ID (a biz_ identifier). Provide this or user_id."
          },
          {
            "name": "user_id",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "The owning user ID (a user_ identifier). Provide this or account_id."
          },
          {
            "name": "line_types",
            "in": "query",
            "required": false,
            "schema": {
              "type": "array",
              "items": {
                "type": "string"
              }
            },
            "style": "form",
            "explode": true,
            "description": "Optional ledger line categories to include. Some categories (for example `onchain_deposit`, which covers inbound crypto deposits such as MoonPay onramps) are only returned when explicitly requested here."
          },
          {
            "name": "currency",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Optional currency code filter, for example `usd`."
          },
          {
            "name": "posted_after",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string",
              "format": "date-time"
            },
            "description": "Only include rows posted after this ISO 8601 timestamp."
          },
          {
            "name": "posted_before",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string",
              "format": "date-time"
            },
            "description": "Only include rows posted before this ISO 8601 timestamp."
          },
          {
            "name": "available_after",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string",
              "format": "date"
            },
            "description": "Only include rows whose funds became withdrawable on or after this `YYYY-MM-DD` settlement date (UTC), distinct from posted_at. Requires currency."
          },
          {
            "name": "available_before",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string",
              "format": "date"
            },
            "description": "Only include rows whose funds became withdrawable on or before this `YYYY-MM-DD` settlement date (UTC). Set equal to available_after for a single day. Requires currency."
          },
          {
            "name": "limit",
            "in": "query",
            "required": false,
            "schema": {
              "type": "integer",
              "default": 20,
              "maximum": 100
            },
            "description": "Maximum number of rows to return."
          },
          {
            "name": "cursor",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Cursor returned by the previous page."
          }
        ],
        "responses": {
          "200": {
            "description": "activities listed for a user",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "data": {
                      "type": "array",
                      "items": {
                        "$ref": "#/components/schemas/LedgerActivity"
                      }
                    },
                    "page_info": {
                      "type": "object",
                      "properties": {
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "end_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        },
                        "start_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "has_next_page",
                        "end_cursor",
                        "has_previous_page",
                        "start_cursor"
                      ]
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ]
                }
              }
            }
          },
          "400": {
            "description": "neither account_id nor user_id provided",
            "$ref": "#/components/responses/InvalidParameters"
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          },
          "403": {
            "description": "credential lacks the required balance-read scope",
            "$ref": "#/components/responses/Forbidden"
          },
          "404": {
            "description": "no ledger for that owner is visible to the credential",
            "$ref": "#/components/responses/NotFound"
          }
        }
      }
    },
    "/payouts": {
      "get": {
        "summary": "List Payouts",
        "tags": [
          "Payouts"
        ],
        "security": [
          {
            "bearerAuth": [
              "payout:withdrawal:read"
            ]
          }
        ],
        "description": "Lists payouts (withdrawal requests) for an account or user, most recent first. Pass exactly one of account_id (a biz_ identifier) or user_id (a user_ identifier). The saved payout method on each payout additionally requires the payout:destination:read scope and is null without it.",
        "operationId": "list",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "account_id",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "The owning account ID (a biz_ identifier). Provide this or user_id."
          },
          {
            "name": "user_id",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "The owning user ID (a user_ identifier). Provide this or account_id."
          },
          {
            "name": "currency",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Optional currency code filter, for example `usd`."
          },
          {
            "name": "first",
            "in": "query",
            "required": false,
            "schema": {
              "type": "integer",
              "default": 20,
              "maximum": 100
            },
            "description": "Number of payouts to return from the start of the window."
          },
          {
            "name": "after",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Cursor to fetch the page after (from page_info.end_cursor)."
          },
          {
            "name": "last",
            "in": "query",
            "required": false,
            "schema": {
              "type": "integer",
              "maximum": 100
            },
            "description": "Number of payouts to return from the end of the window."
          },
          {
            "name": "before",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Cursor to fetch the page before (from page_info.start_cursor)."
          }
        ],
        "responses": {
          "200": {
            "description": "payout method nulled without the destination-read scope",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "data": {
                      "type": "array",
                      "items": {
                        "type": "object",
                        "properties": {
                          "object": {
                            "type": "string",
                            "enum": [
                              "payout"
                            ]
                          },
                          "id": {
                            "type": "string",
                            "description": "Payout ID."
                          },
                          "status": {
                            "type": "string",
                            "enum": [
                              "requested",
                              "awaiting_payment",
                              "in_transit",
                              "completed",
                              "failed",
                              "canceled",
                              "denied"
                            ],
                            "description": "Current payout status."
                          },
                          "amount": {
                            "type": "number",
                            "format": "float",
                            "description": "The payout amount in whole currency units."
                          },
                          "currency": {
                            "type": "string",
                            "description": "Payout currency."
                          },
                          "fee_amount": {
                            "type": "number",
                            "format": "float",
                            "description": "The fee charged for the payout, in the payout currency."
                          },
                          "speed": {
                            "type": "string",
                            "enum": [
                              "standard",
                              "instant"
                            ],
                            "description": "Payout delivery speed."
                          },
                          "created_at": {
                            "type": "string",
                            "format": "date-time",
                            "description": "When the payout was created."
                          },
                          "estimated_arrival": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "format": "date-time",
                            "description": "Estimated time the funds become available in the destination account."
                          },
                          "payer_name": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "Name of the entity processing the payout."
                          },
                          "payout_token": {
                            "type": [
                              "object",
                              "null"
                            ],
                            "description": "The saved payout method used. Requires payout:destination:read; null without it.",
                            "properties": {
                              "nickname": {
                                "type": [
                                  "string",
                                  "null"
                                ],
                                "description": "Saved payout method nickname."
                              },
                              "payout_destination": {
                                "type": [
                                  "object",
                                  "null"
                                ],
                                "description": "Payout destination display details.",
                                "properties": {
                                  "payer_name": {
                                    "type": [
                                      "string",
                                      "null"
                                    ],
                                    "description": "Payout destination display name."
                                  },
                                  "icon_url": {
                                    "type": [
                                      "string",
                                      "null"
                                    ],
                                    "description": "Payout destination icon URL."
                                  }
                                },
                                "required": [
                                  "payer_name",
                                  "icon_url"
                                ]
                              }
                            },
                            "required": [
                              "nickname",
                              "payout_destination"
                            ]
                          }
                        },
                        "required": [
                          "object",
                          "id",
                          "status",
                          "amount",
                          "currency",
                          "fee_amount",
                          "speed",
                          "created_at",
                          "estimated_arrival",
                          "payer_name",
                          "payout_token"
                        ]
                      }
                    },
                    "page_info": {
                      "type": "object",
                      "properties": {
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "end_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        },
                        "start_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "has_next_page",
                        "end_cursor",
                        "has_previous_page",
                        "start_cursor"
                      ]
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ]
                }
              }
            }
          },
          "400": {
            "description": "neither account_id nor user_id provided",
            "$ref": "#/components/responses/InvalidParameters"
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          },
          "403": {
            "description": "credential lacks the withdrawal-read scope",
            "$ref": "#/components/responses/Forbidden"
          },
          "404": {
            "description": "no ledger for that owner is visible to the credential",
            "$ref": "#/components/responses/NotFound"
          }
        }
      }
    },
    "/plans/{id}/calculate_tax": {
      "post": {
        "summary": "Calculate Tax",
        "tags": [
          "Plans"
        ],
        "security": [
          {
            "bearerAuth": [
              "plan:basic:read"
            ]
          }
        ],
        "description": "Calculates the tax owed on a plan based on the buyer's location.",
        "operationId": "calculate_tax",
        "parameters": [
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "Plan ID, prefixed `plan_`."
          }
        ],
        "responses": {
          "200": {
            "description": "tax calculated",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "currency": {
                      "type": "string",
                      "description": "The three-letter ISO 4217 currency code of the amounts."
                    },
                    "tax_behavior": {
                      "type": "string",
                      "enum": [
                        "exclusive",
                        "inclusive"
                      ],
                      "description": "Whether tax is added on top of the price (exclusive) or already included in it (inclusive)."
                    },
                    "subtotal": {
                      "type": "integer",
                      "description": "The plan price in the currency's smallest unit, for example cents. For exclusive tax this is the pre-tax amount; for inclusive tax it already contains the tax and equals the total."
                    },
                    "tax_amount": {
                      "type": "integer",
                      "description": "The tax owed, in the currency's smallest unit. For exclusive tax it is added on top of the subtotal; for inclusive tax it is the portion already contained in the subtotal."
                    },
                    "total": {
                      "type": "integer",
                      "description": "The total amount the buyer pays, in the currency's smallest unit."
                    },
                    "status": {
                      "type": "string",
                      "enum": [
                        "calculated",
                        "not_calculated"
                      ],
                      "description": "Whether tax was successfully calculated. Returns not_calculated when tax could not be determined."
                    }
                  },
                  "required": [
                    "currency",
                    "tax_behavior",
                    "subtotal",
                    "tax_amount",
                    "total",
                    "status"
                  ]
                }
              }
            }
          },
          "400": {
            "description": "request is invalid",
            "$ref": "#/components/responses/InvalidParameters"
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          },
          "403": {
            "description": "credential lacks the plan read scope",
            "$ref": "#/components/responses/Forbidden"
          },
          "404": {
            "description": "plan not found",
            "$ref": "#/components/responses/NotFound"
          }
        },
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "properties": {
                  "address": {
                    "type": [
                      "object",
                      "null"
                    ],
                    "description": "The buyer's billing address. Provide this or ip_address.",
                    "properties": {
                      "country": {
                        "type": "string",
                        "description": "The two-letter ISO 3166-1 country code, for example `US`, `DE`, or `GB`."
                      },
                      "state": {
                        "type": [
                          "string",
                          "null"
                        ],
                        "description": "The state, province, or region code, for example `CA`."
                      },
                      "postal_code": {
                        "type": [
                          "string",
                          "null"
                        ],
                        "description": "The postal or ZIP code."
                      },
                      "city": {
                        "type": [
                          "string",
                          "null"
                        ],
                        "description": "The city name."
                      },
                      "line1": {
                        "type": [
                          "string",
                          "null"
                        ],
                        "description": "The first line of the street address."
                      },
                      "line2": {
                        "type": [
                          "string",
                          "null"
                        ],
                        "description": "The second line of the street address."
                      }
                    },
                    "required": [
                      "country"
                    ]
                  },
                  "ip_address": {
                    "type": "string",
                    "description": "The buyer's IP address, used to resolve their location when no address is provided."
                  },
                  "tax_ids": {
                    "type": [
                      "array",
                      "null"
                    ],
                    "maxItems": 1,
                    "description": "The buyer's tax IDs, such as a VAT number, used to apply B2B reverse-charge exemptions.",
                    "items": {
                      "type": "object",
                      "properties": {
                        "type": {
                          "type": "string",
                          "enum": [
                            "ad_nrt",
                            "ao_tin",
                            "ar_cuit",
                            "al_tin",
                            "am_tin",
                            "aw_tin",
                            "au_abn",
                            "au_arn",
                            "eu_vat",
                            "az_tin",
                            "bs_tin",
                            "bh_vat",
                            "bd_bin",
                            "bb_tin",
                            "by_tin",
                            "bj_ifu",
                            "bo_tin",
                            "ba_tin",
                            "br_cnpj",
                            "br_cpf",
                            "bg_uic",
                            "bf_ifu",
                            "kh_tin",
                            "cm_niu",
                            "ca_bn",
                            "ca_gst_hst",
                            "ca_pst_bc",
                            "ca_pst_mb",
                            "ca_pst_sk",
                            "ca_qst",
                            "cv_nif",
                            "cl_tin",
                            "cn_tin",
                            "co_nit",
                            "cd_nif",
                            "cr_tin",
                            "hr_oib",
                            "do_rcn",
                            "ec_ruc",
                            "eg_tin",
                            "sv_nit",
                            "et_tin",
                            "eu_oss_vat",
                            "ge_vat",
                            "gh_tin",
                            "de_stn",
                            "gb_vat",
                            "gn_nif",
                            "hk_br",
                            "hu_tin",
                            "is_vat",
                            "in_gst",
                            "id_npwp",
                            "il_vat",
                            "jp_cn",
                            "jp_rn",
                            "jp_trn",
                            "kz_bin",
                            "ke_pin",
                            "kg_tin",
                            "la_tin",
                            "li_uid",
                            "li_vat",
                            "my_frp",
                            "my_itn",
                            "my_sst",
                            "mr_nif",
                            "mx_rfc",
                            "md_vat",
                            "me_pib",
                            "ma_vat",
                            "np_pan",
                            "nz_gst",
                            "ng_tin",
                            "mk_vat",
                            "no_vat",
                            "no_voec",
                            "om_vat",
                            "pe_ruc",
                            "ph_tin",
                            "ro_tin",
                            "ru_inn",
                            "ru_kpp",
                            "sa_vat",
                            "sn_ninea",
                            "rs_pib",
                            "sg_gst",
                            "sg_uen",
                            "si_tin",
                            "za_vat",
                            "kr_brn",
                            "es_cif",
                            "ch_uid",
                            "ch_vat",
                            "tw_vat",
                            "tj_tin",
                            "tz_vat",
                            "th_vat",
                            "tr_tin",
                            "ug_tin",
                            "ua_vat",
                            "ae_trn",
                            "us_ein",
                            "uy_ruc",
                            "uz_tin",
                            "uz_vat",
                            "ve_rif",
                            "vn_tin",
                            "zm_tin",
                            "zw_tin",
                            "sr_fin"
                          ],
                          "description": "Tax ID type, for example `eu_vat`."
                        },
                        "value": {
                          "type": "string",
                          "description": "Tax ID number, for example `DE123456789`."
                        }
                      }
                    }
                  }
                }
              }
            }
          },
          "required": true
        }
      }
    },
    "/plans": {
      "get": {
        "summary": "List Plans",
        "tags": [
          "Plans"
        ],
        "security": [
          {
            "bearerAuth": [
              "plan:basic:read"
            ]
          }
        ],
        "description": "Returns a paginated list of plans belonging to an account, with optional filtering by visibility, type, release method, and product.",
        "operationId": "list",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "account_id",
            "in": "query",
            "schema": {
              "type": "string"
            },
            "required": true,
            "description": "The unique identifier of the account to list plans for."
          },
          {
            "name": "direction",
            "in": "query",
            "schema": {
              "type": "string",
              "enum": [
                "asc",
                "desc"
              ]
            },
            "required": false,
            "description": "The sort direction for results. Defaults to descending."
          },
          {
            "name": "order",
            "in": "query",
            "schema": {
              "type": "string",
              "enum": [
                "id",
                "active_members_count",
                "created_at",
                "internal_notes",
                "expiration_days"
              ]
            },
            "required": false,
            "description": "The field to sort results by. Defaults to created_at."
          },
          {
            "name": "release_methods",
            "in": "query",
            "schema": {
              "type": "array",
              "items": {
                "type": "string"
              }
            },
            "required": false,
            "description": "Filter to only plans matching these release methods."
          },
          {
            "name": "visibilities",
            "in": "query",
            "schema": {
              "type": "array",
              "items": {
                "type": "string"
              }
            },
            "required": false,
            "description": "Filter to only plans matching these visibility states."
          },
          {
            "name": "plan_types",
            "in": "query",
            "schema": {
              "type": "array",
              "items": {
                "type": "string"
              }
            },
            "required": false,
            "description": "Filter to only plans matching these billing types."
          },
          {
            "name": "product_ids",
            "in": "query",
            "schema": {
              "type": "array",
              "items": {
                "type": "string"
              }
            },
            "required": false,
            "description": "Filter to only plans belonging to these product identifiers."
          },
          {
            "name": "created_before",
            "in": "query",
            "schema": {
              "type": "string"
            },
            "required": false,
            "description": "Only return plans created before this timestamp."
          },
          {
            "name": "created_after",
            "in": "query",
            "schema": {
              "type": "string"
            },
            "required": false,
            "description": "Only return plans created after this timestamp."
          },
          {
            "name": "first",
            "in": "query",
            "schema": {
              "type": "integer"
            },
            "required": false,
            "description": "The number of plans to return (default and max 100)."
          },
          {
            "name": "after",
            "in": "query",
            "schema": {
              "type": "string"
            },
            "required": false,
            "description": "A cursor; returns plans after this position."
          },
          {
            "name": "last",
            "in": "query",
            "schema": {
              "type": "integer"
            },
            "required": false,
            "description": "The number of plans to return from the end of the range."
          },
          {
            "name": "before",
            "in": "query",
            "schema": {
              "type": "string"
            },
            "required": false,
            "description": "A cursor; returns plans before this position."
          }
        ],
        "responses": {
          "200": {
            "description": "plans listed",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "data": {
                      "type": "array",
                      "items": {
                        "$ref": "#/components/schemas/PlanListItem"
                      }
                    },
                    "page_info": {
                      "type": "object",
                      "properties": {
                        "end_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "start_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        }
                      },
                      "required": [
                        "end_cursor",
                        "start_cursor",
                        "has_next_page",
                        "has_previous_page"
                      ]
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ]
                }
              }
            }
          },
          "400": {
            "description": "missing account_id",
            "$ref": "#/components/responses/InvalidParameters"
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          }
        }
      },
      "post": {
        "summary": "Create Plan",
        "tags": [
          "Plans"
        ],
        "security": [
          {
            "bearerAuth": [
              "plan:create"
            ]
          }
        ],
        "description": "Create a new pricing plan for a product. The plan defines the billing interval, price, and availability for customers.",
        "operationId": "create",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          }
        ],
        "responses": {
          "200": {
            "description": "plan created",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/Plan"
                }
              }
            }
          },
          "400": {
            "description": "invalid parameters",
            "$ref": "#/components/responses/InvalidParameters"
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          }
        },
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "properties": {
                  "account_id": {
                    "type": "string",
                    "description": "The unique identifier of the account to create this plan for. Defaults to the caller's account."
                  },
                  "product_id": {
                    "type": "string",
                    "description": "The unique identifier of the product to attach this plan to."
                  },
                  "plan_type": {
                    "type": "string",
                    "description": "Plan billing type, such as `one_time` or `renewal`."
                  },
                  "release_method": {
                    "type": "string",
                    "description": "Sales method for this plan, such as `buy_now` or `waitlist`."
                  },
                  "split_pay_required_payments": {
                    "type": [
                      "integer",
                      "null"
                    ],
                    "description": "Installment payments required before the subscription pauses."
                  },
                  "adaptive_pricing_enabled": {
                    "type": [
                      "boolean",
                      "null"
                    ],
                    "description": "Whether this plan accepts local currency payments via adaptive pricing."
                  },
                  "billing_period": {
                    "type": [
                      "integer",
                      "null"
                    ],
                    "description": "Recurring billing interval in days, such as 30 for monthly or 365 for annual."
                  },
                  "checkout_styling": {
                    "type": [
                      "object",
                      "null"
                    ],
                    "description": "Checkout styling overrides for this plan."
                  },
                  "currency": {
                    "type": "string",
                    "description": "The three-letter ISO currency code for the plan's pricing. Defaults to USD."
                  },
                  "custom_fields": {
                    "type": [
                      "array",
                      "null"
                    ],
                    "items": {
                      "type": "object",
                      "properties": {
                        "id": {
                          "type": "string",
                          "description": "The ID of the custom field (if being updated)."
                        },
                        "field_type": {
                          "type": "string",
                          "enum": [
                            "text"
                          ],
                          "description": "The type of the custom field."
                        },
                        "name": {
                          "type": "string",
                          "description": "The name of the custom field."
                        },
                        "order": {
                          "type": "integer",
                          "description": "The order of the field."
                        },
                        "placeholder": {
                          "type": [
                            "string",
                            "null"
                          ],
                          "description": "An example response displayed in the input field."
                        },
                        "required": {
                          "type": "boolean",
                          "description": "Whether or not the field is required."
                        }
                      }
                    },
                    "description": "An array of custom field definitions to collect from customers at checkout. Omitting this field clears existing custom fields."
                  },
                  "description": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "A text description of the plan displayed to customers on the product page."
                  },
                  "expiration_days": {
                    "type": [
                      "integer",
                      "null"
                    ],
                    "description": "Access duration in days before the membership expires."
                  },
                  "image": {
                    "type": [
                      "object",
                      "null"
                    ],
                    "properties": {
                      "id": {
                        "type": "string"
                      },
                      "direct_upload_id": {
                        "type": "string"
                      }
                    },
                    "description": "An image displayed on the product page to represent this plan."
                  },
                  "initial_price": {
                    "type": [
                      "number",
                      "null"
                    ],
                    "description": "Initial amount charged in the plan's currency, e.g. 10.43 for $10.43."
                  },
                  "internal_notes": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "Private notes visible only to the account owner. Not shown to customers."
                  },
                  "legacy_payment_method_controls": {
                    "type": [
                      "boolean",
                      "null"
                    ],
                    "description": "Whether this plan uses legacy payment method controls."
                  },
                  "metadata": {
                    "type": [
                      "object",
                      "null"
                    ],
                    "description": "Custom key-value pairs to store on the plan. Included in webhook payloads for payment and membership events. Max 50 keys, 100 chars per key, 500 chars per string value."
                  },
                  "override_tax_type": {
                    "type": "string",
                    "description": "Override the default tax classification for this specific plan."
                  },
                  "payment_method_configuration": {
                    "type": [
                      "object",
                      "null"
                    ],
                    "description": "Explicit payment method configuration for the plan. When not provided, the account's defaults apply.",
                    "properties": {
                      "enabled": {
                        "type": "array",
                        "items": {
                          "type": "string"
                        }
                      },
                      "disabled": {
                        "type": "array",
                        "items": {
                          "type": "string"
                        }
                      },
                      "include_platform_defaults": {
                        "type": "boolean"
                      }
                    }
                  },
                  "renewal_price": {
                    "type": [
                      "number",
                      "null"
                    ],
                    "description": "The amount charged each billing period for recurring plans, in the plan's currency."
                  },
                  "stock": {
                    "type": [
                      "integer",
                      "null"
                    ],
                    "description": "The maximum number of units available for purchase. Ignored when unlimited_stock is true."
                  },
                  "three_ds_level": {
                    "type": "string",
                    "enum": [
                      "mandate_challenge",
                      "frictionless"
                    ],
                    "description": "3D Secure behavior for this plan. Send `null` to inherit the account default."
                  },
                  "title": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "The display name of the plan shown to customers on the product page."
                  },
                  "trial_period_days": {
                    "type": [
                      "integer",
                      "null"
                    ],
                    "description": "Free trial duration before the first recurring charge."
                  },
                  "unlimited_stock": {
                    "type": [
                      "boolean",
                      "null"
                    ],
                    "description": "Whether the plan has unlimited stock. When true, the stock field is ignored."
                  },
                  "visibility": {
                    "type": "string",
                    "description": "Whether the plan is visible to customers or hidden from public view."
                  }
                }
              }
            }
          },
          "required": true
        }
      }
    },
    "/plans/{id}": {
      "get": {
        "summary": "Retrieve Plan",
        "tags": [
          "Plans"
        ],
        "security": [
          {
            "bearerAuth": []
          }
        ],
        "description": "Retrieves the details of an existing plan.",
        "operationId": "get",
        "responses": {
          "200": {
            "description": "plan retrieved",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/Plan"
                }
              }
            }
          },
          "404": {
            "description": "no plan with that id",
            "$ref": "#/components/responses/NotFound"
          }
        },
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "Plan ID, prefixed `plan_`."
          }
        ]
      },
      "patch": {
        "summary": "Update Plan",
        "tags": [
          "Plans"
        ],
        "security": [
          {
            "bearerAuth": [
              "plan:update"
            ]
          }
        ],
        "description": "Update a plan's pricing, billing interval, visibility, stock, and other settings.",
        "operationId": "update",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "Plan ID, prefixed `plan_`."
          }
        ],
        "responses": {
          "200": {
            "description": "plan updated",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/Plan"
                }
              }
            }
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          }
        },
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "properties": {
                  "offer_cancel_discount": {
                    "type": [
                      "boolean",
                      "null"
                    ],
                    "description": "Whether to offer a retention discount when a customer attempts to cancel."
                  },
                  "strike_through_initial_price": {
                    "type": [
                      "number",
                      "null"
                    ],
                    "description": "A comparison price displayed with a strikethrough for the initial price."
                  },
                  "strike_through_renewal_price": {
                    "type": [
                      "number",
                      "null"
                    ],
                    "description": "A comparison price displayed with a strikethrough for the renewal price."
                  },
                  "adaptive_pricing_enabled": {
                    "type": [
                      "boolean",
                      "null"
                    ],
                    "description": "Whether this plan accepts local currency payments via adaptive pricing."
                  },
                  "billing_period": {
                    "type": [
                      "integer",
                      "null"
                    ],
                    "description": "Recurring billing interval in days, such as 30 for monthly or 365 for annual."
                  },
                  "checkout_styling": {
                    "type": [
                      "object",
                      "null"
                    ],
                    "description": "Checkout styling overrides for this plan."
                  },
                  "currency": {
                    "type": "string",
                    "description": "The three-letter ISO currency code for the plan's pricing. Defaults to USD."
                  },
                  "custom_fields": {
                    "type": [
                      "array",
                      "null"
                    ],
                    "items": {
                      "type": "object",
                      "properties": {
                        "id": {
                          "type": "string",
                          "description": "The ID of the custom field (if being updated)."
                        },
                        "field_type": {
                          "type": "string",
                          "enum": [
                            "text"
                          ],
                          "description": "The type of the custom field."
                        },
                        "name": {
                          "type": "string",
                          "description": "The name of the custom field."
                        },
                        "order": {
                          "type": "integer",
                          "description": "The order of the field."
                        },
                        "placeholder": {
                          "type": [
                            "string",
                            "null"
                          ],
                          "description": "An example response displayed in the input field."
                        },
                        "required": {
                          "type": "boolean",
                          "description": "Whether or not the field is required."
                        }
                      }
                    },
                    "description": "An array of custom field definitions to collect from customers at checkout. Omitting this field clears existing custom fields."
                  },
                  "description": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "A text description of the plan displayed to customers on the product page."
                  },
                  "expiration_days": {
                    "type": [
                      "integer",
                      "null"
                    ],
                    "description": "Access duration in days before the membership expires."
                  },
                  "image": {
                    "type": [
                      "object",
                      "null"
                    ],
                    "properties": {
                      "id": {
                        "type": "string"
                      },
                      "direct_upload_id": {
                        "type": "string"
                      }
                    },
                    "description": "An image displayed on the product page to represent this plan."
                  },
                  "initial_price": {
                    "type": [
                      "number",
                      "null"
                    ],
                    "description": "Initial amount charged in the plan's currency, e.g. 10.43 for $10.43."
                  },
                  "internal_notes": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "Private notes visible only to the account owner. Not shown to customers."
                  },
                  "legacy_payment_method_controls": {
                    "type": [
                      "boolean",
                      "null"
                    ],
                    "description": "Whether this plan uses legacy payment method controls."
                  },
                  "metadata": {
                    "type": [
                      "object",
                      "null"
                    ],
                    "description": "Custom key-value pairs to store on the plan. Included in webhook payloads for payment and membership events. Max 50 keys, 100 chars per key, 500 chars per string value."
                  },
                  "override_tax_type": {
                    "type": "string",
                    "description": "Override the default tax classification for this specific plan."
                  },
                  "payment_method_configuration": {
                    "type": [
                      "object",
                      "null"
                    ],
                    "description": "Explicit payment method configuration for the plan. When not provided, the account's defaults apply.",
                    "properties": {
                      "enabled": {
                        "type": "array",
                        "items": {
                          "type": "string"
                        }
                      },
                      "disabled": {
                        "type": "array",
                        "items": {
                          "type": "string"
                        }
                      },
                      "include_platform_defaults": {
                        "type": "boolean"
                      }
                    }
                  },
                  "renewal_price": {
                    "type": [
                      "number",
                      "null"
                    ],
                    "description": "The amount charged each billing period for recurring plans, in the plan's currency."
                  },
                  "stock": {
                    "type": [
                      "integer",
                      "null"
                    ],
                    "description": "The maximum number of units available for purchase. Ignored when unlimited_stock is true."
                  },
                  "three_ds_level": {
                    "type": "string",
                    "enum": [
                      "mandate_challenge",
                      "frictionless"
                    ],
                    "description": "3D Secure behavior for this plan. Send `null` to inherit the account default."
                  },
                  "title": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "The display name of the plan shown to customers on the product page."
                  },
                  "trial_period_days": {
                    "type": [
                      "integer",
                      "null"
                    ],
                    "description": "Free trial duration before the first recurring charge."
                  },
                  "unlimited_stock": {
                    "type": [
                      "boolean",
                      "null"
                    ],
                    "description": "Whether the plan has unlimited stock. When true, the stock field is ignored."
                  },
                  "visibility": {
                    "type": "string",
                    "description": "Whether the plan is visible to customers or hidden from public view."
                  }
                }
              }
            }
          },
          "required": true
        }
      },
      "delete": {
        "summary": "Delete Plan",
        "tags": [
          "Plans"
        ],
        "security": [
          {
            "bearerAuth": [
              "plan:delete"
            ]
          }
        ],
        "description": "Permanently delete a plan from a product. Existing memberships on this plan will not be affected.",
        "operationId": "delete",
        "responses": {
          "200": {
            "description": "plan deleted",
            "content": {
              "application/json": {
                "schema": {
                  "type": "boolean",
                  "description": "Always true on success."
                }
              }
            }
          },
          "404": {
            "description": "no plan with that id",
            "$ref": "#/components/responses/NotFound"
          }
        },
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "Plan ID, prefixed `plan_`."
          }
        ]
      }
    },
    "/products": {
      "get": {
        "summary": "List Products",
        "tags": [
          "Products"
        ],
        "security": [
          {
            "bearerAuth": [
              "product:basic:read"
            ]
          }
        ],
        "description": "Returns a paginated list of products belonging to a company.",
        "operationId": "list",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "company_id",
            "in": "query",
            "schema": {
              "type": "string"
            },
            "required": true,
            "description": "The unique identifier of the company to list products for."
          },
          {
            "name": "visibilities",
            "in": "query",
            "schema": {
              "type": "array",
              "items": {
                "type": "string"
              }
            },
            "required": false,
            "description": "Filter to only products matching these visibility states."
          },
          {
            "name": "access_pass_types",
            "in": "query",
            "schema": {
              "type": "array",
              "items": {
                "type": "string"
              }
            },
            "required": false,
            "description": "Filter to only products matching these types."
          },
          {
            "name": "direction",
            "in": "query",
            "schema": {
              "type": "string",
              "enum": [
                "asc",
                "desc"
              ]
            },
            "required": false,
            "description": "The sort direction for results. Defaults to descending."
          },
          {
            "name": "order",
            "in": "query",
            "schema": {
              "type": "string"
            },
            "required": false,
            "description": "The field to sort results by. Defaults to created_at."
          },
          {
            "name": "first",
            "in": "query",
            "schema": {
              "type": "integer"
            },
            "required": false,
            "description": "The number of products to return (default and max 100)."
          },
          {
            "name": "after",
            "in": "query",
            "schema": {
              "type": "string"
            },
            "required": false,
            "description": "A cursor; returns products after this position."
          },
          {
            "name": "last",
            "in": "query",
            "schema": {
              "type": "integer"
            },
            "required": false,
            "description": "The number of products to return from the end of the range."
          },
          {
            "name": "before",
            "in": "query",
            "schema": {
              "type": "string"
            },
            "required": false,
            "description": "A cursor; returns products before this position."
          }
        ],
        "responses": {
          "200": {
            "description": "products listed",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "data": {
                      "type": "array",
                      "items": {
                        "$ref": "#/components/schemas/ProductListItem"
                      }
                    },
                    "page_info": {
                      "type": "object",
                      "properties": {
                        "end_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "start_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        }
                      },
                      "required": [
                        "end_cursor",
                        "start_cursor",
                        "has_next_page",
                        "has_previous_page"
                      ]
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ]
                }
              }
            }
          },
          "400": {
            "description": "missing company_id",
            "$ref": "#/components/responses/InvalidParameters"
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          }
        }
      },
      "post": {
        "summary": "Create Product",
        "tags": [
          "Products"
        ],
        "security": [
          {
            "bearerAuth": [
              "product:create"
            ]
          }
        ],
        "description": "Creates a new product for a company.",
        "operationId": "create",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          }
        ],
        "responses": {
          "200": {
            "description": "product created",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/Product"
                }
              }
            }
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          }
        },
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "properties": {
                  "company_id": {
                    "type": "string",
                    "description": "The unique identifier of the company to create this product for."
                  },
                  "title": {
                    "type": "string",
                    "description": "The display name of the product. Maximum 80 characters."
                  },
                  "visibility": {
                    "type": "string",
                    "description": "Whether the product is visible to customers."
                  },
                  "headline": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "A short marketing headline for the product page."
                  },
                  "description": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "A written description displayed on the product page."
                  },
                  "metadata": {
                    "type": [
                      "object",
                      "null"
                    ],
                    "description": "Custom key-value pairs to store on the product."
                  },
                  "route": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "The URL slug for the product's public link."
                  },
                  "global_affiliate_percentage": {
                    "type": [
                      "number",
                      "null"
                    ],
                    "description": "The commission rate affiliates earn."
                  },
                  "global_affiliate_status": {
                    "type": "string",
                    "description": "The enrollment status in the global affiliate program."
                  },
                  "member_affiliate_percentage": {
                    "type": [
                      "number",
                      "null"
                    ],
                    "description": "The commission rate members earn."
                  },
                  "member_affiliate_status": {
                    "type": "string",
                    "description": "The enrollment status in the member affiliate program."
                  },
                  "custom_cta": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "The call-to-action button label."
                  },
                  "custom_cta_url": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "A URL the call-to-action button links to."
                  },
                  "custom_statement_descriptor": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "Custom bank statement descriptor. Must start with WHOP*."
                  },
                  "product_tax_code_id": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "The unique identifier of the tax classification code."
                  },
                  "redirect_purchase_url": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "A URL to redirect the customer to after purchase."
                  },
                  "collect_shipping_address": {
                    "type": [
                      "boolean",
                      "null"
                    ],
                    "description": "Whether to collect a shipping address at checkout."
                  }
                },
                "required": [
                  "title"
                ]
              }
            }
          },
          "required": true
        }
      }
    },
    "/products/{id}": {
      "get": {
        "summary": "Retrieve Product",
        "tags": [
          "Products"
        ],
        "security": [
          {
            "bearerAuth": []
          }
        ],
        "description": "Retrieves the details of an existing product. This endpoint is publicly accessible.",
        "operationId": "get",
        "responses": {
          "200": {
            "description": "product retrieved without authentication",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/Product"
                }
              }
            }
          },
          "404": {
            "description": "product not found",
            "$ref": "#/components/responses/NotFound"
          }
        },
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "The unique identifier of the product."
          }
        ]
      },
      "patch": {
        "summary": "Update Product",
        "tags": [
          "Products"
        ],
        "security": [
          {
            "bearerAuth": [
              "product:update"
            ]
          }
        ],
        "description": "Updates an existing product.",
        "operationId": "update",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "The unique identifier of the product."
          }
        ],
        "responses": {
          "200": {
            "description": "product tax code updated",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/Product"
                }
              }
            }
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          },
          "404": {
            "description": "product not found",
            "$ref": "#/components/responses/NotFound"
          }
        },
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "properties": {
                  "title": {
                    "type": "string",
                    "description": "The display name of the product."
                  },
                  "visibility": {
                    "type": "string",
                    "description": "Whether the product is visible to customers."
                  },
                  "headline": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "A short marketing headline for the product page."
                  },
                  "description": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "A written description displayed on the product page."
                  },
                  "metadata": {
                    "type": [
                      "object",
                      "null"
                    ],
                    "description": "Custom key-value pairs to store on the product."
                  },
                  "product_tax_code_id": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "The unique identifier of the tax classification code."
                  }
                }
              }
            }
          },
          "required": true
        }
      },
      "delete": {
        "summary": "Delete Product",
        "tags": [
          "Products"
        ],
        "security": [
          {
            "bearerAuth": [
              "product:delete"
            ]
          }
        ],
        "description": "Deletes a product. Only products with no memberships, entries, reviews, or invoices can be deleted.",
        "operationId": "delete",
        "responses": {
          "200": {
            "description": "product deleted",
            "content": {
              "application/json": {
                "schema": {
                  "type": "boolean"
                }
              }
            }
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          },
          "404": {
            "description": "product not found",
            "$ref": "#/components/responses/NotFound"
          }
        },
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "The unique identifier of the product."
          }
        ]
      }
    },
    "/social_accounts/{id}": {
      "delete": {
        "summary": "Delete a Social Account",
        "tags": [
          "Social Accounts"
        ],
        "security": [
          {
            "bearerAuth": [
              "social_account:delete"
            ]
          }
        ],
        "description": "Disconnects a social account from an account or user by discarding the link record. The underlying social account record is retained.",
        "operationId": "delete",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "The ID of the social account to disconnect."
          },
          {
            "name": "account_id",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "The Account that the social account is connected to. Provide either this or user_id."
          },
          {
            "name": "user_id",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "The User that the social account is connected to. Provide either this or account_id."
          }
        ],
        "responses": {
          "200": {
            "description": "user social account disconnected",
            "content": {
              "application/json": {
                "schema": {
                  "type": "boolean",
                  "description": "Always true on success."
                }
              }
            }
          },
          "400": {
            "description": "account_id and user_id both provided",
            "$ref": "#/components/responses/InvalidParameters"
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          },
          "403": {
            "description": "caller lacks delete permission",
            "$ref": "#/components/responses/Forbidden"
          },
          "404": {
            "description": "link already disconnected",
            "$ref": "#/components/responses/NotFound"
          }
        }
      }
    },
    "/social_accounts/{id}/posts": {
      "get": {
        "summary": "List Social Account Posts",
        "tags": [
          "Social Accounts"
        ],
        "security": [
          {
            "bearerAuth": [
              "social_account:read"
            ]
          }
        ],
        "description": "Lists the existing posts of a connected social account. Supported for Facebook pages and Instagram accounts. Pass post_id to return only that single post. The owning account is passed as account_id (a biz_ identifier).",
        "operationId": "posts",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "The social account (a sacc_ identifier) whose posts to list."
          },
          {
            "name": "account_id",
            "in": "query",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "The Account (a biz_ identifier) the social account is connected to."
          },
          {
            "name": "post_id",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Return only the single post with this platform id, instead of the full list."
          },
          {
            "name": "first",
            "in": "query",
            "required": false,
            "schema": {
              "type": "integer",
              "default": 25,
              "maximum": 100
            },
            "description": "The number of posts to return."
          },
          {
            "name": "after",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Cursor to fetch the page after (from page_info.end_cursor)."
          }
        ],
        "responses": {
          "200": {
            "description": "single post returned when post_id is given",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "data": {
                      "type": "array",
                      "items": {
                        "$ref": "#/components/schemas/SocialAccountPost"
                      }
                    },
                    "page_info": {
                      "type": "object",
                      "properties": {
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "end_cursor": {
                          "type": "string",
                          "nullable": true
                        }
                      },
                      "required": [
                        "has_next_page",
                        "end_cursor"
                      ]
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ]
                }
              }
            }
          },
          "400": {
            "description": "account_id is missing",
            "$ref": "#/components/responses/InvalidParameters"
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          },
          "404": {
            "description": "social account is not linked to the account",
            "$ref": "#/components/responses/NotFound"
          }
        }
      }
    },
    "/social_accounts": {
      "get": {
        "summary": "List Social Accounts",
        "tags": [
          "Social Accounts"
        ],
        "security": [
          {
            "bearerAuth": [
              "social_account:read"
            ]
          }
        ],
        "description": "Lists the social accounts linked to an account or user. The owner is passed as exactly one of account_id (a biz_ identifier) or user_id (a user_ identifier); an account-scoped API key defaults to its own account when neither is given.",
        "operationId": "list",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "account_id",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "The Account that the social accounts are connected to. Provide either this or user_id."
          },
          {
            "name": "user_id",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "The User that the social accounts are connected to. Provide either this or account_id."
          },
          {
            "name": "platform",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string",
              "enum": [
                "x",
                "instagram",
                "youtube",
                "tiktok",
                "facebook"
              ]
            },
            "description": "Only return social accounts for the platform that is specified."
          },
          {
            "name": "verified",
            "in": "query",
            "required": false,
            "schema": {
              "type": "boolean"
            },
            "description": "Only return social accounts that are verified on the platform."
          },
          {
            "name": "scopes",
            "in": "query",
            "required": false,
            "schema": {
              "type": "array",
              "items": {
                "type": "string",
                "enum": [
                  "advertise"
                ]
              }
            },
            "description": "Only return social accounts that have these scopes."
          },
          {
            "name": "first",
            "in": "query",
            "required": false,
            "schema": {
              "type": "integer",
              "default": 20,
              "maximum": 100
            },
            "description": "The number of social accounts to return."
          },
          {
            "name": "after",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Cursor to fetch the page after (from page_info.end_cursor)."
          },
          {
            "name": "last",
            "in": "query",
            "required": false,
            "schema": {
              "type": "integer",
              "maximum": 100
            },
            "description": "The number of social accounts to return from the end of the range."
          },
          {
            "name": "before",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Cursor to fetch the page before (from page_info.start_cursor)."
          },
          {
            "name": "order",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string",
              "enum": [
                "display_order",
                "created_at"
              ],
              "default": "display_order"
            },
            "description": "The field to sort social accounts by."
          },
          {
            "name": "direction",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string",
              "enum": [
                "asc",
                "desc"
              ],
              "default": "asc"
            },
            "description": "Sort direction."
          }
        ],
        "responses": {
          "200": {
            "description": "filtered to accounts holding the requested scope",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "data": {
                      "type": "array",
                      "items": {
                        "$ref": "#/components/schemas/SocialAccount"
                      }
                    },
                    "page_info": {
                      "type": "object",
                      "properties": {
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "end_cursor": {
                          "type": "string",
                          "nullable": true
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        },
                        "start_cursor": {
                          "type": "string",
                          "nullable": true
                        }
                      },
                      "required": [
                        "has_next_page",
                        "end_cursor",
                        "has_previous_page",
                        "start_cursor"
                      ]
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ]
                }
              }
            }
          },
          "400": {
            "description": "neither account_id nor user_id resolvable",
            "$ref": "#/components/responses/InvalidParameters"
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          }
        }
      },
      "post": {
        "summary": "Connect a Social Account",
        "tags": [
          "Social Accounts"
        ],
        "security": [
          {
            "bearerAuth": [
              "social_account:create"
            ]
          }
        ],
        "description": "Starts an OAuth connection flow for a social account and returns an authorize_url to redirect the user to. Today the only supported platform is meta_business, which grants the advertise scope so the connected Facebook page and Instagram account can run ads. The required permission follows the requested capability: the advertise scope requires ad_campaign:create (so advertiser roles can connect), other scopes require social_account:create. The connection is authorized against the account given by account_id (a biz_ identifier); an account-scoped API key may omit it to default to its own account.",
        "operationId": "create",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          }
        ],
        "responses": {
          "200": {
            "description": "authorize url returned",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "authorize_url": {
                      "type": "string",
                      "description": "The OAuth authorization URL to redirect the user to."
                    }
                  },
                  "required": [
                    "authorize_url"
                  ]
                }
              }
            }
          },
          "400": {
            "description": "unsupported platform",
            "$ref": "#/components/responses/InvalidParameters"
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          }
        },
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "properties": {
                  "redirect_url": {
                    "type": "string",
                    "description": "The Whop URL to redirect the user to after they finish connecting."
                  },
                  "platform": {
                    "type": "string",
                    "enum": [
                      "meta_business"
                    ],
                    "description": "The platform to connect the social account on."
                  },
                  "account_id": {
                    "type": "string",
                    "description": "The Account (biz_ identifier) to connect the social account for. An account-scoped API key may omit this to default to its own account."
                  },
                  "scopes": {
                    "type": "array",
                    "items": {
                      "type": "string",
                      "enum": [
                        "advertise"
                      ]
                    },
                    "description": "Capabilities to grant for the connected social account, for example `advertise`."
                  }
                },
                "required": [
                  "redirect_url",
                  "platform"
                ]
              }
            }
          },
          "required": true
        }
      }
    },
    "/stats": {
      "get": {
        "summary": "List Metrics",
        "tags": [
          "Stats"
        ],
        "security": [
          {
            "bearerAuth": []
          }
        ],
        "description": "Lists every metric you can query, with its unit and the properties you can filter or break it down by.",
        "operationId": "list",
        "responses": {
          "200": {
            "description": "metric catalog",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "data": {
                      "type": "array",
                      "description": "The available metrics.",
                      "items": {
                        "type": "object",
                        "properties": {
                          "key": {
                            "type": "string",
                            "description": "The metric's key. Pass it to GET /stats/{metric} to query its values."
                          },
                          "name": {
                            "type": "string",
                            "description": "Human-readable display name for the metric."
                          },
                          "unit": {
                            "type": "string",
                            "enum": [
                              "count",
                              "currency",
                              "percent"
                            ],
                            "description": "How to read the metric's values: count is an integer, currency is a decimal amount, and percent is a number where 1.6 means 1.6%."
                          },
                          "properties": {
                            "type": "array",
                            "items": {
                              "type": "string"
                            },
                            "description": "The properties you can use with this metric — pass one as a filter (property=value) to narrow the series, or as breakdown_by=property to split it."
                          },
                          "description": {
                            "type": "string",
                            "description": "A short description of what the metric measures."
                          },
                          "windows": {
                            "type": "array",
                            "items": {
                              "type": "string"
                            },
                            "description": "Snapshot metrics only: the trailing windows you can pass as snapshot_window, for example 30d. Absent on live metrics, which use from/to instead."
                          }
                        },
                        "required": [
                          "key",
                          "name",
                          "unit",
                          "properties",
                          "description"
                        ]
                      }
                    }
                  },
                  "required": [
                    "data"
                  ]
                }
              }
            }
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          }
        },
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          }
        ]
      }
    },
    "/stats/{metric}": {
      "get": {
        "summary": "Retrieve Metric",
        "tags": [
          "Stats"
        ],
        "security": [
          {
            "bearerAuth": [
              "stats:read"
            ]
          }
        ],
        "description": "Retrieves a metric as a time series of points for an account over a date range.",
        "operationId": "get",
        "parameters": [
          {
            "name": "metric",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "The metric to retrieve, for example net_revenue. Use GET /stats to see every metric key. The metric sets the unit and the properties you can filter or break down by."
          },
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "account_id",
            "in": "query",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "The account to measure, for example biz_AbC123."
          },
          {
            "name": "from",
            "in": "query",
            "required": true,
            "schema": {
              "type": "string",
              "format": "date"
            },
            "description": "Start of the date range (YYYY-MM-DD)."
          },
          {
            "name": "to",
            "in": "query",
            "required": true,
            "schema": {
              "type": "string",
              "format": "date"
            },
            "description": "End of the date range (YYYY-MM-DD)."
          },
          {
            "name": "interval",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string",
              "enum": [
                "hour",
                "day",
                "week",
                "month"
              ]
            },
            "description": "How wide each point is. Defaults to day. Snapshot metrics are day-only."
          },
          {
            "name": "breakdown_by",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Split the metric out by one of its properties — each point gets a breakdown array. For example breakdown_by=currency returns an entry for usd, an entry for eur, and so on."
          },
          {
            "name": "convert_to",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Display currency for money metrics — every amount is converted into this ISO currency using the exchange rate on each period's date. Defaults to usd. Ignored when you filter or break down by currency (those report the original transaction currency, unconverted)."
          },
          {
            "name": "currency",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Filter to transactions made in this original ISO currency, for example eur — reported in that currency, not converted. Pair with breakdown_by=currency to split a metric by currency. Available on metrics that list currency."
          },
          {
            "name": "time_zone",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "IANA time zone to bucket the series in, for example America/New_York. Defaults to UTC. Not accepted by snapshot metrics, which are UTC only."
          },
          {
            "name": "payment_method",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Filter to a single payment method, for example card or crypto. Available on metrics that list payment_method."
          },
          {
            "name": "card_network",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Filter to a single card brand, for example visa. A refinement of payment_method=card. Available on metrics that list card_network."
          },
          {
            "name": "snapshot_window",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string",
              "enum": [
                "30d"
              ]
            },
            "description": "Trailing window for snapshot metrics. Only accepted by snapshot metrics (each lists its allowed windows in the catalog); defaults to the metric's first supported window. Only 30d today."
          }
        ],
        "responses": {
          "200": {
            "description": "split into one segment per property value",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "data": {
                      "type": "object",
                      "properties": {
                        "points": {
                          "type": "array",
                          "description": "One entry per period, oldest first.",
                          "items": {
                            "type": "object",
                            "properties": {
                              "timestamp": {
                                "type": "integer",
                                "description": "Unix timestamp (seconds) of the period start."
                              },
                              "value": {
                                "type": "number",
                                "nullable": true,
                                "description": "The metric's value for this period, in the metric's unit."
                              },
                              "breakdown": {
                                "type": "array",
                                "description": "Present only when broken down: one entry per property value in this period.",
                                "items": {
                                  "type": "object",
                                  "properties": {
                                    "name": {
                                      "type": "string",
                                      "description": "The property value, for example usd or visa."
                                    },
                                    "value": {
                                      "type": "number",
                                      "nullable": true,
                                      "description": "The metric's value for this entry."
                                    }
                                  },
                                  "required": [
                                    "name",
                                    "value"
                                  ]
                                }
                              }
                            },
                            "required": [
                              "timestamp",
                              "value"
                            ]
                          }
                        }
                      },
                      "required": [
                        "points"
                      ]
                    }
                  },
                  "required": [
                    "data"
                  ]
                }
              }
            }
          },
          "400": {
            "description": "subject tag type mismatch",
            "$ref": "#/components/responses/InvalidParameters"
          },
          "404": {
            "description": "unknown account",
            "$ref": "#/components/responses/NotFound"
          }
        }
      }
    },
    "/swaps/quote": {
      "post": {
        "summary": "Create Swap Quote",
        "tags": [
          "Swaps"
        ],
        "security": [],
        "description": "Returns a stateless swap price preview. No funds move and nothing is persisted.",
        "operationId": "quote",
        "parameters": [],
        "responses": {
          "200": {
            "description": "swap quote created",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "object": {
                      "type": "string",
                      "enum": [
                        "swap_quote"
                      ]
                    },
                    "amount_in": {
                      "type": "string",
                      "description": "Source token amount used for the quote."
                    },
                    "amount_out": {
                      "type": "string",
                      "description": "Estimated destination token amount."
                    },
                    "amount_out_min": {
                      "type": "string",
                      "description": "Minimum destination amount after slippage."
                    },
                    "rate": {
                      "type": "string",
                      "description": "Quoted exchange rate."
                    },
                    "fee_bps": {
                      "type": "integer",
                      "description": "Whop fee in basis points."
                    },
                    "bridge_fee": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "description": "Estimated bridge fee for cross-chain swaps."
                    },
                    "estimated_duration_seconds": {
                      "type": [
                        "integer",
                        "null"
                      ],
                      "description": "Estimated time for the swap to complete."
                    },
                    "requires_token_approval": {
                      "type": [
                        "boolean",
                        "null"
                      ],
                      "description": "Whether the source token needs approval before swapping."
                    },
                    "from_address": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "description": "Source wallet address used for the quote."
                    },
                    "to_address": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "description": "Destination wallet address used for the quote."
                    },
                    "from_token": {
                      "type": "object",
                      "additionalProperties": true,
                      "description": "Resolved source token details."
                    },
                    "to_token": {
                      "type": "object",
                      "additionalProperties": true,
                      "description": "Resolved destination token details."
                    },
                    "metadata": {
                      "type": "object",
                      "additionalProperties": true,
                      "description": "Metadata from the request."
                    }
                  },
                  "required": [
                    "object",
                    "amount_in",
                    "amount_out",
                    "rate",
                    "fee_bps",
                    "from_token",
                    "to_token",
                    "metadata"
                  ]
                }
              }
            }
          },
          "400": {
            "description": "request is invalid",
            "$ref": "#/components/responses/InvalidParameters"
          }
        },
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "properties": {
                  "amount": {
                    "type": "string",
                    "description": "Source token amount."
                  },
                  "from_token": {
                    "type": "string",
                    "description": "Source token contract address or ticker symbol, such as \"USDT\"."
                  },
                  "to_token": {
                    "type": "string",
                    "description": "Destination token contract address or ticker symbol, such as \"XAUT\"."
                  },
                  "from_chain": {
                    "oneOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "integer"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "description": "Source chain name or chain ID. Defaults to the source token's chain when omitted."
                  },
                  "to_chain": {
                    "oneOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "integer"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "description": "Destination chain name or chain ID. Defaults to the destination token's chain when omitted."
                  },
                  "from_address": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "Source wallet address used for the quote."
                  },
                  "to_address": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "Destination wallet address used for the quote."
                  },
                  "slippage_bps": {
                    "type": [
                      "integer",
                      "null"
                    ],
                    "description": "Maximum slippage tolerance in basis points."
                  },
                  "metadata": {
                    "type": "object",
                    "additionalProperties": true,
                    "description": "Metadata to include with the quote response."
                  }
                },
                "required": [
                  "amount",
                  "from_token",
                  "to_token"
                ]
              }
            }
          },
          "required": true
        }
      }
    },
    "/swaps": {
      "post": {
        "summary": "Create Swap",
        "tags": [
          "Swaps"
        ],
        "security": [
          {
            "bearerAuth": [
              "crypto_wallet:swap"
            ]
          }
        ],
        "description": "Executes a swap from the account's wallet. Runs asynchronously; poll GET /swaps/{id} for status.",
        "operationId": "create",
        "parameters": [],
        "responses": {
          "201": {
            "description": "swap created",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "object": {
                      "type": "string",
                      "enum": [
                        "swap"
                      ]
                    },
                    "id": {
                      "type": "string",
                      "description": "Swap ID. Poll `GET /swaps/:id` for status."
                    },
                    "account_id": {
                      "type": "string",
                      "description": "Account ID that owns the wallet used for the swap."
                    },
                    "status": {
                      "type": "string",
                      "description": "Initial swap status."
                    },
                    "to_chain": {
                      "type": "string",
                      "description": "Destination chain for the swap."
                    },
                    "amount_out_expected": {
                      "type": "string",
                      "description": "Expected destination token amount."
                    },
                    "amount_out_min": {
                      "type": "string",
                      "description": "Minimum destination amount after slippage."
                    },
                    "rate": {
                      "type": "string",
                      "description": "Quoted exchange rate used to create the swap."
                    }
                  },
                  "required": [
                    "object",
                    "id",
                    "account_id",
                    "status"
                  ]
                }
              }
            }
          },
          "400": {
            "description": "request is invalid",
            "$ref": "#/components/responses/InvalidParameters"
          },
          "403": {
            "description": "credential lacks the crypto-wallet scope",
            "$ref": "#/components/responses/Forbidden"
          }
        },
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "properties": {
                  "account_id": {
                    "type": "string",
                    "description": "Business or user account ID (biz_* / user_*)."
                  },
                  "amount": {
                    "type": "string",
                    "description": "Source token amount."
                  },
                  "from_token": {
                    "type": "string",
                    "description": "Source token contract address or ticker symbol, such as \"USDT\"."
                  },
                  "to_token": {
                    "type": "string",
                    "description": "Destination token contract address or ticker symbol, such as \"XAUT\"."
                  },
                  "from_chain": {
                    "oneOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "integer"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "description": "Source chain name or chain ID. Defaults to the source token's chain when omitted."
                  },
                  "to_chain": {
                    "oneOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "integer"
                      },
                      {
                        "type": "null"
                      }
                    ],
                    "description": "Destination chain name or chain ID. Defaults to the destination token's chain when omitted."
                  },
                  "slippage_bps": {
                    "type": [
                      "integer",
                      "null"
                    ],
                    "description": "Maximum slippage tolerance in basis points."
                  }
                },
                "required": [
                  "account_id",
                  "amount",
                  "from_token",
                  "to_token"
                ]
              }
            }
          },
          "required": true
        }
      },
      "get": {
        "summary": "List Swaps",
        "tags": [
          "Swaps"
        ],
        "security": [
          {
            "bearerAuth": [
              "crypto_wallet:swap"
            ]
          }
        ],
        "description": "Lists the account's swaps. Currently returns the in-flight or most recent swap, so zero or one rows.",
        "operationId": "status",
        "parameters": [
          {
            "name": "account_id",
            "in": "query",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "Business or user account ID (biz_* / user_*)."
          }
        ],
        "responses": {
          "200": {
            "description": "swaps listed",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "data": {
                      "type": "array",
                      "description": "Swaps returned for this account.",
                      "items": {
                        "type": "object",
                        "properties": {
                          "object": {
                            "type": "string",
                            "enum": [
                              "swap"
                            ]
                          },
                          "id": {
                            "type": "string",
                            "description": "Swap ID."
                          },
                          "account_id": {
                            "type": "string",
                            "description": "Account ID that owns the wallet used for the swap."
                          },
                          "status": {
                            "type": "string",
                            "description": "Current swap status."
                          },
                          "tx_hashes": {
                            "type": "array",
                            "items": {
                              "type": "string"
                            },
                            "description": "On-chain transaction hashes produced by the swap."
                          },
                          "error": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "Latest error returned for a failed swap."
                          }
                        },
                        "required": [
                          "object",
                          "id",
                          "account_id",
                          "status",
                          "tx_hashes"
                        ]
                      }
                    }
                  },
                  "required": [
                    "data"
                  ]
                }
              }
            }
          },
          "403": {
            "description": "credential lacks the crypto-wallet scope",
            "$ref": "#/components/responses/Forbidden"
          }
        }
      }
    },
    "/swaps/{id}": {
      "get": {
        "summary": "Retrieve Swap",
        "tags": [
          "Swaps"
        ],
        "security": [
          {
            "bearerAuth": [
              "crypto_wallet:swap"
            ]
          }
        ],
        "description": "Returns the status of a specific swap, by the id returned from POST /swaps.",
        "operationId": "get",
        "parameters": [
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "Swap ID returned from POST /swaps."
          }
        ],
        "responses": {
          "200": {
            "description": "swap status retrieved",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "object": {
                      "type": "string",
                      "enum": [
                        "swap"
                      ]
                    },
                    "id": {
                      "type": "string",
                      "description": "Swap ID."
                    },
                    "account_id": {
                      "type": "string",
                      "description": "Account ID that owns the wallet used for the swap."
                    },
                    "status": {
                      "type": "string",
                      "description": "Current swap status."
                    },
                    "tx_hashes": {
                      "type": "array",
                      "items": {
                        "type": "string"
                      },
                      "description": "On-chain transaction hashes produced by the swap."
                    },
                    "error": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "description": "Latest error returned for a failed swap."
                    }
                  },
                  "required": [
                    "object",
                    "id",
                    "account_id",
                    "status",
                    "tx_hashes"
                  ]
                }
              }
            }
          },
          "400": {
            "description": "swap id is invalid",
            "$ref": "#/components/responses/InvalidParameters"
          },
          "403": {
            "description": "credential lacks the crypto-wallet scope",
            "$ref": "#/components/responses/Forbidden"
          },
          "404": {
            "description": "swap does not exist",
            "$ref": "#/components/responses/NotFound"
          }
        }
      }
    },
    "/transfers": {
      "post": {
        "summary": "Create Transfer",
        "tags": [
          "Transfers"
        ],
        "security": [
          {
            "bearerAuth": [
              "payout:transfer_funds"
            ]
          },
          {
            "bearerAuth": [
              "payout:withdraw_funds"
            ]
          },
          {
            "bearerAuth": [
              "airdrop_link:manage"
            ]
          }
        ],
        "description": "Moves funds out of an account. `type` selects the kind of movement (default `ledger`): `ledger` transfers credit between two ledger accounts and returns a Transfer; `wallet_send` sends USDT from the origin account's Ethereum wallet to a recipient; `claim_link` funds a shareable claim link anyone with the URL can redeem.",
        "operationId": "create",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          }
        ],
        "responses": {
          "201": {
            "description": "money movement created",
            "content": {
              "application/json": {
                "schema": {
                  "oneOf": [
                    {
                      "type": "object",
                      "title": "Transfer",
                      "description": "A transfer of credit between two ledger accounts.",
                      "properties": {
                        "id": {
                          "type": "string",
                          "description": "Transfer ID."
                        },
                        "amount": {
                          "type": "number",
                          "description": "Transfer amount."
                        },
                        "currency": {
                          "type": "string",
                          "description": "Transfer currency."
                        },
                        "created_at": {
                          "type": "string",
                          "format": "date-time",
                          "description": "When the transfer was created."
                        },
                        "fee_amount": {
                          "type": [
                            "number",
                            "null"
                          ],
                          "description": "Fee charged for the transfer."
                        },
                        "notes": {
                          "type": [
                            "string",
                            "null"
                          ],
                          "description": "Transfer note."
                        },
                        "metadata": {
                          "type": [
                            "object",
                            "null"
                          ],
                          "additionalProperties": true,
                          "description": "Custom metadata attached to the transfer."
                        },
                        "origin_ledger_account_id": {
                          "type": "string",
                          "description": "Source ledger account ID."
                        },
                        "destination_ledger_account_id": {
                          "type": "string",
                          "description": "Destination ledger account ID."
                        },
                        "origin": {
                          "oneOf": [
                            {
                              "type": "object",
                              "title": "Company",
                              "properties": {
                                "typename": {
                                  "type": "string",
                                  "enum": [
                                    "Company"
                                  ]
                                },
                                "id": {
                                  "type": "string",
                                  "description": "Account ID."
                                },
                                "route": {
                                  "type": [
                                    "string",
                                    "null"
                                  ],
                                  "description": "Account route."
                                },
                                "title": {
                                  "type": [
                                    "string",
                                    "null"
                                  ],
                                  "description": "Account display name."
                                }
                              },
                              "required": [
                                "typename",
                                "id"
                              ]
                            },
                            {
                              "type": "object",
                              "title": "User",
                              "properties": {
                                "typename": {
                                  "type": "string",
                                  "enum": [
                                    "User"
                                  ]
                                },
                                "id": {
                                  "type": "string",
                                  "description": "User ID."
                                },
                                "name": {
                                  "type": [
                                    "string",
                                    "null"
                                  ],
                                  "description": "User display name."
                                },
                                "username": {
                                  "type": "string",
                                  "description": "User's username."
                                }
                              },
                              "required": [
                                "typename",
                                "id"
                              ]
                            }
                          ],
                          "discriminator": {
                            "propertyName": "typename"
                          },
                          "description": "Account or user sending funds."
                        },
                        "destination": {
                          "oneOf": [
                            {
                              "type": "object",
                              "title": "Company",
                              "properties": {
                                "typename": {
                                  "type": "string",
                                  "enum": [
                                    "Company"
                                  ]
                                },
                                "id": {
                                  "type": "string",
                                  "description": "Account ID."
                                },
                                "route": {
                                  "type": [
                                    "string",
                                    "null"
                                  ],
                                  "description": "Account route."
                                },
                                "title": {
                                  "type": [
                                    "string",
                                    "null"
                                  ],
                                  "description": "Account display name."
                                }
                              },
                              "required": [
                                "typename",
                                "id"
                              ]
                            },
                            {
                              "type": "object",
                              "title": "User",
                              "properties": {
                                "typename": {
                                  "type": "string",
                                  "enum": [
                                    "User"
                                  ]
                                },
                                "id": {
                                  "type": "string",
                                  "description": "User ID."
                                },
                                "name": {
                                  "type": [
                                    "string",
                                    "null"
                                  ],
                                  "description": "User display name."
                                },
                                "username": {
                                  "type": "string",
                                  "description": "User's username."
                                }
                              },
                              "required": [
                                "typename",
                                "id"
                              ]
                            }
                          ],
                          "discriminator": {
                            "propertyName": "typename"
                          },
                          "description": "Account or user receiving funds."
                        }
                      },
                      "required": [
                        "id",
                        "amount",
                        "currency",
                        "created_at",
                        "origin_ledger_account_id",
                        "destination_ledger_account_id",
                        "origin",
                        "destination"
                      ]
                    },
                    {
                      "type": "object",
                      "title": "Send",
                      "description": "Returned for a wallet_send: an onchain USDT send to a recipient.",
                      "properties": {
                        "object": {
                          "type": "string",
                          "enum": [
                            "send"
                          ]
                        },
                        "tx_hash": {
                          "type": "string"
                        },
                        "amount": {
                          "type": "string"
                        },
                        "currency": {
                          "type": "string"
                        },
                        "source": {
                          "type": "object",
                          "properties": {
                            "account_id": {
                              "type": "string"
                            },
                            "address": {
                              "type": "string"
                            }
                          },
                          "required": [
                            "account_id",
                            "address"
                          ]
                        },
                        "destination": {
                          "type": "object",
                          "properties": {
                            "account_id": {
                              "type": "string"
                            },
                            "address": {
                              "type": "string"
                            }
                          },
                          "required": [
                            "account_id",
                            "address"
                          ]
                        }
                      },
                      "required": [
                        "object",
                        "tx_hash",
                        "amount",
                        "currency",
                        "source",
                        "destination"
                      ]
                    },
                    {
                      "type": "object",
                      "title": "ClaimLink",
                      "description": "Returned for a claim_link: a shareable URL anyone can open to claim the funds.",
                      "properties": {
                        "object": {
                          "type": "string",
                          "enum": [
                            "claim_link"
                          ]
                        },
                        "id": {
                          "type": "string"
                        },
                        "claim_url": {
                          "type": "string"
                        },
                        "status": {
                          "type": "string"
                        },
                        "amount": {
                          "type": "string"
                        },
                        "currency": {
                          "type": "string"
                        },
                        "redeemable_count": {
                          "type": "integer"
                        },
                        "expires_at": {
                          "type": [
                            "string",
                            "null"
                          ],
                          "format": "date-time"
                        },
                        "source": {
                          "type": "object",
                          "properties": {
                            "account_id": {
                              "type": "string"
                            }
                          },
                          "required": [
                            "account_id"
                          ]
                        }
                      },
                      "required": [
                        "object",
                        "id",
                        "claim_url",
                        "status",
                        "amount",
                        "currency",
                        "redeemable_count",
                        "expires_at",
                        "source"
                      ]
                    }
                  ]
                }
              }
            }
          },
          "400": {
            "description": "request is invalid",
            "$ref": "#/components/responses/InvalidParameters"
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          },
          "403": {
            "description": "credential lacks the required scope",
            "$ref": "#/components/responses/Forbidden"
          }
        },
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "properties": {
                  "type": {
                    "type": "string",
                    "enum": [
                      "ledger",
                      "wallet_send",
                      "claim_link"
                    ],
                    "description": "The kind of money movement. Defaults to ledger."
                  },
                  "origin_id": {
                    "type": "string",
                    "description": "The account sending the funds. A user ID (user_xxx), account ID (biz_xxx), or ledger account ID (ldgr_xxx)."
                  },
                  "destination_id": {
                    "type": "string",
                    "description": "The recipient. Required for ledger and wallet_send (a user_/biz_/ldgr_ ID, or — for sends — an email). Omit for claim_link."
                  },
                  "amount": {
                    "type": "number",
                    "description": "The amount to move, in the transfer currency. For example 25.00."
                  },
                  "currency": {
                    "type": "string",
                    "description": "Currency, such as `usd`. Required for ledger transfers.",
                    "example": "usd"
                  },
                  "idempotence_key": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "Ledger transfers only. A unique key to prevent duplicate transfers."
                  },
                  "notes": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "Ledger transfers only. A short note describing the transfer."
                  },
                  "metadata": {
                    "type": [
                      "object",
                      "null"
                    ],
                    "additionalProperties": true,
                    "description": "Ledger transfers only. Custom key-value pairs attached to the transfer. Max 50 keys, 100 chars per key, 500 chars per string value."
                  },
                  "expires_at": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "format": "date-time",
                    "description": "claim_link only. Link expiry as an ISO 8601 timestamp. Defaults to 24 hours from creation."
                  },
                  "redeemable_count": {
                    "type": "integer",
                    "description": "claim_link only. How many different users can claim the link. Defaults to 1."
                  }
                },
                "required": [
                  "origin_id",
                  "amount"
                ]
              }
            }
          },
          "required": true
        }
      },
      "get": {
        "summary": "List Transfers",
        "tags": [
          "Transfers"
        ],
        "security": [
          {
            "bearerAuth": [
              "payout:transfer:read"
            ]
          }
        ],
        "description": "Lists ledger transfers for an account. You must specify an origin_id or a destination_id.",
        "operationId": "list",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "origin_id",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Filter to transfers sent from this account."
          },
          {
            "name": "destination_id",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Filter to transfers received by this account."
          },
          {
            "name": "order",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string",
              "enum": [
                "created_at",
                "amount"
              ]
            },
            "description": "Sort column. Defaults to created_at."
          },
          {
            "name": "direction",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string",
              "enum": [
                "asc",
                "desc"
              ]
            },
            "description": "Sort direction. Defaults to desc."
          },
          {
            "name": "created_before",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Only transfers created strictly before this ISO 8601 timestamp."
          },
          {
            "name": "created_after",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Only transfers created strictly after this ISO 8601 timestamp."
          },
          {
            "name": "first",
            "in": "query",
            "required": false,
            "schema": {
              "type": "integer",
              "default": 50,
              "maximum": 50
            },
            "description": "Number of transfers to return from the start of the window."
          },
          {
            "name": "after",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Cursor to fetch the page after (from page_info.end_cursor)."
          },
          {
            "name": "last",
            "in": "query",
            "required": false,
            "schema": {
              "type": "integer",
              "maximum": 50
            },
            "description": "Number of transfers to return from the end of the window."
          },
          {
            "name": "before",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string"
            },
            "description": "Cursor to fetch the page before (from page_info.start_cursor)."
          }
        ],
        "responses": {
          "200": {
            "description": "transfers listed",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "data": {
                      "type": "array",
                      "items": {
                        "type": "object",
                        "title": "TransferListItem",
                        "description": "A transfer of credit between two ledger accounts.",
                        "properties": {
                          "id": {
                            "type": "string",
                            "description": "Transfer ID."
                          },
                          "amount": {
                            "type": "number",
                            "description": "Transfer amount."
                          },
                          "currency": {
                            "type": "string",
                            "description": "Transfer currency."
                          },
                          "created_at": {
                            "type": "string",
                            "format": "date-time",
                            "description": "When the transfer was created."
                          },
                          "fee_amount": {
                            "type": [
                              "number",
                              "null"
                            ],
                            "description": "Fee charged for the transfer."
                          },
                          "notes": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "Transfer note."
                          },
                          "metadata": {
                            "type": [
                              "object",
                              "null"
                            ],
                            "additionalProperties": true,
                            "description": "Custom metadata attached to the transfer."
                          },
                          "origin_ledger_account_id": {
                            "type": "string",
                            "description": "Source ledger account ID."
                          },
                          "destination_ledger_account_id": {
                            "type": "string",
                            "description": "Destination ledger account ID."
                          }
                        },
                        "required": [
                          "id",
                          "amount",
                          "currency",
                          "created_at",
                          "origin_ledger_account_id",
                          "destination_ledger_account_id"
                        ]
                      }
                    },
                    "page_info": {
                      "type": "object",
                      "properties": {
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        },
                        "start_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "end_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "has_next_page",
                        "has_previous_page",
                        "start_cursor",
                        "end_cursor"
                      ]
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ]
                }
              }
            }
          },
          "400": {
            "description": "neither origin_id nor destination_id given",
            "$ref": "#/components/responses/InvalidParameters"
          }
        }
      }
    },
    "/transfers/{id}": {
      "get": {
        "summary": "Retrieve Transfer",
        "tags": [
          "Transfers"
        ],
        "security": [
          {
            "bearerAuth": [
              "payout:transfer:read"
            ]
          }
        ],
        "description": "Retrieves a ledger transfer by ID.",
        "operationId": "get",
        "responses": {
          "200": {
            "description": "transfer retrieved",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "title": "Transfer",
                  "description": "A transfer of credit between two ledger accounts.",
                  "properties": {
                    "id": {
                      "type": "string",
                      "description": "Transfer ID."
                    },
                    "amount": {
                      "type": "number",
                      "description": "Transfer amount."
                    },
                    "currency": {
                      "type": "string",
                      "description": "Transfer currency."
                    },
                    "created_at": {
                      "type": "string",
                      "format": "date-time",
                      "description": "When the transfer was created."
                    },
                    "fee_amount": {
                      "type": [
                        "number",
                        "null"
                      ],
                      "description": "Fee charged for the transfer."
                    },
                    "notes": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "description": "Transfer note."
                    },
                    "metadata": {
                      "type": [
                        "object",
                        "null"
                      ],
                      "additionalProperties": true,
                      "description": "Custom metadata attached to the transfer."
                    },
                    "origin_ledger_account_id": {
                      "type": "string",
                      "description": "Source ledger account ID."
                    },
                    "destination_ledger_account_id": {
                      "type": "string",
                      "description": "Destination ledger account ID."
                    },
                    "origin": {
                      "oneOf": [
                        {
                          "type": "object",
                          "title": "Company",
                          "properties": {
                            "typename": {
                              "type": "string",
                              "enum": [
                                "Company"
                              ]
                            },
                            "id": {
                              "type": "string",
                              "description": "Account ID."
                            },
                            "route": {
                              "type": [
                                "string",
                                "null"
                              ],
                              "description": "Account route."
                            },
                            "title": {
                              "type": [
                                "string",
                                "null"
                              ],
                              "description": "Account display name."
                            }
                          },
                          "required": [
                            "typename",
                            "id"
                          ]
                        },
                        {
                          "type": "object",
                          "title": "User",
                          "properties": {
                            "typename": {
                              "type": "string",
                              "enum": [
                                "User"
                              ]
                            },
                            "id": {
                              "type": "string",
                              "description": "User ID."
                            },
                            "name": {
                              "type": [
                                "string",
                                "null"
                              ],
                              "description": "User display name."
                            },
                            "username": {
                              "type": "string",
                              "description": "User's username."
                            }
                          },
                          "required": [
                            "typename",
                            "id"
                          ]
                        }
                      ],
                      "discriminator": {
                        "propertyName": "typename"
                      },
                      "description": "Account or user sending funds."
                    },
                    "destination": {
                      "oneOf": [
                        {
                          "type": "object",
                          "title": "Company",
                          "properties": {
                            "typename": {
                              "type": "string",
                              "enum": [
                                "Company"
                              ]
                            },
                            "id": {
                              "type": "string",
                              "description": "Account ID."
                            },
                            "route": {
                              "type": [
                                "string",
                                "null"
                              ],
                              "description": "Account route."
                            },
                            "title": {
                              "type": [
                                "string",
                                "null"
                              ],
                              "description": "Account display name."
                            }
                          },
                          "required": [
                            "typename",
                            "id"
                          ]
                        },
                        {
                          "type": "object",
                          "title": "User",
                          "properties": {
                            "typename": {
                              "type": "string",
                              "enum": [
                                "User"
                              ]
                            },
                            "id": {
                              "type": "string",
                              "description": "User ID."
                            },
                            "name": {
                              "type": [
                                "string",
                                "null"
                              ],
                              "description": "User display name."
                            },
                            "username": {
                              "type": "string",
                              "description": "User's username."
                            }
                          },
                          "required": [
                            "typename",
                            "id"
                          ]
                        }
                      ],
                      "discriminator": {
                        "propertyName": "typename"
                      },
                      "description": "Account or user receiving funds."
                    }
                  },
                  "required": [
                    "id",
                    "amount",
                    "currency",
                    "created_at",
                    "origin_ledger_account_id",
                    "destination_ledger_account_id",
                    "origin",
                    "destination"
                  ]
                }
              }
            }
          },
          "404": {
            "description": "transfer not found",
            "$ref": "#/components/responses/NotFound"
          }
        },
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "The transfer ID."
          }
        ]
      }
    },
    "/users": {
      "get": {
        "summary": "List Users",
        "tags": [
          "Users"
        ],
        "security": [
          {
            "bearerAuth": [
              "dms:read"
            ]
          }
        ],
        "description": "Search for users by name or username, ranked by social proximity to the authenticated user. Returns the user's most recently followed users when no query is given.",
        "operationId": "list",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "query",
            "in": "query",
            "schema": {
              "type": "string"
            },
            "required": false,
            "description": "A search term to filter users by name or username."
          },
          {
            "name": "first",
            "in": "query",
            "schema": {
              "type": "integer"
            },
            "required": false,
            "description": "The number of users to return (max 50)."
          },
          {
            "name": "after",
            "in": "query",
            "schema": {
              "type": "string"
            },
            "required": false,
            "description": "A cursor; returns users after this position."
          },
          {
            "name": "last",
            "in": "query",
            "schema": {
              "type": "integer"
            },
            "required": false,
            "description": "The number of users to return from the end of the range."
          },
          {
            "name": "before",
            "in": "query",
            "schema": {
              "type": "string"
            },
            "required": false,
            "description": "A cursor; returns users before this position."
          }
        ],
        "responses": {
          "200": {
            "description": "users listed",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "data": {
                      "type": "array",
                      "items": {
                        "$ref": "#/components/schemas/User"
                      }
                    },
                    "page_info": {
                      "type": "object",
                      "properties": {
                        "end_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "start_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        }
                      },
                      "required": [
                        "end_cursor",
                        "start_cursor",
                        "has_next_page",
                        "has_previous_page"
                      ]
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ]
                }
              }
            }
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          }
        }
      }
    },
    "/users/me": {
      "patch": {
        "summary": "Update Current User",
        "tags": [
          "Users"
        ],
        "security": [
          {
            "bearerAuth": [
              "user:profile:update"
            ]
          }
        ],
        "description": "Updates the authenticated user's global profile, or their profile override for an account when account_id is given. Not available to API keys.",
        "operationId": "me",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "account_id",
            "in": "query",
            "schema": {
              "type": "string"
            },
            "required": false,
            "description": "When set, updates the authenticated user's profile override for this account instead of their global profile."
          }
        ],
        "responses": {
          "200": {
            "description": "current user updated",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/User"
                }
              }
            }
          },
          "400": {
            "description": "API keys cannot update a global profile",
            "$ref": "#/components/responses/InvalidParameters"
          }
        },
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "properties": {
                  "name": {
                    "type": "string"
                  },
                  "bio": {
                    "type": "string"
                  },
                  "username": {
                    "type": "string"
                  },
                  "profile_picture": {
                    "type": "object",
                    "properties": {
                      "id": {
                        "type": "string"
                      },
                      "direct_upload_id": {
                        "type": "string"
                      }
                    }
                  }
                }
              }
            }
          },
          "required": true
        }
      }
    },
    "/users/{id}": {
      "get": {
        "summary": "Retrieve User",
        "tags": [
          "Users"
        ],
        "security": [
          {
            "bearerAuth": []
          }
        ],
        "description": "Retrieves a user's public profile by user_ tag, username, or 'me'.",
        "operationId": "get",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "User ID (prefixed `user_`), username, or `me`."
          },
          {
            "name": "account_id",
            "in": "query",
            "schema": {
              "type": "string"
            },
            "required": false,
            "description": "When set, returns the user's account-specific profile overrides for this account."
          }
        ],
        "responses": {
          "200": {
            "description": "user retrieved",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/User"
                }
              }
            }
          },
          "404": {
            "description": "no user with that id",
            "$ref": "#/components/responses/NotFound"
          }
        }
      },
      "patch": {
        "summary": "Update User",
        "tags": [
          "Users"
        ],
        "security": [
          {
            "bearerAuth": [
              "user:profile:update"
            ]
          }
        ],
        "description": "Updates a user. A user token updates their own global profile; an API key updates the user's account-specific profile override (account_id required).",
        "operationId": "update",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "User ID (prefixed `user_`), username, or `me`."
          },
          {
            "name": "account_id",
            "in": "query",
            "schema": {
              "type": "string"
            },
            "required": false,
            "description": "The account whose profile override to update. Required for API key callers."
          }
        ],
        "responses": {
          "200": {
            "description": "user updated",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/User"
                }
              }
            }
          }
        },
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "properties": {
                  "name": {
                    "type": "string"
                  },
                  "bio": {
                    "type": "string"
                  },
                  "username": {
                    "type": "string"
                  },
                  "profile_picture": {
                    "type": "object",
                    "properties": {
                      "id": {
                        "type": "string"
                      },
                      "direct_upload_id": {
                        "type": "string"
                      }
                    }
                  }
                }
              }
            }
          },
          "required": true
        }
      }
    },
    "/users/{id}/access/{resource_id}": {
      "get": {
        "summary": "Check User Access",
        "tags": [
          "Users"
        ],
        "security": [
          {
            "bearerAuth": []
          }
        ],
        "description": "Checks whether a user has access to an account, product, or experience the caller can reach.",
        "operationId": "access",
        "responses": {
          "200": {
            "description": "access checked",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "has_access": {
                      "type": "boolean"
                    },
                    "access_level": {
                      "type": "string",
                      "enum": [
                        "no_access",
                        "admin",
                        "customer"
                      ]
                    }
                  },
                  "required": [
                    "has_access",
                    "access_level"
                  ]
                }
              }
            }
          }
        },
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "The user_ tag or username to check access for."
          },
          {
            "name": "resource_id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "An account (biz_), product (prod_), or experience (exp_) ID."
          }
        ]
      }
    },
    "/verifications": {
      "post": {
        "summary": "Create Verification",
        "tags": [
          "Verifications"
        ],
        "security": [
          {
            "bearerAuth": [
              "identity:write"
            ]
          }
        ],
        "description": "Creates or resumes a verification session for an account.",
        "operationId": "create",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "account_id",
            "in": "query",
            "schema": {
              "type": "string"
            },
            "required": true,
            "description": "The account ID to verify (biz_ tag)."
          }
        ],
        "responses": {
          "201": {
            "description": "verification created",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "id": {
                      "type": "string",
                      "description": "The verification ID, e.g. idpf_*"
                    },
                    "kind": {
                      "type": "string",
                      "enum": [
                        "individual",
                        "business"
                      ]
                    },
                    "status": {
                      "type": "string",
                      "enum": [
                        "not_started",
                        "pending",
                        "approved",
                        "rejected",
                        "action_required"
                      ]
                    },
                    "first_name": {
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "last_name": {
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "date_of_birth": {
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "business_name": {
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "business_structure": {
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "country": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "description": "ISO 3166-1 alpha-2 country code (e.g. `US`, `GB`). For individuals this is the country of citizenship or residence reported by the identity provider; for businesses this is the country of incorporation."
                    },
                    "address": {
                      "type": [
                        "object",
                        "null"
                      ]
                    },
                    "session_url": {
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "requested_information": {
                      "type": "array",
                      "description": "The outstanding information this verification still needs — payout RFIs and audit RMIs, one uniform shape.",
                      "items": {
                        "type": "object",
                        "properties": {
                          "id": {
                            "type": "string",
                            "description": "The requested information item id (inrqi_*). Use this when answering."
                          },
                          "field": {
                            "type": "string",
                            "description": "Stable snake_case key for the field (e.g. ssn, business_description)."
                          },
                          "type": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "How to render the input: text, date, phone, address, files, or select."
                          },
                          "label": {
                            "type": "string",
                            "description": "Human-readable label for the field (e.g. \"Social Security Number\")."
                          },
                          "description": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "Additional guidance for the field beyond the label."
                          },
                          "requested_files": {
                            "type": "array",
                            "description": "Upload slots for a files item — always at least one when type is `files`, empty otherwise.",
                            "items": {
                              "type": "object",
                              "properties": {
                                "label": {
                                  "type": "string",
                                  "description": "Label for this upload slot (e.g. \"Front of ID Document\")."
                                },
                                "category": {
                                  "type": [
                                    "string",
                                    "null"
                                  ],
                                  "description": "Identifier to send back with the uploaded file so it routes correctly; null for a generic upload."
                                },
                                "kind": {
                                  "type": [
                                    "string",
                                    "null"
                                  ],
                                  "description": "Provider-specific document kind, when applicable."
                                },
                                "is_optional": {
                                  "type": "boolean",
                                  "description": "Whether this slot can be left empty."
                                },
                                "multiple": {
                                  "type": "boolean",
                                  "description": "Whether this slot accepts more than one file."
                                }
                              }
                            }
                          },
                          "options": {
                            "type": "array",
                            "description": "Allowed values for a `select` field (e.g. account_type, business_structure) — the submitted value must be one of these; empty for other types.",
                            "items": {
                              "type": "string"
                            }
                          },
                          "error_message": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "The reason a previously submitted value was rejected, or null."
                          }
                        }
                      }
                    },
                    "created_at": {
                      "type": "string"
                    },
                    "updated_at": {
                      "type": "string"
                    }
                  }
                }
              }
            }
          },
          "400": {
            "description": "missing account_id",
            "$ref": "#/components/responses/InvalidParameters"
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          },
          "403": {
            "description": "credential lacks the required identity-write scope",
            "$ref": "#/components/responses/Forbidden"
          },
          "404": {
            "description": "account not found",
            "$ref": "#/components/responses/NotFound"
          }
        },
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "properties": {
                  "kind": {
                    "type": "string",
                    "enum": [
                      "individual",
                      "business"
                    ],
                    "description": "The verification type. Defaults to individual."
                  },
                  "restart": {
                    "type": "boolean",
                    "description": "Whether to restart an in-flight verification."
                  },
                  "tax_identification_number": {
                    "type": "string",
                    "description": "Optional. Tax identification number — SSN for individuals, EIN for businesses. Tokenized in transit, never stored raw; stored on the profile so the payout account, provisioned on approval, doesn't raise a tax-id RFI."
                  },
                  "first_name": {
                    "type": "string",
                    "description": "Optional pre-fill claim. Seeds the Sumsub session; attested values come from Sumsub on approval."
                  },
                  "last_name": {
                    "type": "string",
                    "description": "Optional pre-fill claim. Seeds the Sumsub session; attested values come from Sumsub on approval."
                  },
                  "date_of_birth": {
                    "type": "string",
                    "description": "Optional pre-fill claim. Seeds the Sumsub session; attested values come from Sumsub on approval."
                  },
                  "business_name": {
                    "type": "string",
                    "description": "Optional pre-fill claim for businesses."
                  },
                  "business_structure": {
                    "type": "string",
                    "description": "Optional. Business structure (e.g. llc, corporation)."
                  },
                  "place_of_incorporation": {
                    "type": "string",
                    "description": "Optional. Place of incorporation (state/region); maps to the business address state."
                  },
                  "business_website": {
                    "type": "string",
                    "description": "Optional. Business website URL. Accepted for both individual and business verifications on company accounts; persisted to the account's metadata and used to provision the payout account on approval. Whop store pages are rejected."
                  },
                  "phone": {
                    "type": "string",
                    "description": "Optional pre-fill claim — phone number."
                  },
                  "country": {
                    "type": "string",
                    "description": "Optional pre-fill claim. Country code; for businesses, the country of incorporation."
                  },
                  "address": {
                    "type": "object",
                    "description": "Optional pre-fill claim. Address (line1, city, state, postal_code).",
                    "additionalProperties": true
                  }
                }
              }
            }
          }
        }
      },
      "get": {
        "summary": "List Verifications for Account",
        "tags": [
          "Verifications"
        ],
        "security": [
          {
            "bearerAuth": [
              "identity:read"
            ]
          }
        ],
        "description": "Lists identity verification profiles for an account.",
        "operationId": "list",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "account_id",
            "in": "query",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "The account ID to list verifications for (biz_ tag)."
          },
          {
            "name": "order",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string",
              "enum": [
                "updated_at",
                "created_at"
              ],
              "default": "updated_at"
            },
            "description": "The field to sort verifications by."
          },
          {
            "name": "direction",
            "in": "query",
            "required": false,
            "schema": {
              "type": "string",
              "enum": [
                "asc",
                "desc"
              ],
              "default": "desc"
            },
            "description": "Sort direction."
          }
        ],
        "responses": {
          "200": {
            "description": "verifications listed",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "data": {
                      "type": "array",
                      "items": {
                        "type": "object",
                        "properties": {
                          "id": {
                            "type": "string",
                            "description": "The verification ID, e.g. idpf_*"
                          },
                          "kind": {
                            "type": "string",
                            "enum": [
                              "individual",
                              "business"
                            ]
                          },
                          "status": {
                            "type": "string",
                            "enum": [
                              "not_started",
                              "pending",
                              "approved",
                              "rejected",
                              "action_required"
                            ]
                          },
                          "first_name": {
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "last_name": {
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "date_of_birth": {
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "business_name": {
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "business_structure": {
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "country": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "ISO 3166-1 alpha-2 country code (e.g. `US`, `GB`). For individuals this is the country of citizenship or residence reported by the identity provider; for businesses this is the country of incorporation."
                          },
                          "address": {
                            "type": [
                              "object",
                              "null"
                            ]
                          },
                          "session_url": {
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "requested_information": {
                            "type": "array",
                            "description": "The outstanding information this verification still needs — payout RFIs and audit RMIs, one uniform shape.",
                            "items": {
                              "type": "object",
                              "properties": {
                                "id": {
                                  "type": "string",
                                  "description": "The requested information item id (inrqi_*). Use this when answering."
                                },
                                "field": {
                                  "type": "string",
                                  "description": "Stable snake_case key for the field (e.g. ssn, business_description)."
                                },
                                "type": {
                                  "type": [
                                    "string",
                                    "null"
                                  ],
                                  "description": "How to render the input: text, date, phone, address, files, or select."
                                },
                                "label": {
                                  "type": "string",
                                  "description": "Human-readable label for the field (e.g. \"Social Security Number\")."
                                },
                                "description": {
                                  "type": [
                                    "string",
                                    "null"
                                  ],
                                  "description": "Additional guidance for the field beyond the label."
                                },
                                "requested_files": {
                                  "type": "array",
                                  "description": "Upload slots for a files item — always at least one when type is `files`, empty otherwise.",
                                  "items": {
                                    "type": "object",
                                    "properties": {
                                      "label": {
                                        "type": "string",
                                        "description": "Label for this upload slot (e.g. \"Front of ID Document\")."
                                      },
                                      "category": {
                                        "type": [
                                          "string",
                                          "null"
                                        ],
                                        "description": "Identifier to send back with the uploaded file so it routes correctly; null for a generic upload."
                                      },
                                      "kind": {
                                        "type": [
                                          "string",
                                          "null"
                                        ],
                                        "description": "Provider-specific document kind, when applicable."
                                      },
                                      "is_optional": {
                                        "type": "boolean",
                                        "description": "Whether this slot can be left empty."
                                      },
                                      "multiple": {
                                        "type": "boolean",
                                        "description": "Whether this slot accepts more than one file."
                                      }
                                    }
                                  }
                                },
                                "options": {
                                  "type": "array",
                                  "description": "Allowed values for a `select` field (e.g. account_type, business_structure) — the submitted value must be one of these; empty for other types.",
                                  "items": {
                                    "type": "string"
                                  }
                                },
                                "error_message": {
                                  "type": [
                                    "string",
                                    "null"
                                  ],
                                  "description": "The reason a previously submitted value was rejected, or null."
                                }
                              }
                            }
                          },
                          "created_at": {
                            "type": "string"
                          },
                          "updated_at": {
                            "type": "string"
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          },
          "400": {
            "description": "missing account_id",
            "$ref": "#/components/responses/InvalidParameters"
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          },
          "403": {
            "description": "credential lacks the required identity-read scope",
            "$ref": "#/components/responses/Forbidden"
          }
        }
      }
    },
    "/verifications/{verification_id}": {
      "get": {
        "summary": "Retrieve Verification",
        "tags": [
          "Verifications"
        ],
        "security": [
          {
            "bearerAuth": [
              "identity:read"
            ]
          }
        ],
        "description": "Retrieves a single identity verification profile by its idpf_ tag.",
        "operationId": "get",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "verification_id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "The ID of the verification, which will look like idpf_*************"
          }
        ],
        "responses": {
          "200": {
            "description": "verification retrieved",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "id": {
                      "type": "string",
                      "description": "The verification ID, e.g. idpf_*"
                    },
                    "kind": {
                      "type": "string",
                      "enum": [
                        "individual",
                        "business"
                      ]
                    },
                    "status": {
                      "type": "string",
                      "enum": [
                        "not_started",
                        "pending",
                        "approved",
                        "rejected",
                        "action_required"
                      ]
                    },
                    "first_name": {
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "last_name": {
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "date_of_birth": {
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "business_name": {
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "business_structure": {
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "country": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "description": "ISO 3166-1 alpha-2 country code (e.g. `US`, `GB`). For individuals this is the country of citizenship or residence reported by the identity provider; for businesses this is the country of incorporation."
                    },
                    "address": {
                      "type": [
                        "object",
                        "null"
                      ]
                    },
                    "session_url": {
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "requested_information": {
                      "type": "array",
                      "description": "The outstanding information this verification still needs — payout RFIs and audit RMIs, one uniform shape.",
                      "items": {
                        "type": "object",
                        "properties": {
                          "id": {
                            "type": "string",
                            "description": "The requested information item id (inrqi_*). Use this when answering."
                          },
                          "field": {
                            "type": "string",
                            "description": "Stable snake_case key for the field (e.g. ssn, business_description)."
                          },
                          "type": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "How to render the input: text, date, phone, address, files, or select."
                          },
                          "label": {
                            "type": "string",
                            "description": "Human-readable label for the field (e.g. \"Social Security Number\")."
                          },
                          "description": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "Additional guidance for the field beyond the label."
                          },
                          "requested_files": {
                            "type": "array",
                            "description": "Upload slots for a files item — always at least one when type is `files`, empty otherwise.",
                            "items": {
                              "type": "object",
                              "properties": {
                                "label": {
                                  "type": "string",
                                  "description": "Label for this upload slot (e.g. \"Front of ID Document\")."
                                },
                                "category": {
                                  "type": [
                                    "string",
                                    "null"
                                  ],
                                  "description": "Identifier to send back with the uploaded file so it routes correctly; null for a generic upload."
                                },
                                "kind": {
                                  "type": [
                                    "string",
                                    "null"
                                  ],
                                  "description": "Provider-specific document kind, when applicable."
                                },
                                "is_optional": {
                                  "type": "boolean",
                                  "description": "Whether this slot can be left empty."
                                },
                                "multiple": {
                                  "type": "boolean",
                                  "description": "Whether this slot accepts more than one file."
                                }
                              }
                            }
                          },
                          "options": {
                            "type": "array",
                            "description": "Allowed values for a `select` field (e.g. account_type, business_structure) — the submitted value must be one of these; empty for other types.",
                            "items": {
                              "type": "string"
                            }
                          },
                          "error_message": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "The reason a previously submitted value was rejected, or null."
                          }
                        }
                      }
                    },
                    "created_at": {
                      "type": "string"
                    },
                    "updated_at": {
                      "type": "string"
                    }
                  }
                }
              }
            }
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          },
          "404": {
            "description": "no verification with that ID is visible to the credential",
            "$ref": "#/components/responses/NotFound"
          }
        }
      },
      "patch": {
        "summary": "Update Verification",
        "tags": [
          "Verifications"
        ],
        "security": [
          {
            "bearerAuth": [
              "identity:write"
            ]
          }
        ],
        "description": "Updates fields on an identity verification profile, or responds to outstanding RFIs.",
        "operationId": "update",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "verification_id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "The ID of the verification, which will look like idpf_*************"
          }
        ],
        "responses": {
          "200": {
            "description": "verification updated",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "id": {
                      "type": "string",
                      "description": "The verification ID, e.g. idpf_*"
                    },
                    "kind": {
                      "type": "string",
                      "enum": [
                        "individual",
                        "business"
                      ]
                    },
                    "status": {
                      "type": "string",
                      "enum": [
                        "not_started",
                        "pending",
                        "approved",
                        "rejected",
                        "action_required"
                      ]
                    },
                    "first_name": {
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "last_name": {
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "date_of_birth": {
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "business_name": {
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "business_structure": {
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "country": {
                      "type": [
                        "string",
                        "null"
                      ],
                      "description": "ISO 3166-1 alpha-2 country code (e.g. `US`, `GB`). For individuals this is the country of citizenship or residence reported by the identity provider; for businesses this is the country of incorporation."
                    },
                    "address": {
                      "type": [
                        "object",
                        "null"
                      ]
                    },
                    "session_url": {
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "requested_information": {
                      "type": "array",
                      "description": "The outstanding information this verification still needs — payout RFIs and audit RMIs, one uniform shape.",
                      "items": {
                        "type": "object",
                        "properties": {
                          "id": {
                            "type": "string",
                            "description": "The requested information item id (inrqi_*). Use this when answering."
                          },
                          "field": {
                            "type": "string",
                            "description": "Stable snake_case key for the field (e.g. ssn, business_description)."
                          },
                          "type": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "How to render the input: text, date, phone, address, files, or select."
                          },
                          "label": {
                            "type": "string",
                            "description": "Human-readable label for the field (e.g. \"Social Security Number\")."
                          },
                          "description": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "Additional guidance for the field beyond the label."
                          },
                          "requested_files": {
                            "type": "array",
                            "description": "Upload slots for a files item — always at least one when type is `files`, empty otherwise.",
                            "items": {
                              "type": "object",
                              "properties": {
                                "label": {
                                  "type": "string",
                                  "description": "Label for this upload slot (e.g. \"Front of ID Document\")."
                                },
                                "category": {
                                  "type": [
                                    "string",
                                    "null"
                                  ],
                                  "description": "Identifier to send back with the uploaded file so it routes correctly; null for a generic upload."
                                },
                                "kind": {
                                  "type": [
                                    "string",
                                    "null"
                                  ],
                                  "description": "Provider-specific document kind, when applicable."
                                },
                                "is_optional": {
                                  "type": "boolean",
                                  "description": "Whether this slot can be left empty."
                                },
                                "multiple": {
                                  "type": "boolean",
                                  "description": "Whether this slot accepts more than one file."
                                }
                              }
                            }
                          },
                          "options": {
                            "type": "array",
                            "description": "Allowed values for a `select` field (e.g. account_type, business_structure) — the submitted value must be one of these; empty for other types.",
                            "items": {
                              "type": "string"
                            }
                          },
                          "error_message": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "The reason a previously submitted value was rejected, or null."
                          }
                        }
                      }
                    },
                    "created_at": {
                      "type": "string"
                    },
                    "updated_at": {
                      "type": "string"
                    }
                  }
                }
              }
            }
          },
          "400": {
            "description": "the request body has unsupported fields",
            "$ref": "#/components/responses/InvalidParameters"
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          },
          "403": {
            "description": "credential lacks the required identity-write scope",
            "$ref": "#/components/responses/Forbidden"
          },
          "404": {
            "description": "no verification with that ID is visible to the credential",
            "$ref": "#/components/responses/NotFound"
          }
        },
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "properties": {
                  "first_name": {
                    "type": "string",
                    "description": "The first name on the verification."
                  },
                  "last_name": {
                    "type": "string",
                    "description": "The last name on the verification."
                  },
                  "date_of_birth": {
                    "type": "string",
                    "description": "The date of birth."
                  },
                  "country": {
                    "type": "string",
                    "description": "The country code."
                  },
                  "personal_address": {
                    "type": "object",
                    "additionalProperties": true,
                    "description": "The personal address."
                  },
                  "business_name": {
                    "type": "string",
                    "description": "The business name."
                  },
                  "business_address": {
                    "type": "object",
                    "additionalProperties": true,
                    "description": "The business address."
                  },
                  "business_structure": {
                    "type": "string",
                    "description": "The business structure."
                  },
                  "requested_information": {
                    "type": "array",
                    "description": "Answers to requested information. Each entry must include id and a value, address, or files payload.",
                    "items": {
                      "type": "object",
                      "properties": {
                        "id": {
                          "type": "string",
                          "description": "The requested information item id (inrqi_*)."
                        },
                        "value": {
                          "type": "string",
                          "description": "The value for text/date/phone items."
                        },
                        "value_type": {
                          "type": "string",
                          "enum": [
                            "raw",
                            "vault_token"
                          ],
                          "description": "Defaults to the field's configured type."
                        },
                        "address": {
                          "type": "object",
                          "additionalProperties": true,
                          "description": "Address payload for address items."
                        },
                        "files": {
                          "type": "array",
                          "items": {
                            "type": "object"
                          },
                          "description": "File upload payload for document items."
                        }
                      },
                      "required": [
                        "id"
                      ]
                    }
                  }
                }
              }
            }
          },
          "required": true
        }
      },
      "delete": {
        "summary": "Delete Verification",
        "tags": [
          "Verifications"
        ],
        "security": [
          {
            "bearerAuth": [
              "identity:write"
            ]
          }
        ],
        "description": "Unlinks a verification from the caller's accounts.",
        "operationId": "delete",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "name": "verification_id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string"
            },
            "description": "The ID of the verification, which will look like idpf_*************"
          }
        ],
        "responses": {
          "200": {
            "description": "verification deleted",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "id": {
                      "type": "string"
                    },
                    "deleted": {
                      "type": "boolean"
                    }
                  }
                }
              }
            }
          },
          "401": {
            "description": "missing or invalid authentication",
            "$ref": "#/components/responses/Unauthorized"
          },
          "403": {
            "description": "credential lacks the required identity-write scope",
            "$ref": "#/components/responses/Forbidden"
          },
          "404": {
            "description": "no verification with that ID is visible to the credential",
            "$ref": "#/components/responses/NotFound"
          }
        }
      }
    }
  },
  "components": {
    "securitySchemes": {
      "bearerAuth": {
        "type": "http",
        "scheme": "bearer",
        "bearerFormat": "auth-scheme",
        "description": "An account API key, account scoped JWT, app API key, or user OAuth token."
      }
    },
    "schemas": {
      "AccountBalanceToken": {
        "type": "object",
        "properties": {
          "symbol": {
            "type": "string",
            "description": "Holding display symbol, such as `USDT`, `cbBTC`, or `EUR`."
          },
          "name": {
            "type": "string",
            "description": "The holding's display name"
          },
          "balance": {
            "type": "string",
            "description": "Total amount held in native units, as a decimal string."
          },
          "value_usd": {
            "type": [
              "string",
              "null"
            ],
            "description": "Holding USD value, or `null` when no exchange rate is available."
          },
          "price_usd": {
            "type": [
              "number",
              "null"
            ],
            "description": "USD price per unit, or `null` when no exchange rate is available."
          },
          "icon_url": {
            "type": [
              "string",
              "null"
            ],
            "description": "Holding icon URL."
          },
          "breakdown": {
            "type": "object",
            "description": "Balance split into available, pending, and reserve amounts, as native-unit decimal strings. On-chain crypto is entirely available; good_funds and fiat cash can have pending or reserve portions."
          }
        },
        "required": [
          "symbol",
          "name",
          "balance",
          "value_usd",
          "price_usd",
          "icon_url",
          "breakdown"
        ]
      },
      "AccountCapabilities": {
        "type": "object",
        "properties": {
          "accept_card_payments": {
            "type": "string",
            "description": "Card payins, including Apple Pay and Google Pay",
            "enum": [
              "active",
              "inactive",
              "pending"
            ],
            "example": "active"
          },
          "accept_bank_payments": {
            "type": "string",
            "description": "Bank payins: debits, transfers, and local bank rails",
            "enum": [
              "active",
              "inactive",
              "pending"
            ],
            "example": "active"
          },
          "accept_bnpl_payments": {
            "type": "string",
            "description": "Buy-now-pay-later payins; requires approval",
            "enum": [
              "active",
              "inactive",
              "pending"
            ],
            "example": "active"
          },
          "standard_payout": {
            "type": "string",
            "description": "Standard payouts to an external payout destination",
            "enum": [
              "active",
              "inactive",
              "pending"
            ],
            "example": "active"
          },
          "instant_payout": {
            "type": "string",
            "description": "Instant payouts to an eligible payout destination",
            "enum": [
              "active",
              "inactive",
              "pending"
            ],
            "example": "active"
          },
          "crypto_payout": {
            "type": "string",
            "description": "On-chain payouts to a crypto wallet",
            "enum": [
              "active",
              "inactive",
              "pending"
            ],
            "example": "active"
          },
          "transfer": {
            "type": "string",
            "description": "Transfers to other accounts",
            "enum": [
              "active",
              "inactive",
              "pending"
            ],
            "example": "active"
          },
          "bank_deposit": {
            "type": "string",
            "description": "Deposits by bank wire or ACH to the account's virtual bank account",
            "enum": [
              "active",
              "inactive",
              "pending"
            ],
            "example": "active"
          },
          "crypto_deposit": {
            "type": "string",
            "description": "On-chain deposits to the account's crypto wallet",
            "enum": [
              "active",
              "inactive",
              "pending"
            ],
            "example": "active"
          },
          "card_deposit": {
            "type": "string",
            "description": "Balance top-ups by charging a stored payment method",
            "enum": [
              "active",
              "inactive",
              "pending"
            ],
            "example": "active"
          },
          "card_issuing": {
            "type": "string",
            "description": "Issuing Whop cards; requires card application approval",
            "enum": [
              "active",
              "inactive",
              "pending"
            ],
            "example": "active"
          }
        },
        "required": [
          "accept_card_payments",
          "accept_bank_payments",
          "accept_bnpl_payments",
          "standard_payout",
          "instant_payout",
          "crypto_payout",
          "transfer",
          "bank_deposit",
          "crypto_deposit",
          "card_deposit",
          "card_issuing"
        ]
      },
      "AccountRecommendedAction": {
        "type": "object",
        "properties": {
          "action": {
            "type": "string",
            "description": "The recommendation; new values may be added, so handle unknown actions gracefully",
            "enum": [
              "apply_for_financing",
              "migrate_from_stripe",
              "accept_first_payment",
              "join_whop_university"
            ],
            "example": "apply_for_financing"
          },
          "status": {
            "type": "string",
            "description": "Always optional — never blocking",
            "enum": [
              "optional"
            ],
            "example": "optional"
          },
          "title": {
            "type": "string",
            "description": "Headline for the recommendation"
          },
          "description": {
            "type": "string",
            "description": "Supporting copy, or empty"
          },
          "cta_label": {
            "type": "string",
            "description": "Button label"
          },
          "cta": {
            "type": "string",
            "description": "The URL the call-to-action links to"
          },
          "icon_url": {
            "type": [
              "string",
              "null"
            ],
            "description": "Illustration icon URL, or `null`"
          },
          "blocked_capabilities": {
            "type": "array",
            "items": {
              "type": "string",
              "description": "Capabilities this would unlock, or empty"
            }
          }
        },
        "required": [
          "action",
          "status",
          "title",
          "description",
          "cta_label",
          "cta",
          "icon_url",
          "blocked_capabilities"
        ]
      },
      "AccountRequiredAction": {
        "type": "object",
        "properties": {
          "action": {
            "type": "string",
            "description": "What the holder must do; new values may be added, so handle unknown actions gracefully",
            "enum": [
              "deposit_funds",
              "submit_information_request",
              "verify_identity",
              "connect_fulfillment_tracker"
            ],
            "example": "deposit_funds"
          },
          "status": {
            "type": "string",
            "description": "required (act now) or pending (under review)",
            "enum": [
              "required",
              "pending"
            ],
            "example": "required"
          },
          "title": {
            "type": "string",
            "description": "Headline for the action"
          },
          "description": {
            "type": "string",
            "description": "Supporting copy, or empty"
          },
          "cta_label": {
            "type": "string",
            "description": "Button label, or empty when there is no button"
          },
          "cta": {
            "type": [
              "string",
              "null"
            ],
            "description": "The URL the call-to-action links to, or null when there is no button"
          },
          "icon_url": {
            "type": [
              "string",
              "null"
            ],
            "description": "The URL of the action's illustration icon, or null if it has none"
          },
          "blocked_capabilities": {
            "type": "array",
            "items": {
              "type": "string",
              "description": "Capabilities gated until this is resolved"
            }
          }
        },
        "required": [
          "action",
          "status",
          "title",
          "description",
          "cta_label",
          "cta",
          "icon_url",
          "blocked_capabilities"
        ]
      },
      "Account": {
        "type": "object",
        "properties": {
          "id": {
            "type": "string",
            "description": "Account ID, prefixed `biz_`."
          },
          "email": {
            "type": [
              "string",
              "null"
            ],
            "description": "Account owner email address."
          },
          "title": {
            "type": "string",
            "description": "Account display name."
          },
          "description": {
            "type": [
              "string",
              "null"
            ],
            "description": "Account promotional description."
          },
          "route": {
            "type": "string",
            "description": "Account public route identifier."
          },
          "logo_url": {
            "type": [
              "string",
              "null"
            ],
            "description": "Account logo image URL."
          },
          "banner_image_url": {
            "type": [
              "string",
              "null"
            ],
            "description": "Account banner image URL."
          },
          "business_type": {
            "type": [
              "string",
              "null"
            ],
            "description": "High-level business category for the account."
          },
          "country": {
            "type": [
              "string",
              "null"
            ],
            "description": "Country where the account is located."
          },
          "home_preferences": {
            "type": "array",
            "items": {
              "type": "string",
              "description": "Public account home page preferences."
            }
          },
          "industry_group": {
            "type": [
              "string",
              "null"
            ],
            "description": "Account industry group."
          },
          "industry_type": {
            "type": [
              "string",
              "null"
            ],
            "description": "Specific industry vertical for the account."
          },
          "invoice_prefix": {
            "type": [
              "string",
              "null"
            ],
            "description": "Prefix used for account invoices."
          },
          "onboarding_type": {
            "type": [
              "string",
              "null"
            ],
            "description": "Type of onboarding the account has completed."
          },
          "opengraph_image_url": {
            "type": [
              "string",
              "null"
            ],
            "description": "Account Open Graph image URL."
          },
          "opengraph_image_variant": {
            "type": [
              "string",
              "null"
            ],
            "description": "Account Open Graph image variant."
          },
          "other_business_description": {
            "type": [
              "string",
              "null"
            ],
            "description": "Business type details when business_type is `other`."
          },
          "other_industry_description": {
            "type": [
              "string",
              "null"
            ],
            "description": "Industry details when industry_type is `other`."
          },
          "require_2fa": {
            "type": "boolean",
            "description": "Whether authorized users must enable two-factor authentication."
          },
          "target_audience": {
            "type": [
              "string",
              "null"
            ],
            "description": "Target audience for this account."
          },
          "send_customer_emails": {
            "type": "boolean",
            "description": "Whether Whop sends transactional emails to customers on behalf of this account."
          },
          "show_joined_whops": {
            "type": "boolean",
            "description": "Whether the account appears in joined whops on other accounts."
          },
          "show_reviews_dtc": {
            "type": "boolean",
            "description": "Whether reviews are displayed on direct-to-consumer product pages."
          },
          "show_user_directory": {
            "type": "boolean",
            "description": "Whether the account shows users in the user directory."
          },
          "store_page_config": {
            "type": "object",
            "description": "Account store page display configuration."
          },
          "parent_account_id": {
            "type": [
              "string",
              "null"
            ],
            "description": "Parent account ID for connected accounts."
          },
          "metadata": {
            "type": "object",
            "description": "Arbitrary key/value metadata supplied at account creation."
          },
          "created_at": {
            "type": "string",
            "description": "When the account was created, as an ISO 8601 timestamp."
          },
          "use_logo_as_opengraph_image_fallback": {
            "type": "boolean",
            "description": "Whether the account uses its logo as the fallback Open Graph image."
          },
          "total_earned_usd": {
            "type": [
              "number",
              "null"
            ],
            "description": "Account lifetime sales, normalized to USD. Computed only on `retrieve` and `me` for callers with `stats:read` scope; `null` otherwise."
          },
          "status": {
            "type": [
              "string",
              "null"
            ],
            "description": "Whether the account can operate on Whop: `active` or `suspended`. Computed only on `retrieve` and `me`; `null` otherwise."
          },
          "capabilities": {
            "description": "Payment rails enabled for this account, each `active`, `inactive`, or `pending` (onboarding or review in progress). Computed only on `retrieve` and `me` for callers with `company:balance:read` scope; `null` otherwise.",
            "oneOf": [
              {
                "$ref": "#/components/schemas/AccountCapabilities"
              },
              {
                "type": "null"
              }
            ]
          },
          "required_actions": {
            "type": [
              "array",
              "null"
            ],
            "items": {
              "$ref": "#/components/schemas/AccountRequiredAction",
              "description": "Actions the account owner must take to unblock capabilities like payouts and card spend, ordered by display priority. Computed only on `retrieve` and `me` for callers with `company:balance:read` scope; `null` otherwise."
            }
          },
          "recommended_actions": {
            "type": [
              "array",
              "null"
            ],
            "items": {
              "$ref": "#/components/schemas/AccountRecommendedAction",
              "description": "Optional actions that unlock capabilities or grow the account, same shape as `required_actions`. Computed only on `retrieve` and `me`; `null` otherwise."
            }
          },
          "tax_remitted_by": {
            "type": [
              "string",
              "null"
            ],
            "description": "Who calculates and remits tax for the account: `whop` (Whop calculates and remits), `self` (Whop calculates; the account collects and remits), or `none` (neither; the account is responsible). `null` until the account enrolls in the Whop tax service."
          },
          "product_tax_code": {
            "type": [
              "object",
              "null"
            ],
            "description": "Tax classification code applied by default to the account's products, with `id`, `name`, and `product_type`. `null` when no default is set."
          },
          "business_address": {
            "type": [
              "object",
              "null"
            ],
            "description": "Account business address used to calculate tax, with `line1`, `line2`, `city`, `state`, `postal_code`, and `country`. `null` when no address is set."
          },
          "tax_identifiers": {
            "type": "array",
            "description": "Account tax/VAT registrations, each with `id`, `tax_id_type`, and `tax_id_value`. Empty when none are set."
          },
          "total_usd": {
            "type": [
              "string",
              "null"
            ],
            "description": "Total USD value across balances with known exchange rates. Computed only on single-account reads (`retrieve` and `me`); `null` on list responses, writes, missing balance-read permission, or unavailable balance source."
          },
          "balances": {
            "type": "array",
            "items": {
              "$ref": "#/components/schemas/AccountBalanceToken",
              "description": "Account holdings, each with USD value. Empty when `total_usd` is `null`."
            }
          },
          "verification": {
            "type": "object",
            "description": "Account identity verification status for the `individual` (KYC) and `business` (KYB) profiles. Each is `null` until created, otherwise a `status` of `not_started`, `pending`, `approved`, or `rejected`."
          },
          "wallet": {
            "description": "Account primary crypto wallet, or `null` if none has been provisioned.",
            "oneOf": [
              {
                "$ref": "#/components/schemas/AccountWallet"
              },
              {
                "type": "null"
              }
            ]
          },
          "social_links": {
            "type": "array",
            "items": {
              "$ref": "#/components/schemas/AccountSocialLink",
              "description": "Account social links."
            }
          }
        },
        "required": [
          "id",
          "email",
          "title",
          "description",
          "route",
          "logo_url",
          "banner_image_url",
          "business_type",
          "country",
          "home_preferences",
          "industry_group",
          "industry_type",
          "invoice_prefix",
          "onboarding_type",
          "opengraph_image_url",
          "opengraph_image_variant",
          "other_business_description",
          "other_industry_description",
          "require_2fa",
          "target_audience",
          "send_customer_emails",
          "show_joined_whops",
          "show_reviews_dtc",
          "show_user_directory",
          "store_page_config",
          "parent_account_id",
          "metadata",
          "created_at",
          "use_logo_as_opengraph_image_fallback",
          "total_earned_usd",
          "status",
          "capabilities",
          "required_actions",
          "recommended_actions",
          "tax_remitted_by",
          "product_tax_code",
          "business_address",
          "tax_identifiers",
          "total_usd",
          "balances",
          "verification",
          "wallet",
          "social_links"
        ]
      },
      "AccountSocialLink": {
        "type": "object",
        "properties": {
          "id": {
            "type": "string",
            "description": "The ID of the social link"
          },
          "website": {
            "type": "string",
            "description": "The social platform for this link",
            "enum": [
              "x",
              "instagram",
              "facebook",
              "tiktok",
              "youtube",
              "linkedin",
              "twitch",
              "website",
              "custom"
            ],
            "example": "x"
          },
          "url": {
            "type": "string",
            "description": "The social link URL"
          },
          "title": {
            "type": [
              "string",
              "null"
            ],
            "description": "The optional display title for the social link"
          }
        },
        "required": [
          "id",
          "website",
          "url",
          "title"
        ]
      },
      "AccountWallet": {
        "type": "object",
        "properties": {
          "id": {
            "type": "string",
            "description": "Wallet ID, prefixed `wallet_`."
          },
          "address": {
            "type": "string",
            "description": "The on-chain address of the wallet"
          },
          "network": {
            "type": "string",
            "description": "The blockchain network the wallet lives on",
            "enum": [
              "solana",
              "ethereum",
              "bitcoin"
            ],
            "example": "solana"
          }
        },
        "required": [
          "id",
          "address",
          "network"
        ]
      },
      "AdCampaign": {
        "type": "object",
        "properties": {
          "click_through_rate": {
            "type": "number",
            "description": "Clicks divided by impressions, between 0 and 1."
          },
          "clicks": {
            "type": "number",
            "description": "The number of clicks."
          },
          "cost_per_click": {
            "type": "number",
            "description": "Spend divided by clicks; 0 when there are no clicks."
          },
          "cost_per_lead": {
            "type": [
              "number",
              "null"
            ],
            "description": "Spend divided by attributed leads; null when leads are not a goal and none are attributed."
          },
          "cost_per_mille": {
            "type": "number",
            "description": "Spend per 1,000 impressions; 0 when there are no impressions."
          },
          "cost_per_purchase": {
            "type": [
              "number",
              "null"
            ],
            "description": "Spend divided by attributed purchases; null when purchases are not a goal and none are attributed."
          },
          "cost_per_schedule": {
            "type": [
              "number",
              "null"
            ],
            "description": "Spend divided by attributed schedule events; null when schedules are not the goal and none are attributed."
          },
          "cost_per_submitted_application": {
            "type": [
              "number",
              "null"
            ],
            "description": "Spend divided by attributed submit-application events; null when they are not the goal and none are attributed."
          },
          "cost_per_contact": {
            "type": [
              "number",
              "null"
            ],
            "description": "Spend divided by attributed contact events; null when contacts are not the goal and none are attributed."
          },
          "cost_per_completed_registration": {
            "type": [
              "number",
              "null"
            ],
            "description": "Spend divided by attributed complete-registration events; null when they are not the goal and none are attributed."
          },
          "cost_per_viewed_content": {
            "type": [
              "number",
              "null"
            ],
            "description": "Spend divided by attributed view-content events; null when they are not the goal and none are attributed."
          },
          "cost_per_added_to_cart": {
            "type": [
              "number",
              "null"
            ],
            "description": "Spend divided by attributed add-to-cart events; null when they are not the goal and none are attributed."
          },
          "frequency": {
            "type": [
              "number",
              "null"
            ],
            "description": "Platform-reported impressions divided by reach."
          },
          "impressions": {
            "type": "number",
            "description": "The number of impressions."
          },
          "leads": {
            "type": "number",
            "description": "Whop pixel-attributed leads, last-click."
          },
          "purchase_value": {
            "type": "number",
            "description": "USD value of pixel-attributed purchases."
          },
          "purchases": {
            "type": "number",
            "description": "Whop pixel-attributed purchases, last-click."
          },
          "reach": {
            "type": "number",
            "description": "The number of unique people who saw this."
          },
          "return_on_ad_spend": {
            "type": "number",
            "description": "Purchase value divided by spend; 0 when there is no spend."
          },
          "spend": {
            "type": "number",
            "description": "The amount charged, in spend_currency."
          },
          "spend_currency": {
            "type": [
              "string",
              "null"
            ],
            "description": "The ISO 4217 currency code of all monetary metrics."
          },
          "unique_click_through_rate": {
            "type": [
              "number",
              "null"
            ],
            "description": "Unique clicks divided by impressions, between 0 and 1."
          },
          "unique_clicks": {
            "type": "number",
            "description": "The number of unique clicks."
          },
          "schedules": {
            "type": "number",
            "description": "Whop pixel-attributed schedule events, last-click."
          },
          "submitted_applications": {
            "type": "number",
            "description": "Whop pixel-attributed submit-application events, last-click."
          },
          "contacts": {
            "type": "number",
            "description": "Whop pixel-attributed contact events, last-click."
          },
          "completed_registrations": {
            "type": "number",
            "description": "Whop pixel-attributed complete-registration events, last-click."
          },
          "viewed_contents": {
            "type": "number",
            "description": "Whop pixel-attributed view-content events, last-click."
          },
          "added_to_carts": {
            "type": "number",
            "description": "Whop pixel-attributed add-to-cart events, last-click."
          },
          "custom_conversions": {
            "type": "number",
            "description": "Whop pixel-attributed custom (merchant-defined) conversion events, last-click, across all custom event names."
          },
          "id": {
            "type": "string",
            "description": "Unique identifier for the ad campaign."
          },
          "title": {
            "type": "string",
            "description": "The title of the ad campaign."
          },
          "status": {
            "type": "string",
            "description": "The lifecycle status of the ad campaign.",
            "enum": [
              "draft",
              "active",
              "paused",
              "payment_failed"
            ],
            "example": "draft"
          },
          "platform": {
            "type": "string",
            "description": "The ad network the campaign runs on.",
            "enum": [
              "meta"
            ],
            "example": "meta"
          },
          "objective": {
            "type": [
              "string",
              "null"
            ],
            "description": "The goal the campaign optimizes toward.",
            "enum": [
              "awareness",
              "traffic",
              "engagement",
              "leads",
              "sales",
              null
            ],
            "example": "awareness"
          },
          "special_ad_categories": {
            "type": "array",
            "items": {
              "type": "string",
              "description": "Regulated categories the campaign is declared under. Ads in these categories are subject to extra targeting restrictions. Empty when none apply.",
              "enum": [
                "housing",
                "employment",
                "financial_products",
                "politics"
              ],
              "example": "housing"
            }
          },
          "optimization_goal": {
            "type": [
              "string",
              "null"
            ],
            "description": "The specific event the campaign optimizes for. If the campaign is CBO, then all ad groups will have the same optimization goal, which will be returned here."
          },
          "budget_amount": {
            "type": [
              "number",
              "null"
            ],
            "description": "The campaign budget in USD. Null when budget is set at the ad group level (ABO)."
          },
          "budget_type": {
            "type": [
              "string",
              "null"
            ],
            "description": "Whether the budget is spent per day or over the campaign's lifetime.",
            "enum": [
              "daily",
              "lifetime",
              null
            ],
            "example": "daily"
          },
          "budget_optimization": {
            "type": [
              "string",
              "null"
            ],
            "description": "Which level owns the budget — the campaign (CBO) or each ad group (ABO).",
            "enum": [
              "ad_campaign",
              "ad_group",
              null
            ],
            "example": "ad_campaign"
          },
          "bid_type": {
            "type": [
              "string",
              "null"
            ],
            "description": "The bidding strategy the campaign uses.",
            "enum": [
              "minimum_cost",
              "average_target",
              "maximum_target",
              null
            ],
            "example": "minimum_cost"
          },
          "created_at": {
            "type": "string",
            "description": "When the campaign was created, as an ISO 8601 timestamp."
          },
          "updated_at": {
            "type": "string",
            "description": "When the campaign was last updated, as an ISO 8601 timestamp."
          },
          "cost_per_result": {
            "type": [
              "number",
              "null"
            ],
            "description": "Spend divided by Whop pixel-attributed results; null when nothing Whop-attributable is being optimized for."
          },
          "issues": {
            "type": "array",
            "items": {
              "$ref": "#/components/schemas/AdPlatformIssue",
              "description": "Open issues affecting the campaign and its descendant ad groups and ads."
            }
          }
        },
        "required": [
          "click_through_rate",
          "clicks",
          "cost_per_click",
          "cost_per_lead",
          "cost_per_mille",
          "cost_per_purchase",
          "cost_per_schedule",
          "cost_per_submitted_application",
          "cost_per_contact",
          "cost_per_completed_registration",
          "cost_per_viewed_content",
          "cost_per_added_to_cart",
          "frequency",
          "impressions",
          "leads",
          "purchase_value",
          "purchases",
          "reach",
          "return_on_ad_spend",
          "spend",
          "spend_currency",
          "unique_click_through_rate",
          "unique_clicks",
          "schedules",
          "submitted_applications",
          "contacts",
          "completed_registrations",
          "viewed_contents",
          "added_to_carts",
          "custom_conversions",
          "id",
          "title",
          "status",
          "platform",
          "objective",
          "special_ad_categories",
          "optimization_goal",
          "budget_amount",
          "budget_type",
          "budget_optimization",
          "bid_type",
          "created_at",
          "updated_at",
          "cost_per_result",
          "issues"
        ]
      },
      "AdGroup": {
        "type": "object",
        "properties": {
          "click_through_rate": {
            "type": "number",
            "description": "Clicks divided by impressions, between 0 and 1."
          },
          "clicks": {
            "type": "number",
            "description": "The number of clicks."
          },
          "cost_per_click": {
            "type": "number",
            "description": "Spend divided by clicks; 0 when there are no clicks."
          },
          "cost_per_lead": {
            "type": [
              "number",
              "null"
            ],
            "description": "Spend divided by attributed leads; null when leads are not a goal and none are attributed."
          },
          "cost_per_mille": {
            "type": "number",
            "description": "Spend per 1,000 impressions; 0 when there are no impressions."
          },
          "cost_per_purchase": {
            "type": [
              "number",
              "null"
            ],
            "description": "Spend divided by attributed purchases; null when purchases are not a goal and none are attributed."
          },
          "cost_per_schedule": {
            "type": [
              "number",
              "null"
            ],
            "description": "Spend divided by attributed schedule events; null when schedules are not the goal and none are attributed."
          },
          "cost_per_submitted_application": {
            "type": [
              "number",
              "null"
            ],
            "description": "Spend divided by attributed submit-application events; null when they are not the goal and none are attributed."
          },
          "cost_per_contact": {
            "type": [
              "number",
              "null"
            ],
            "description": "Spend divided by attributed contact events; null when contacts are not the goal and none are attributed."
          },
          "cost_per_completed_registration": {
            "type": [
              "number",
              "null"
            ],
            "description": "Spend divided by attributed complete-registration events; null when they are not the goal and none are attributed."
          },
          "cost_per_viewed_content": {
            "type": [
              "number",
              "null"
            ],
            "description": "Spend divided by attributed view-content events; null when they are not the goal and none are attributed."
          },
          "cost_per_added_to_cart": {
            "type": [
              "number",
              "null"
            ],
            "description": "Spend divided by attributed add-to-cart events; null when they are not the goal and none are attributed."
          },
          "frequency": {
            "type": [
              "number",
              "null"
            ],
            "description": "Platform-reported impressions divided by reach."
          },
          "impressions": {
            "type": "number",
            "description": "The number of impressions."
          },
          "leads": {
            "type": "number",
            "description": "Whop pixel-attributed leads, last-click."
          },
          "purchase_value": {
            "type": "number",
            "description": "USD value of pixel-attributed purchases."
          },
          "purchases": {
            "type": "number",
            "description": "Whop pixel-attributed purchases, last-click."
          },
          "reach": {
            "type": "number",
            "description": "The number of unique people who saw this."
          },
          "return_on_ad_spend": {
            "type": "number",
            "description": "Purchase value divided by spend; 0 when there is no spend."
          },
          "spend": {
            "type": "number",
            "description": "The amount charged, in spend_currency."
          },
          "spend_currency": {
            "type": [
              "string",
              "null"
            ],
            "description": "The ISO 4217 currency code of all monetary metrics."
          },
          "unique_click_through_rate": {
            "type": [
              "number",
              "null"
            ],
            "description": "Unique clicks divided by impressions, between 0 and 1."
          },
          "unique_clicks": {
            "type": "number",
            "description": "The number of unique clicks."
          },
          "schedules": {
            "type": "number",
            "description": "Whop pixel-attributed schedule events, last-click."
          },
          "submitted_applications": {
            "type": "number",
            "description": "Whop pixel-attributed submit-application events, last-click."
          },
          "contacts": {
            "type": "number",
            "description": "Whop pixel-attributed contact events, last-click."
          },
          "completed_registrations": {
            "type": "number",
            "description": "Whop pixel-attributed complete-registration events, last-click."
          },
          "viewed_contents": {
            "type": "number",
            "description": "Whop pixel-attributed view-content events, last-click."
          },
          "added_to_carts": {
            "type": "number",
            "description": "Whop pixel-attributed add-to-cart events, last-click."
          },
          "custom_conversions": {
            "type": "number",
            "description": "Whop pixel-attributed custom (merchant-defined) conversion events, last-click, across all custom event names."
          },
          "id": {
            "type": "string",
            "description": "Unique identifier for the ad group."
          },
          "title": {
            "type": [
              "string",
              "null"
            ],
            "description": "The display title of the ad group."
          },
          "status": {
            "type": "string",
            "description": "Delivery status of the ad group.",
            "enum": [
              "active",
              "paused",
              "rejected"
            ],
            "example": "active"
          },
          "optimization_goal": {
            "type": [
              "string",
              "null"
            ],
            "description": "What the ad group optimizes for."
          },
          "conversion_location": {
            "type": [
              "string",
              "null"
            ],
            "description": "Where results happen: website, profile (IG/FB), messaging (DM), on_ad (engagement), or the lead destinations (instant_forms, instant_forms_and_messenger, website_and_instant_forms).",
            "enum": [
              "website",
              "profile",
              "messaging",
              "on_ad",
              "instant_forms",
              "instant_forms_and_messenger",
              "website_and_instant_forms",
              null
            ],
            "example": "website"
          },
          "message_apps": {
            "type": "array",
            "items": {
              "type": "string",
              "description": "For messaging destinations: the apps to message on (messenger, instagram, whatsapp). Empty otherwise."
            }
          },
          "conversion_event": {
            "$ref": "#/components/schemas/ConversionEvent"
          },
          "budget_amount": {
            "type": [
              "number",
              "null"
            ],
            "description": "Ad-set budget; null when the campaign owns budget (CBO)."
          },
          "budget_type": {
            "type": [
              "string",
              "null"
            ],
            "description": "Whether the budget is daily or lifetime.",
            "enum": [
              "daily",
              "lifetime",
              null
            ],
            "example": "daily"
          },
          "minimum_daily_spend": {
            "type": [
              "number",
              "null"
            ],
            "description": "Daily spend floor within the budget."
          },
          "bid_type": {
            "type": [
              "string",
              "null"
            ],
            "description": "Bid strategy.",
            "enum": [
              "minimum_cost",
              "average_target",
              "maximum_target",
              null
            ],
            "example": "minimum_cost"
          },
          "desired_cost_per_result": {
            "type": [
              "number",
              "null"
            ],
            "description": "Target/cap cost for average_target / maximum_target."
          },
          "starts_at": {
            "type": [
              "string",
              "null"
            ],
            "description": "Schedule start, ISO 8601."
          },
          "ends_at": {
            "type": [
              "string",
              "null"
            ],
            "description": "Schedule end, ISO 8601."
          },
          "regions": {
            "type": "object",
            "description": "Geo targeting: include/exclude countries, regions (ISO 3166-2 states, e.g. US-CA), cities, zips."
          },
          "demographics": {
            "type": "object",
            "description": "Demographic targeting: automatic (Advantage+), age range, gender."
          },
          "audiences": {
            "type": "object",
            "description": "Saved-audience targeting: { include, exclude } arrays of audience IDs."
          },
          "languages": {
            "type": "array",
            "items": {
              "type": "string",
              "description": "Targeted languages as ISO 639 codes (e.g. en, es). Region-specific Meta locales without an ISO mapping appear as their numeric Meta locale key. Empty = all languages."
            }
          },
          "dynamic_creative": {
            "type": "boolean",
            "description": "Whether ads within this ad group have their creatives and copy dynamically AB tested."
          },
          "devices": {
            "type": "object",
            "description": "Device targeting: platforms and operating systems."
          },
          "frequency_cap": {
            "type": [
              "object",
              "null"
            ],
            "description": "Impression cap; only valid for reach optimization."
          },
          "placements": {
            "type": "array",
            "items": {
              "type": "object",
              "description": "Targeted placements as { platform, positions }. Empty = automatic (Advantage+) placements."
            }
          },
          "ad_campaign": {
            "type": "object",
            "description": "The ad campaign this ad group belongs to, an object with an id."
          },
          "created_at": {
            "type": "string",
            "description": "When the ad group was created, ISO 8601."
          },
          "updated_at": {
            "type": "string",
            "description": "When the ad group was last updated, ISO 8601."
          },
          "issues": {
            "type": "array",
            "items": {
              "$ref": "#/components/schemas/AdPlatformIssue",
              "description": "Open issues affecting this ad group. Empty when there are none."
            }
          }
        },
        "required": [
          "click_through_rate",
          "clicks",
          "cost_per_click",
          "cost_per_lead",
          "cost_per_mille",
          "cost_per_purchase",
          "cost_per_schedule",
          "cost_per_submitted_application",
          "cost_per_contact",
          "cost_per_completed_registration",
          "cost_per_viewed_content",
          "cost_per_added_to_cart",
          "frequency",
          "impressions",
          "leads",
          "purchase_value",
          "purchases",
          "reach",
          "return_on_ad_spend",
          "spend",
          "spend_currency",
          "unique_click_through_rate",
          "unique_clicks",
          "schedules",
          "submitted_applications",
          "contacts",
          "completed_registrations",
          "viewed_contents",
          "added_to_carts",
          "custom_conversions",
          "id",
          "title",
          "status",
          "optimization_goal",
          "conversion_location",
          "message_apps",
          "conversion_event",
          "budget_amount",
          "budget_type",
          "minimum_daily_spend",
          "bid_type",
          "desired_cost_per_result",
          "starts_at",
          "ends_at",
          "regions",
          "demographics",
          "audiences",
          "languages",
          "dynamic_creative",
          "devices",
          "frequency_cap",
          "placements",
          "ad_campaign",
          "created_at",
          "updated_at",
          "issues"
        ]
      },
      "AdPlatformIssue": {
        "type": "object",
        "properties": {
          "id": {
            "type": "string",
            "description": "Unique identifier for the issue."
          },
          "category": {
            "type": [
              "string",
              "null"
            ],
            "description": "The kind of problem the issue represents.",
            "enum": [
              "policy_rejection",
              "creative_media",
              "audience_targeting",
              "ad_volume_limit",
              null
            ],
            "example": "policy_rejection"
          },
          "resource_id": {
            "type": [
              "string",
              "null"
            ],
            "description": "The ID of the campaign, ad group, or ad the issue is attached to."
          },
          "resource_type": {
            "type": "string",
            "description": "The type of resource the issue is attached to.",
            "enum": [
              "ad_campaign",
              "ad_group",
              "ad"
            ],
            "example": "ad_campaign"
          }
        },
        "required": [
          "id",
          "category",
          "resource_id",
          "resource_type"
        ]
      },
      "Ad": {
        "type": "object",
        "properties": {
          "click_through_rate": {
            "type": "number",
            "description": "Clicks divided by impressions, between 0 and 1."
          },
          "clicks": {
            "type": "number",
            "description": "The number of clicks."
          },
          "cost_per_click": {
            "type": "number",
            "description": "Spend divided by clicks; 0 when there are no clicks."
          },
          "cost_per_lead": {
            "type": [
              "number",
              "null"
            ],
            "description": "Spend divided by attributed leads; null when leads are not a goal and none are attributed."
          },
          "cost_per_mille": {
            "type": "number",
            "description": "Spend per 1,000 impressions; 0 when there are no impressions."
          },
          "cost_per_purchase": {
            "type": [
              "number",
              "null"
            ],
            "description": "Spend divided by attributed purchases; null when purchases are not a goal and none are attributed."
          },
          "cost_per_schedule": {
            "type": [
              "number",
              "null"
            ],
            "description": "Spend divided by attributed schedule events; null when schedules are not the goal and none are attributed."
          },
          "cost_per_submitted_application": {
            "type": [
              "number",
              "null"
            ],
            "description": "Spend divided by attributed submit-application events; null when they are not the goal and none are attributed."
          },
          "cost_per_contact": {
            "type": [
              "number",
              "null"
            ],
            "description": "Spend divided by attributed contact events; null when contacts are not the goal and none are attributed."
          },
          "cost_per_completed_registration": {
            "type": [
              "number",
              "null"
            ],
            "description": "Spend divided by attributed complete-registration events; null when they are not the goal and none are attributed."
          },
          "cost_per_viewed_content": {
            "type": [
              "number",
              "null"
            ],
            "description": "Spend divided by attributed view-content events; null when they are not the goal and none are attributed."
          },
          "cost_per_added_to_cart": {
            "type": [
              "number",
              "null"
            ],
            "description": "Spend divided by attributed add-to-cart events; null when they are not the goal and none are attributed."
          },
          "frequency": {
            "type": [
              "number",
              "null"
            ],
            "description": "Platform-reported impressions divided by reach."
          },
          "impressions": {
            "type": "number",
            "description": "The number of impressions."
          },
          "leads": {
            "type": "number",
            "description": "Whop pixel-attributed leads, last-click."
          },
          "purchase_value": {
            "type": "number",
            "description": "USD value of pixel-attributed purchases."
          },
          "purchases": {
            "type": "number",
            "description": "Whop pixel-attributed purchases, last-click."
          },
          "reach": {
            "type": "number",
            "description": "The number of unique people who saw this."
          },
          "return_on_ad_spend": {
            "type": "number",
            "description": "Purchase value divided by spend; 0 when there is no spend."
          },
          "spend": {
            "type": "number",
            "description": "The amount charged, in spend_currency."
          },
          "spend_currency": {
            "type": [
              "string",
              "null"
            ],
            "description": "The ISO 4217 currency code of all monetary metrics."
          },
          "unique_click_through_rate": {
            "type": [
              "number",
              "null"
            ],
            "description": "Unique clicks divided by impressions, between 0 and 1."
          },
          "unique_clicks": {
            "type": "number",
            "description": "The number of unique clicks."
          },
          "schedules": {
            "type": "number",
            "description": "Whop pixel-attributed schedule events, last-click."
          },
          "submitted_applications": {
            "type": "number",
            "description": "Whop pixel-attributed submit-application events, last-click."
          },
          "contacts": {
            "type": "number",
            "description": "Whop pixel-attributed contact events, last-click."
          },
          "completed_registrations": {
            "type": "number",
            "description": "Whop pixel-attributed complete-registration events, last-click."
          },
          "viewed_contents": {
            "type": "number",
            "description": "Whop pixel-attributed view-content events, last-click."
          },
          "added_to_carts": {
            "type": "number",
            "description": "Whop pixel-attributed add-to-cart events, last-click."
          },
          "custom_conversions": {
            "type": "number",
            "description": "Whop pixel-attributed custom (merchant-defined) conversion events, last-click, across all custom event names."
          },
          "id": {
            "type": "string",
            "description": "Unique identifier for the ad."
          },
          "title": {
            "type": [
              "string",
              "null"
            ],
            "description": "The display title of the ad. Falls back to the creative set caption when unset."
          },
          "status": {
            "type": "string",
            "description": "The delivery status of the ad.",
            "enum": [
              "active",
              "paused",
              "in_review",
              "rejected"
            ],
            "example": "active"
          },
          "primary_texts": {
            "type": "array",
            "items": {
              "type": "string",
              "description": "The primary text variants shown in the ad body."
            }
          },
          "headlines": {
            "type": "array",
            "items": {
              "type": "string",
              "description": "The headline variants shown on the ad."
            }
          },
          "descriptions": {
            "type": "array",
            "items": {
              "type": "string",
              "description": "The description variants shown on the ad."
            }
          },
          "call_to_action": {
            "type": [
              "string",
              "null"
            ],
            "description": "The call-to-action button shown on the ad.",
            "enum": [
              "learn_more",
              "shop_now",
              "sign_up",
              "subscribe",
              "get_started",
              "book_now",
              "apply_now",
              "contact_us",
              "download",
              "order_now",
              "buy_now",
              "get_quote",
              "message_page",
              "whatsapp_message",
              "instagram_message",
              "call_now",
              "get_directions",
              "send_updates",
              "get_offer",
              "watch_more",
              "listen_now",
              "play_game",
              "open_link",
              "no_button",
              "get_offer_view",
              "get_event_tickets",
              "see_menu",
              "request_time",
              "event_rsvp",
              "see_details",
              "view_instagram_profile",
              null
            ],
            "example": "learn_more"
          },
          "url": {
            "type": [
              "string",
              "null"
            ],
            "description": "The URL the ad links to."
          },
          "url_parameters": {
            "type": "object",
            "description": "Query parameters appended to the URL, as a string-to-string map."
          },
          "post_id": {
            "type": [
              "string",
              "null"
            ],
            "description": "The existing post this ad promotes (a Facebook post or Instagram media), or null when it uses uploaded creatives."
          },
          "messaging_config": {
            "type": [
              "object",
              "null"
            ],
            "description": "The click-to-message welcome copy, an object with message and keyword, or null when the ad has none."
          },
          "multi_advertiser_ads": {
            "type": "boolean",
            "description": "Whether the ad can appear alongside other advertisers' ads in the same unit. Defaults to true."
          },
          "ad_group": {
            "type": "object",
            "description": "The ad group this ad belongs to, an object with an id."
          },
          "ad_campaign": {
            "type": "object",
            "description": "The ad campaign this ad belongs to, an object with an id."
          },
          "creatives": {
            "type": "array",
            "items": {
              "type": "object",
              "description": "The creatives used by this ad. Each entry has the creative's file id, its format (square, vertical, horizontal) or null for the original/uncropped asset, the asset url, and its media_type (image or video)."
            }
          },
          "lead_form": {
            "type": [
              "object",
              "null"
            ],
            "description": "The instant lead form on the ad (Meta lead ads), or null when the ad group's conversion_location is not an instant-form destination. An object with name, form_type (more_volume or higher_intent), an optional intro, questions, a privacy_policy, an optional completion screen, and phone_verification."
          },
          "social_accounts": {
            "type": "array",
            "items": {
              "type": "object",
              "description": "The social accounts (Facebook page, Instagram profile) the ad runs under, each an object with an id."
            }
          },
          "created_at": {
            "type": "string",
            "description": "When the ad was created, as an ISO 8601 timestamp."
          },
          "updated_at": {
            "type": "string",
            "description": "When the ad was last updated, as an ISO 8601 timestamp."
          },
          "issues": {
            "type": "array",
            "items": {
              "$ref": "#/components/schemas/AdPlatformIssue",
              "description": "Open issues affecting this ad. Empty when there are none."
            }
          }
        },
        "required": [
          "click_through_rate",
          "clicks",
          "cost_per_click",
          "cost_per_lead",
          "cost_per_mille",
          "cost_per_purchase",
          "cost_per_schedule",
          "cost_per_submitted_application",
          "cost_per_contact",
          "cost_per_completed_registration",
          "cost_per_viewed_content",
          "cost_per_added_to_cart",
          "frequency",
          "impressions",
          "leads",
          "purchase_value",
          "purchases",
          "reach",
          "return_on_ad_spend",
          "spend",
          "spend_currency",
          "unique_click_through_rate",
          "unique_clicks",
          "schedules",
          "submitted_applications",
          "contacts",
          "completed_registrations",
          "viewed_contents",
          "added_to_carts",
          "custom_conversions",
          "id",
          "title",
          "status",
          "primary_texts",
          "headlines",
          "descriptions",
          "call_to_action",
          "url",
          "url_parameters",
          "post_id",
          "messaging_config",
          "multi_advertiser_ads",
          "ad_group",
          "ad_campaign",
          "creatives",
          "lead_form",
          "social_accounts",
          "created_at",
          "updated_at",
          "issues"
        ]
      },
      "Audience": {
        "type": "object",
        "properties": {
          "id": {
            "type": "string",
            "description": "Audience ID, prefixed `adaud_`."
          },
          "name": {
            "type": "string",
            "description": "Audience display name."
          },
          "status": {
            "type": "string",
            "description": "Current state of the audience import. `syncing` means Whop is sending matched rows to connected ad accounts. When status is `partial` or `failed`, `error_message` explains what went wrong.",
            "enum": [
              "pending",
              "processing",
              "syncing",
              "ready",
              "partial",
              "failed"
            ]
          },
          "total_rows": {
            "type": "number",
            "description": "Total rows detected in the uploaded CSV."
          },
          "processed_rows": {
            "type": "number",
            "description": "Rows processed from the uploaded CSV."
          },
          "matched_rows": {
            "type": "number",
            "description": "Rows successfully uploaded to connected ad accounts."
          },
          "progress_percent": {
            "type": "number",
            "description": "Processing progress from 0 to 100."
          },
          "error_message": {
            "type": [
              "string",
              "null"
            ],
            "description": "Processing error message. `null` unless processing is partial or failed."
          },
          "platform_audience_ids": {
            "type": "array",
            "items": {
              "type": "string",
              "description": "External audience IDs created on connected ad platforms, such as Meta."
            }
          },
          "created_at": {
            "type": "number",
            "description": "Unix timestamp when the audience was created."
          },
          "updated_at": {
            "type": "number",
            "description": "Unix timestamp when the audience was last updated."
          }
        },
        "required": [
          "id",
          "name",
          "status",
          "total_rows",
          "processed_rows",
          "matched_rows",
          "progress_percent",
          "error_message",
          "platform_audience_ids",
          "created_at",
          "updated_at"
        ]
      },
      "CheckoutConfiguration": {
        "type": "object",
        "properties": {
          "id": {
            "type": "string"
          },
          "created_at": {
            "type": "number"
          },
          "updated_at": {
            "type": "number"
          },
          "company_id": {
            "type": "string",
            "description": "The ID of the company that owns this checkout configuration"
          },
          "mode": {
            "type": "string",
            "description": "The checkout mode: payment or setup"
          },
          "currency": {
            "type": [
              "string",
              "null"
            ],
            "description": "The currency for this checkout configuration"
          },
          "plan": {
            "type": [
              "object",
              "null"
            ],
            "description": "The plan associated with this checkout configuration"
          },
          "affiliate_code": {
            "type": [
              "string",
              "null"
            ],
            "description": "The affiliate code applied to this checkout configuration"
          },
          "metadata": {
            "type": [
              "object",
              "null"
            ],
            "description": "Arbitrary key-value metadata attached to this checkout configuration"
          },
          "redirect_url": {
            "type": [
              "string",
              "null"
            ],
            "description": "The URL to redirect the user to after checkout"
          },
          "purchase_url": {
            "type": [
              "string",
              "null"
            ],
            "description": "The URL to use for the checkout page"
          },
          "three_ds_level": {
            "type": [
              "string",
              "null"
            ],
            "description": "The 3D Secure enforcement level"
          },
          "payment_method_configuration": {
            "type": [
              "object",
              "null"
            ],
            "description": "Configuration for which payment methods are enabled or disabled"
          }
        },
        "required": [
          "id",
          "created_at",
          "updated_at",
          "company_id",
          "mode",
          "currency",
          "plan",
          "affiliate_code",
          "metadata",
          "redirect_url",
          "purchase_url",
          "three_ds_level",
          "payment_method_configuration"
        ]
      },
      "PlanCustomField": {
        "type": "object",
        "properties": {
          "id": {
            "type": "string",
            "description": "Custom field ID."
          },
          "field_type": {
            "type": "string",
            "description": "Custom field input type.",
            "enum": [
              "text"
            ],
            "example": "text"
          },
          "name": {
            "type": "string",
            "description": "Field label shown to customer at checkout."
          },
          "order": {
            "type": "number",
            "description": "Field position on checkout form."
          },
          "placeholder": {
            "type": [
              "string",
              "null"
            ],
            "description": "Placeholder text shown in empty field."
          },
          "required": {
            "type": "boolean",
            "description": "Whether the customer must complete this field to check out."
          }
        },
        "required": [
          "id",
          "field_type",
          "name",
          "order",
          "placeholder",
          "required"
        ]
      },
      "PlanListItem": {
        "type": "object",
        "properties": {
          "id": {
            "type": "string",
            "description": "Plan ID, prefixed `plan_`."
          },
          "created_at": {
            "type": "string",
            "description": "When the plan was created, as an ISO 8601 timestamp."
          },
          "updated_at": {
            "type": "string",
            "description": "When the plan was last updated, as an ISO 8601 timestamp."
          },
          "visibility": {
            "type": "string",
            "description": "Whether the plan is visible to customers or hidden from public view."
          },
          "plan_type": {
            "type": "string",
            "description": "Billing model for this plan: `renewal` (recurring) or `one_time` (single payment)."
          },
          "release_method": {
            "type": "string",
            "description": "Sales method for this plan, such as `buy_now` or `waitlist`."
          },
          "currency": {
            "type": "string",
            "description": "Three-letter ISO currency code for this plan's prices."
          },
          "account": {
            "type": [
              "object",
              "null"
            ],
            "description": "Account that sells this plan; `null` for standalone invoice plans."
          },
          "product": {
            "type": [
              "object",
              "null"
            ],
            "description": "Product this plan belongs to; `null` for standalone plans."
          },
          "invoice": {
            "type": [
              "object",
              "null"
            ],
            "description": "Invoice this plan was generated for; `null` unless created for an invoice."
          },
          "billing_period": {
            "type": [
              "number",
              "null"
            ],
            "description": "Recurring billing interval in days, such as 30 for monthly or 365 for annual. `null` for one-time plans."
          },
          "title": {
            "type": [
              "string",
              "null"
            ],
            "description": "Plan display name shown to customers."
          },
          "description": {
            "type": [
              "string",
              "null"
            ],
            "description": "Customer-visible plan description."
          },
          "purchase_url": {
            "type": "string",
            "description": "URL where customers can purchase this plan directly."
          },
          "expiration_days": {
            "type": [
              "number",
              "null"
            ],
            "description": "Access duration in days for expiration-based plans."
          },
          "initial_price": {
            "type": "number",
            "description": "Initial purchase price in plan currency."
          },
          "renewal_price": {
            "type": "number",
            "description": "Recurring price charged every billing period."
          },
          "trial_period_days": {
            "type": [
              "number",
              "null"
            ],
            "description": "Free trial days before the first renewal charge. `null` if no trial is configured or the user has already used a trial for this plan."
          },
          "three_ds_level": {
            "type": [
              "string",
              "null"
            ],
            "description": "3D Secure behavior for this plan; `null` inherits account default."
          },
          "member_count": {
            "type": [
              "number",
              "null"
            ],
            "description": "Active memberships through this plan, when visible to the requester."
          },
          "internal_notes": {
            "type": [
              "string",
              "null"
            ],
            "description": "Private notes visible only to authorized team members."
          },
          "metadata": {
            "type": [
              "object",
              "null"
            ],
            "description": "Custom key-value pairs stored on the plan."
          },
          "stock": {
            "type": [
              "number",
              "null"
            ],
            "description": "Units available for purchase, when visible to the requester."
          },
          "unlimited_stock": {
            "type": "boolean",
            "description": "Whether the plan has unlimited stock."
          },
          "split_pay_required_payments": {
            "type": [
              "number",
              "null"
            ],
            "description": "Installment payments required before the subscription pauses."
          },
          "adaptive_pricing_enabled": {
            "type": "boolean",
            "description": "Whether this plan accepts local currency payments via adaptive pricing."
          },
          "payment_method_configuration": {
            "type": [
              "object",
              "null"
            ],
            "description": "Payment method configuration (`enabled`, `disabled`, `include_platform_defaults`); `null` when plan uses default settings."
          }
        },
        "required": [
          "id",
          "created_at",
          "updated_at",
          "visibility",
          "plan_type",
          "release_method",
          "currency",
          "account",
          "product",
          "invoice",
          "billing_period",
          "title",
          "description",
          "purchase_url",
          "expiration_days",
          "initial_price",
          "renewal_price",
          "trial_period_days",
          "three_ds_level",
          "member_count",
          "internal_notes",
          "metadata",
          "stock",
          "unlimited_stock",
          "split_pay_required_payments",
          "adaptive_pricing_enabled",
          "payment_method_configuration"
        ]
      },
      "Plan": {
        "type": "object",
        "properties": {
          "id": {
            "type": "string",
            "description": "Plan ID, prefixed `plan_`."
          },
          "created_at": {
            "type": "string",
            "description": "When the plan was created, as an ISO 8601 timestamp."
          },
          "updated_at": {
            "type": "string",
            "description": "When the plan was last updated, as an ISO 8601 timestamp."
          },
          "visibility": {
            "type": "string",
            "description": "Whether the plan is visible to customers or hidden from public view.",
            "enum": [
              "visible",
              "hidden",
              "archived",
              "quick_link"
            ],
            "example": "visible"
          },
          "plan_type": {
            "type": "string",
            "description": "Billing model for this plan: `renewal` (recurring) or `one_time` (single payment).",
            "enum": [
              "renewal",
              "one_time"
            ],
            "example": "renewal"
          },
          "release_method": {
            "type": "string",
            "description": "Sales method for this plan, such as `buy_now` or `waitlist`.",
            "enum": [
              "buy_now",
              "waitlist"
            ],
            "example": "buy_now"
          },
          "currency": {
            "type": "string",
            "description": "Three-letter ISO currency code for this plan's prices.",
            "enum": [
              "usd",
              "sgd",
              "inr",
              "aud",
              "brl",
              "cad",
              "dkk",
              "eur",
              "nok",
              "gbp",
              "sek",
              "chf",
              "hkd",
              "huf",
              "jpy",
              "mxn",
              "myr",
              "pln",
              "czk",
              "nzd",
              "aed",
              "eth",
              "ape",
              "cop",
              "ron",
              "thb",
              "bgn",
              "idr",
              "dop",
              "php",
              "try",
              "krw",
              "twd",
              "vnd",
              "pkr",
              "clp",
              "uyu",
              "ars",
              "zar",
              "dzd",
              "tnd",
              "mad",
              "kes",
              "kwd",
              "jod",
              "all",
              "xcd",
              "amd",
              "bsd",
              "bhd",
              "bob",
              "bam",
              "khr",
              "crc",
              "xof",
              "egp",
              "etb",
              "gmd",
              "ghs",
              "gtq",
              "gyd",
              "ils",
              "jmd",
              "mop",
              "mga",
              "mur",
              "mdl",
              "mnt",
              "nad",
              "ngn",
              "mkd",
              "omr",
              "pyg",
              "pen",
              "qar",
              "rwf",
              "sar",
              "rsd",
              "lkr",
              "tzs",
              "ttd",
              "uzs",
              "rub",
              "btc",
              "cny",
              "usdt",
              "kzt",
              "awg",
              "whop_usd",
              "xau"
            ],
            "example": "usd"
          },
          "account": {
            "type": [
              "object",
              "null"
            ],
            "description": "Account that sells this plan; `null` for standalone invoice plans."
          },
          "product": {
            "type": [
              "object",
              "null"
            ],
            "description": "Product this plan belongs to; `null` for standalone plans."
          },
          "invoice": {
            "type": [
              "object",
              "null"
            ],
            "description": "Invoice this plan was generated for; `null` unless created for an invoice."
          },
          "billing_period": {
            "type": [
              "number",
              "null"
            ],
            "description": "Recurring billing interval in days, such as 30 for monthly or 365 for annual. `null` for one-time plans."
          },
          "title": {
            "type": [
              "string",
              "null"
            ],
            "description": "Plan display name shown to customers."
          },
          "description": {
            "type": [
              "string",
              "null"
            ],
            "description": "Customer-visible plan description."
          },
          "purchase_url": {
            "type": "string",
            "description": "URL where customers can purchase this plan directly."
          },
          "expiration_days": {
            "type": [
              "number",
              "null"
            ],
            "description": "Access duration in days for expiration-based plans."
          },
          "initial_price": {
            "type": "number",
            "description": "Initial purchase price in plan currency."
          },
          "renewal_price": {
            "type": "number",
            "description": "Recurring price charged every billing period."
          },
          "trial_period_days": {
            "type": [
              "number",
              "null"
            ],
            "description": "Free trial days before the first renewal charge. `null` if no trial is configured or the user has already used a trial for this plan."
          },
          "three_ds_level": {
            "type": [
              "string",
              "null"
            ],
            "description": "3D Secure behavior for this plan; `null` inherits account default.",
            "enum": [
              "mandate_challenge",
              "frictionless",
              null
            ],
            "example": "mandate_challenge"
          },
          "member_count": {
            "type": [
              "number",
              "null"
            ],
            "description": "Active memberships through this plan, when visible to the requester."
          },
          "internal_notes": {
            "type": [
              "string",
              "null"
            ],
            "description": "Private notes visible only to authorized team members."
          },
          "metadata": {
            "type": [
              "object",
              "null"
            ],
            "description": "Custom key-value pairs stored on the plan."
          },
          "stock": {
            "type": [
              "number",
              "null"
            ],
            "description": "Units available for purchase, when visible to the requester."
          },
          "unlimited_stock": {
            "type": "boolean",
            "description": "Whether the plan has unlimited stock."
          },
          "split_pay_required_payments": {
            "type": [
              "number",
              "null"
            ],
            "description": "Installment payments required before the subscription pauses."
          },
          "adaptive_pricing_enabled": {
            "type": "boolean",
            "description": "Whether this plan accepts local currency payments via adaptive pricing."
          },
          "payment_method_configuration": {
            "type": [
              "object",
              "null"
            ],
            "description": "Payment method configuration (`enabled`, `disabled`, `include_platform_defaults`); `null` when plan uses default settings."
          },
          "tax_type": {
            "type": "string",
            "description": "How tax is handled for this plan.",
            "enum": [
              "inclusive",
              "exclusive",
              "unspecified"
            ],
            "example": "inclusive"
          },
          "collect_tax": {
            "type": "boolean",
            "description": "Whether tax is collected on purchases of this plan."
          },
          "custom_fields": {
            "type": "array",
            "items": {
              "$ref": "#/components/schemas/PlanCustomField",
              "description": "Custom input fields collected on the checkout form."
            }
          }
        },
        "required": [
          "id",
          "created_at",
          "updated_at",
          "visibility",
          "plan_type",
          "release_method",
          "currency",
          "account",
          "product",
          "invoice",
          "billing_period",
          "title",
          "description",
          "purchase_url",
          "expiration_days",
          "initial_price",
          "renewal_price",
          "trial_period_days",
          "three_ds_level",
          "member_count",
          "internal_notes",
          "metadata",
          "stock",
          "unlimited_stock",
          "split_pay_required_payments",
          "adaptive_pricing_enabled",
          "payment_method_configuration",
          "tax_type",
          "collect_tax",
          "custom_fields"
        ]
      },
      "ProductListItem": {
        "type": "object",
        "properties": {
          "id": {
            "type": "string",
            "description": "Product ID, prefixed `prod_`."
          },
          "created_at": {
            "type": "string",
            "description": "When the product was created, as an ISO 8601 timestamp."
          },
          "updated_at": {
            "type": "string",
            "description": "When the product was last updated, as an ISO 8601 timestamp."
          },
          "title": {
            "type": [
              "string",
              "null"
            ],
            "description": "Product display name shown to customers."
          },
          "visibility": {
            "type": [
              "string",
              "null"
            ],
            "description": "Whether the product is publicly visible, hidden, or archived."
          },
          "headline": {
            "type": [
              "string",
              "null"
            ],
            "description": "Short marketing headline displayed on product page."
          },
          "verified": {
            "type": "boolean",
            "description": "Whether the product has been verified by Whop."
          },
          "member_count": {
            "type": "number",
            "description": "Active memberships for this product; 0 if public member counts are disabled."
          },
          "route": {
            "type": [
              "string",
              "null"
            ],
            "description": "URL slug for the product's public link."
          },
          "published_reviews_count": {
            "type": "number",
            "description": "Published customer reviews for this product."
          },
          "external_identifier": {
            "type": [
              "string",
              "null"
            ],
            "description": "External identifier stored on the product for your own reference."
          },
          "metadata": {
            "type": [
              "object",
              "null"
            ],
            "description": "Custom key-value pairs stored on the product."
          }
        },
        "required": [
          "id",
          "created_at",
          "updated_at",
          "title",
          "visibility",
          "headline",
          "verified",
          "member_count",
          "route",
          "published_reviews_count",
          "external_identifier",
          "metadata"
        ]
      },
      "Product": {
        "type": "object",
        "properties": {
          "id": {
            "type": "string",
            "description": "Product ID, prefixed `prod_`."
          },
          "created_at": {
            "type": "string",
            "description": "When the product was created, as an ISO 8601 timestamp."
          },
          "updated_at": {
            "type": "string",
            "description": "When the product was last updated, as an ISO 8601 timestamp."
          },
          "title": {
            "type": [
              "string",
              "null"
            ],
            "description": "Product display name shown to customers."
          },
          "visibility": {
            "type": [
              "string",
              "null"
            ],
            "description": "Whether the product is publicly visible, hidden, or archived."
          },
          "headline": {
            "type": [
              "string",
              "null"
            ],
            "description": "Short marketing headline displayed on product page."
          },
          "verified": {
            "type": "boolean",
            "description": "Whether the product has been verified by Whop."
          },
          "member_count": {
            "type": "number",
            "description": "Active memberships for this product; 0 if public member counts are disabled."
          },
          "route": {
            "type": [
              "string",
              "null"
            ],
            "description": "URL slug for the product's public link."
          },
          "published_reviews_count": {
            "type": "number",
            "description": "Published customer reviews for this product."
          },
          "external_identifier": {
            "type": [
              "string",
              "null"
            ],
            "description": "External identifier stored on the product for your own reference."
          },
          "metadata": {
            "type": [
              "object",
              "null"
            ],
            "description": "Custom key-value pairs stored on the product."
          },
          "description": {
            "type": [
              "string",
              "null"
            ],
            "description": "Written description displayed on product page."
          },
          "custom_cta": {
            "type": [
              "string",
              "null"
            ],
            "description": "Call-to-action button label shown on the product purchase page."
          },
          "custom_cta_url": {
            "type": [
              "string",
              "null"
            ],
            "description": "URL the call-to-action button links to instead of checkout."
          },
          "custom_statement_descriptor": {
            "type": [
              "string",
              "null"
            ],
            "description": "Custom text label on customer's bank statement."
          },
          "global_affiliate_percentage": {
            "type": [
              "number",
              "null"
            ],
            "description": "Commission rate affiliates earn through the global affiliate program."
          },
          "global_affiliate_status": {
            "type": [
              "string",
              "null"
            ],
            "description": "Enrollment status in the global affiliate program."
          },
          "member_affiliate_percentage": {
            "type": [
              "number",
              "null"
            ],
            "description": "Commission rate members earn through the member affiliate program."
          },
          "member_affiliate_status": {
            "type": [
              "string",
              "null"
            ],
            "description": "Enrollment status in the member affiliate program."
          },
          "gallery_images": {
            "type": "array",
            "description": "Gallery images for this product."
          },
          "product_tax_code": {
            "type": [
              "object",
              "null"
            ],
            "description": "Tax classification code for this product, or `null` if no tax code is set."
          },
          "owner_user": {
            "type": [
              "object",
              "null"
            ],
            "description": "User who owns the account selling this product."
          },
          "company": {
            "type": [
              "object",
              "null"
            ],
            "description": "Account that sells this product."
          }
        },
        "required": [
          "id",
          "created_at",
          "updated_at",
          "title",
          "visibility",
          "headline",
          "verified",
          "member_count",
          "route",
          "published_reviews_count",
          "external_identifier",
          "metadata",
          "description",
          "custom_cta",
          "custom_cta_url",
          "custom_statement_descriptor",
          "global_affiliate_percentage",
          "global_affiliate_status",
          "member_affiliate_percentage",
          "member_affiliate_status",
          "gallery_images",
          "product_tax_code",
          "owner_user",
          "company"
        ]
      },
      "SocialAccountPost": {
        "type": "object",
        "properties": {
          "id": {
            "type": "string",
            "description": "The platform's own identifier for the post or media. Use it to reference the post on an ad."
          },
          "media_url": {
            "type": [
              "string",
              "null"
            ],
            "description": "The URL of the post's media — the image for image posts, the playable video file for video posts. Meta signs these and they expire after roughly 24 hours, so don't store them."
          },
          "thumbnail_url": {
            "type": [
              "string",
              "null"
            ],
            "description": "Poster image for video posts; null for image posts, where media_url is already the image. Signed and short-lived like media_url."
          },
          "call_to_action": {
            "type": [
              "string",
              "null"
            ],
            "description": "The post's call-to-action button, for example shop_now (Facebook only; null for Instagram).",
            "enum": [
              "learn_more",
              "shop_now",
              "sign_up",
              "subscribe",
              "get_started",
              "book_now",
              "apply_now",
              "contact_us",
              "download",
              "order_now",
              "buy_now",
              "get_quote",
              "message_page",
              "whatsapp_message",
              "instagram_message",
              "call_now",
              "get_directions",
              "send_updates",
              "get_offer",
              "watch_more",
              "listen_now",
              "play_game",
              "open_link",
              "no_button",
              "get_offer_view",
              "get_event_tickets",
              "see_menu",
              "request_time",
              "event_rsvp",
              "see_details",
              "view_instagram_profile",
              null
            ],
            "example": "learn_more"
          },
          "destination_url": {
            "type": [
              "string",
              "null"
            ],
            "description": "The URL the post's call-to-action drives to (Facebook only; null for Instagram)."
          }
        },
        "required": [
          "id",
          "media_url",
          "thumbnail_url",
          "call_to_action",
          "destination_url"
        ]
      },
      "SocialAccount": {
        "type": "object",
        "properties": {
          "id": {
            "type": "string",
            "description": "Unique identifier for the social account."
          },
          "platform": {
            "type": "string",
            "description": "The platform the social account exists on.",
            "enum": [
              "x",
              "instagram",
              "youtube",
              "tiktok",
              "facebook"
            ],
            "example": "x"
          },
          "username": {
            "type": "string",
            "description": "The username of the social account on the platform."
          },
          "name": {
            "type": [
              "string",
              "null"
            ],
            "description": "The display name of the social account on the platform."
          },
          "url": {
            "type": "string",
            "description": "The URL where the social account can be accessed on the platform."
          },
          "profile_picture_url": {
            "type": [
              "string",
              "null"
            ],
            "description": "The URL where the profile picture of the social account can be accessed."
          },
          "verified": {
            "type": "boolean",
            "description": "Whether the social account is verified on the platform."
          },
          "external_id": {
            "type": [
              "string",
              "null"
            ],
            "description": "The platform-specific ID for this social account."
          },
          "scopes": {
            "type": "array",
            "items": {
              "type": "string",
              "description": "Capabilities Whop retains specific to this social account. For example, Whop may request the ability to run advertisements that use this social account's identity, reflected by the presence of `advertise` in this value."
            }
          }
        },
        "required": [
          "id",
          "platform",
          "username",
          "name",
          "url",
          "profile_picture_url",
          "verified",
          "external_id",
          "scopes"
        ]
      },
      "User": {
        "type": "object",
        "properties": {
          "id": {
            "type": "string",
            "description": "User ID, prefixed `user_`."
          },
          "username": {
            "type": "string",
            "description": "The user's unique username"
          },
          "name": {
            "type": [
              "string",
              "null"
            ],
            "description": "The user's display name"
          },
          "bio": {
            "type": [
              "string",
              "null"
            ],
            "description": "The user's biography"
          },
          "created_at": {
            "type": "string",
            "description": "When the user was created, as an ISO 8601 timestamp"
          },
          "profile_picture": {
            "type": [
              "object",
              "null"
            ],
            "description": "The user's profile picture, an object with a url"
          },
          "total_usd": {
            "type": [
              "string",
              "null"
            ],
            "description": "Total USD value across the user's balances with known exchange rates. Computed only on `GET /users/me` self-view for callers with balance-read scope; `null` otherwise."
          },
          "balances": {
            "type": "array",
            "items": {
              "$ref": "#/components/schemas/AccountBalanceToken",
              "description": "User holdings (crypto and fiat), each with USD value. Empty when `total_usd` is `null`."
            }
          },
          "verification": {
            "type": "object",
            "description": "Identity verification status for the user's `individual` (KYC) and `business` (KYB) profiles. Each is `null` until created, otherwise a `status` of `not_started`, `pending`, `approved`, or `rejected`."
          }
        },
        "required": [
          "id",
          "username",
          "name",
          "bio",
          "created_at",
          "profile_picture",
          "total_usd",
          "balances",
          "verification"
        ]
      },
      "V1ErrorResponse": {
        "type": "object",
        "properties": {
          "error": {
            "type": "object",
            "properties": {
              "type": {
                "type": "string",
                "description": "Machine-readable error code."
              },
              "message": {
                "type": "string",
                "description": "Human-readable error message."
              }
            },
            "required": [
              "type",
              "message"
            ]
          }
        },
        "required": [
          "error"
        ]
      },
      "ConversionEvent": {
        "description": "The pixel event optimized for. A standard event, or any custom pixel event name.",
        "anyOf": [
          {
            "type": "string",
            "enum": [
              "purchase",
              "add_to_cart",
              "initiated_checkout",
              "add_payment_info",
              "complete_registration",
              "lead",
              "content_view",
              "search",
              "contact",
              "customize_product",
              "donate",
              "find_location",
              "schedule",
              "start_trial",
              "submit_application",
              "subscribe"
            ]
          },
          {
            "type": "string"
          },
          {
            "type": "null"
          }
        ]
      },
      "LedgerActivity": {
        "type": "object",
        "properties": {
          "object": {
            "type": "string",
            "enum": [
              "ledger_activity"
            ]
          },
          "id": {
            "type": "string",
            "description": "Ledger activity ID."
          },
          "line_type": {
            "type": "string",
            "description": "Type of ledger activity."
          },
          "amount": {
            "type": "string",
            "description": "Signed amount in the currency's smallest precision units."
          },
          "currency": {
            "type": "object",
            "description": "Currency for this ledger activity.",
            "properties": {
              "code": {
                "type": "string",
                "description": "Currency code."
              },
              "precision": {
                "type": "string",
                "description": "Precision factor for the currency, for example `100000000` for USD."
              }
            },
            "required": [
              "code",
              "precision"
            ]
          },
          "posted_at": {
            "type": "string",
            "format": "date-time",
            "description": "When the activity posted to the ledger."
          },
          "available_at": {
            "type": [
              "string",
              "null"
            ],
            "format": "date-time",
            "description": "ISO 8601 timestamp these funds became (or are scheduled to become) withdrawable: the posted time for already-settled funds, or 00:00:00 UTC on the scheduled release date for pending funds. Present only on inflows entering the balance (payments, top-ups, incoming transfers/affiliate); null on withdrawals, refunds, disputes and on-chain rows. The available_after/before filters window on its UTC settlement date."
          },
          "created_at": {
            "type": [
              "string",
              "null"
            ],
            "format": "date-time",
            "description": "When the activity record was created."
          },
          "resource": {
            "description": "Resource associated with this ledger activity.",
            "oneOf": [
              {
                "type": "object",
                "properties": {
                  "object": {
                    "type": "string",
                    "enum": [
                      "account"
                    ]
                  },
                  "id": {
                    "type": "string",
                    "description": "Account ID."
                  },
                  "title": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "Account display name."
                  },
                  "route": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "Account route."
                  },
                  "logo_url": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "Account logo URL."
                  }
                },
                "required": [
                  "object",
                  "id",
                  "title",
                  "route",
                  "logo_url"
                ]
              },
              {
                "type": "object",
                "properties": {
                  "object": {
                    "type": "string",
                    "enum": [
                      "user"
                    ]
                  },
                  "id": {
                    "type": "string",
                    "description": "User ID."
                  },
                  "name": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "User display name."
                  },
                  "username": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "User's username."
                  },
                  "profile_picture_url": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "User profile image URL."
                  }
                },
                "required": [
                  "object",
                  "id",
                  "name",
                  "username",
                  "profile_picture_url"
                ]
              },
              {
                "type": "object",
                "properties": {
                  "object": {
                    "type": "string",
                    "enum": [
                      "ledger_account"
                    ]
                  },
                  "id": {
                    "type": "string",
                    "description": "Ledger account ID."
                  },
                  "owner": {
                    "oneOf": [
                      {
                        "type": "object",
                        "properties": {
                          "object": {
                            "type": "string",
                            "enum": [
                              "account"
                            ]
                          },
                          "id": {
                            "type": "string",
                            "description": "Account ID."
                          },
                          "title": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "Account display name."
                          },
                          "route": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "Account route."
                          },
                          "logo_url": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "Account logo URL."
                          }
                        },
                        "required": [
                          "object",
                          "id",
                          "title",
                          "route",
                          "logo_url"
                        ]
                      },
                      {
                        "type": "object",
                        "properties": {
                          "object": {
                            "type": "string",
                            "enum": [
                              "user"
                            ]
                          },
                          "id": {
                            "type": "string",
                            "description": "User ID."
                          },
                          "name": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "User display name."
                          },
                          "username": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "User's username."
                          },
                          "profile_picture_url": {
                            "type": [
                              "string",
                              "null"
                            ],
                            "description": "User profile image URL."
                          }
                        },
                        "required": [
                          "object",
                          "id",
                          "name",
                          "username",
                          "profile_picture_url"
                        ]
                      },
                      {
                        "type": "null"
                      }
                    ]
                  }
                },
                "required": [
                  "object",
                  "id",
                  "owner"
                ]
              },
              {
                "type": "object",
                "properties": {
                  "object": {
                    "type": "string",
                    "enum": [
                      "payment_method"
                    ]
                  },
                  "id": {
                    "type": "string",
                    "description": "Payment method ID."
                  },
                  "payment_method_type": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "Payment method type."
                  },
                  "gateway_type": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "Payment gateway type."
                  },
                  "card": {
                    "type": [
                      "object",
                      "null"
                    ],
                    "properties": {
                      "brand": {
                        "type": [
                          "string",
                          "null"
                        ],
                        "description": "Card brand."
                      },
                      "last4": {
                        "type": [
                          "string",
                          "null"
                        ],
                        "description": "Last four digits of the card."
                      },
                      "exp_month": {
                        "type": [
                          "integer",
                          "null"
                        ],
                        "description": "Card expiration month."
                      },
                      "exp_year": {
                        "type": [
                          "integer",
                          "null"
                        ],
                        "description": "Card expiration year."
                      }
                    },
                    "required": [
                      "brand",
                      "last4",
                      "exp_month",
                      "exp_year"
                    ]
                  },
                  "bank": {
                    "type": [
                      "object",
                      "null"
                    ],
                    "properties": {
                      "bank_name": {
                        "type": [
                          "string",
                          "null"
                        ],
                        "description": "Bank name."
                      },
                      "account_name": {
                        "type": [
                          "string",
                          "null"
                        ],
                        "description": "Bank account holder name."
                      },
                      "last4": {
                        "type": [
                          "string",
                          "null"
                        ],
                        "description": "Last four digits of the bank account."
                      },
                      "account_type": {
                        "type": [
                          "string",
                          "null"
                        ],
                        "description": "Bank account type."
                      }
                    },
                    "required": [
                      "bank_name",
                      "account_name",
                      "last4",
                      "account_type"
                    ]
                  },
                  "email_identifier": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "Email identifier for email-based payment methods."
                  }
                },
                "required": [
                  "object",
                  "id",
                  "payment_method_type",
                  "gateway_type",
                  "card",
                  "bank",
                  "email_identifier"
                ]
              },
              {
                "type": "object",
                "properties": {
                  "object": {
                    "type": "string",
                    "enum": [
                      "payout_method"
                    ]
                  },
                  "id": {
                    "type": "string",
                    "description": "Payout method ID."
                  },
                  "nickname": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "Payout method nickname."
                  },
                  "institution_name": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "Payout institution name."
                  },
                  "account_reference": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "Masked account reference."
                  },
                  "provider": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "Payout provider."
                  },
                  "destination_currency_code": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "Destination currency code."
                  }
                },
                "required": [
                  "object",
                  "id",
                  "nickname",
                  "institution_name",
                  "account_reference",
                  "provider",
                  "destination_currency_code"
                ]
              },
              {
                "type": "object",
                "properties": {
                  "object": {
                    "type": "string",
                    "enum": [
                      "card_transaction"
                    ]
                  },
                  "id": {
                    "type": "string",
                    "description": "Card transaction ID."
                  },
                  "card_id": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "Identifier of the card that the transaction was charged to."
                  },
                  "merchant_name": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "Merchant display name."
                  },
                  "merchant_icon_url": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "Merchant icon URL."
                  },
                  "merchant_category": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "Merchant category."
                  },
                  "status": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "Current card transaction status."
                  },
                  "usd_amount": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "The processor-settled USD amount as a decimal string. The ledger's USDT leg is posted 1:1 from this value."
                  },
                  "local_amount": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "Amount the merchant charged in their local currency, as a decimal string. Pair with local_currency."
                  },
                  "local_currency": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "ISO 4217 currency code of the merchant-charged amount in local_amount."
                  },
                  "cashback_usd": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "Cashback earned on this transaction as a USD decimal string. Zero for declined or ineligible transactions; null when cashback has not been computed yet."
                  },
                  "authorized_at": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "format": "date-time",
                    "description": "ISO 8601 timestamp the transaction was authorized."
                  },
                  "posted_at": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "format": "date-time",
                    "description": "ISO 8601 timestamp the transaction was settled by the card network."
                  },
                  "declined_reason": {
                    "type": [
                      "string",
                      "null"
                    ],
                    "description": "Reason the transaction was declined (when status is declined)."
                  }
                },
                "required": [
                  "object",
                  "id",
                  "card_id",
                  "merchant_name",
                  "merchant_icon_url",
                  "merchant_category",
                  "status",
                  "usd_amount",
                  "local_amount",
                  "local_currency",
                  "cashback_usd",
                  "authorized_at",
                  "posted_at",
                  "declined_reason"
                ]
              },
              {
                "type": "null"
              }
            ]
          },
          "source": {
            "type": [
              "object",
              "null"
            ],
            "additionalProperties": true,
            "description": "Source of this ledger activity.",
            "properties": {
              "object": {
                "type": "string"
              },
              "id": {
                "type": "string"
              },
              "status": {
                "type": [
                  "string",
                  "null"
                ],
                "description": "Lifecycle status. On withdrawal sources this is the withdrawal status (requires payout:withdrawal:read); on airdrop_link sources it is the claim-link status (ungated); on payment and top-up sources it is the friendly payment status such as succeeded/pending/failed (ungated)."
              },
              "claim_url": {
                "type": [
                  "string",
                  "null"
                ],
                "description": "Public claim URL for the airdrop link (airdrop_link sources only)."
              },
              "amount_float": {
                "type": [
                  "number",
                  "null"
                ],
                "description": "Withdrawal amount as a decimal number in the destination currency (withdrawal sources only; requires payout:withdrawal:read)."
              },
              "created_at": {
                "type": [
                  "string",
                  "null"
                ],
                "format": "date-time",
                "description": "Withdrawal creation time as an ISO 8601 timestamp (withdrawal sources only; requires payout:withdrawal:read)."
              },
              "estimated_arrival": {
                "type": [
                  "string",
                  "null"
                ],
                "format": "date-time",
                "description": "Estimated arrival as an ISO 8601 timestamp (withdrawal sources only; requires payout:withdrawal:read)."
              },
              "payer_name": {
                "type": [
                  "string",
                  "null"
                ],
                "description": "Name of the entity processing the payout (withdrawal sources only; requires payout:withdrawal:read)."
              },
              "payout_token_nickname": {
                "type": [
                  "string",
                  "null"
                ],
                "description": "Saved payout destination nickname (withdrawal sources only)."
              },
              "tx_hash": {
                "type": [
                  "string",
                  "null"
                ],
                "description": "On-chain transaction hash (onchain_transaction and swap sources only)."
              },
              "sender_address": {
                "type": [
                  "string",
                  "null"
                ],
                "description": "Sender wallet address or onramp provider identifier (onchain_transaction sources only)."
              },
              "chain": {
                "type": [
                  "string",
                  "null"
                ],
                "description": "Chain the deposit landed on, for example plasma (onchain_transaction sources only)."
              },
              "from_amount": {
                "type": [
                  "string",
                  "null"
                ],
                "description": "Amount converted out of from_currency as a decimal string (swap sources only)."
              },
              "from_currency": {
                "type": [
                  "string",
                  "null"
                ],
                "description": "Lowercase currency code converted from (swap sources only)."
              },
              "to_amount": {
                "type": [
                  "string",
                  "null"
                ],
                "description": "Amount received in to_currency as a decimal string (swap sources only)."
              },
              "to_currency": {
                "type": [
                  "string",
                  "null"
                ],
                "description": "Lowercase currency code converted to (swap sources only)."
              },
              "payout_destination": {
                "type": [
                  "object",
                  "null"
                ],
                "description": "Payout destination display info (withdrawal sources only).",
                "properties": {
                  "payer_name": {
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "icon_url": {
                    "type": [
                      "string",
                      "null"
                    ]
                  }
                }
              }
            },
            "required": [
              "object",
              "id"
            ]
          }
        },
        "required": [
          "object",
          "id",
          "line_type",
          "amount",
          "currency",
          "posted_at",
          "available_at",
          "created_at",
          "resource",
          "source"
        ]
      },
      "Pagination": {
        "type": "object",
        "properties": {
          "current_page": {
            "type": "number",
            "description": "Current page number"
          },
          "total_pages": {
            "type": "number",
            "description": "Total number of pages"
          },
          "next_page": {
            "type": [
              "number",
              "null"
            ],
            "description": "Next page number"
          },
          "prev_page": {
            "type": [
              "number",
              "null"
            ],
            "description": "Previous page number"
          },
          "total_count": {
            "type": "number",
            "description": "Total number of records"
          }
        },
        "additionalProperties": false,
        "required": [
          "current_page",
          "total_pages",
          "next_page",
          "prev_page",
          "total_count"
        ]
      }
    },
    "parameters": {
      "ApiVersionDate": {
        "name": "Api-Version-Date",
        "in": "header",
        "required": false,
        "schema": {
          "type": "string",
          "format": "date",
          "example": "2026-07-01"
        },
        "description": "Pins the request to a dated API version. When omitted, the request uses the original (2025-01-01) request/response shapes, so existing unversioned callers are never broken. Generated SDKs always send the latest version they were built against. An unrecognized value is rejected with a 400. The schemas documented here always describe the latest version."
      }
    },
    "responses": {
      "NotFound": {
        "description": "Resource not found",
        "content": {
          "application/json": {
            "schema": {
              "$ref": "#/components/schemas/V1ErrorResponse"
            }
          }
        }
      },
      "Forbidden": {
        "description": "Forbidden",
        "content": {
          "application/json": {
            "schema": {
              "$ref": "#/components/schemas/V1ErrorResponse"
            }
          }
        }
      },
      "Unauthorized": {
        "description": "Unauthorized",
        "content": {
          "application/json": {
            "schema": {
              "$ref": "#/components/schemas/V1ErrorResponse"
            }
          }
        }
      },
      "InvalidParameters": {
        "description": "Invalid Parameters",
        "content": {
          "application/json": {
            "schema": {
              "$ref": "#/components/schemas/V1ErrorResponse"
            }
          }
        }
      }
    }
  }
}
